<template>
	<div id="clientSettings" class="jc-view">
		<MediaLibraryModal v-on:mediaInsert="onMediaInsert($event)" v-on:hideMediaModal="onMediaHide()" />
		<JuniperPageHeader :back="'/juniper/clients/edit/' + client.id" :title="client ? client.name : ''" subtitle="Settings">
			<template #title-actions>
				<span v-if="client && client.staging && !client.maintenance_mode" class="badge bg-secondary text-white">Staging</span>
				<span v-if="client && client.maintenance_mode" class="badge bg-warning text-white">Maintenance</span>
				<span v-if="client && !client.staging && !client.maintenance_mode" class="badge bg-success text-white">Live</span>
			</template>
		</JuniperPageHeader>
		<template v-if="!loading">
			<div class="row mb-3 mt-4">
				<!-- Horizontal top tabs -->
				<div class="col-md-12 mt-3">
					<ul class="nav nav-tabs jcs-top-tabs" role="tablist" aria-label="Settings sections">
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'general' }" @click="activeTab = 'general'" type="button" role="tab">
								General
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'domains' }" @click="activeTab = 'domains'" type="button" role="tab">
								Domains
							</button>
						</li>
						<!--
							Posts tab — hidden for now. The full Posts settings panel
							(prefix, single/archive templates, per-page) still lives in
							the template below, just not reachable via the tab strip.
							To bring it back, uncomment this <li> AND the matching
							<div v-show="activeTab === 'posts'"> panel block.

						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'posts' }" @click="activeTab = 'posts'" type="button" role="tab">
								Posts
							</button>
						</li>
						-->
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'news' }" @click="activeTab = 'news'" type="button" role="tab">
								News &amp; Notices
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'faqs' }" @click="activeTab = 'faqs'" type="button" role="tab">
								FAQs
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'departments' }" @click="activeTab = 'departments'" type="button" role="tab">
								Departments
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'events' }" @click="activeTab = 'events'" type="button" role="tab">
								Events
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'meetings' }" @click="activeTab = 'meetings'" type="button" role="tab">
								Meetings
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'subscribers' }" @click="activeTab = 'subscribers'" type="button" role="tab">
								Subscribers
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'contact' }" @click="activeTab = 'contact'" type="button" role="tab">
								Contact
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'security' }" @click="activeTab = 'security'" type="button" role="tab">
								Security
							</button>
						</li>
						<!--
							Audits tab — moved to its own page: Client → Tools & Insights →
							Accessibility (ClientAccessibility.vue, same ClientAuditsTab).
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'audits' }" @click="activeTab = 'audits'" type="button" role="tab">
								Audits
							</button>
						</li>
						-->
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'integrations' }" @click="activeTab = 'integrations'" type="button" role="tab">
								Integrations
							</button>
						</li>
						<li class="nav-item" role="presentation">
							<button class="nav-link" :class="{ active: activeTab === 'modules' }" @click="activeTab = 'modules'" type="button" role="tab">
								Modules
							</button>
						</li>
					</ul>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 1: General -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'general'" class="col-md-12 mt-3 js-tab-content">
					<!-- Dashboard -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Dashboard</h2>
							<p class="gh-intro__desc">Choose which dashboard interface is shown to client users. The Unified Dashboard is the modern HeyGov-integrated experience; the Town Web Dashboard is the classic interface.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card cs-pick-card">
								<div class="cs-pick-group cs-pick-group--2">
									<label
										class="cs-pick"
										:class="{ 'cs-pick--active': clientOptions.main_dashboard === 'town-web-dashboard' }"
									>
										<input
											type="radio"
											name="dashboardType"
											value="town-web-dashboard"
											v-model="clientOptions.main_dashboard"
										/>
										<i class="fas fa-th-large"></i>
										<div>
											<div class="cs-pick__name">Town Web Dashboard</div>
											<div class="cs-pick__sub">Classic Town Web interface</div>
										</div>
									</label>
									<label
										class="cs-pick"
										:class="{ 'cs-pick--active': clientOptions.main_dashboard === 'unified-dashboard' }"
									>
										<input
											type="radio"
											name="dashboardType"
											value="unified-dashboard"
											v-model="clientOptions.main_dashboard"
										/>
										<i class="fas fa-bolt"></i>
										<div>
											<div class="cs-pick__name">Unified Dashboard</div>
											<div class="cs-pick__sub">Modern HeyGov-integrated experience</div>
										</div>
									</label>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Branding -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Branding</h2>
							<p class="gh-intro__desc">Site identity — title, description, language and images. The Preview shows roughly how the site will appear in search results and social shares.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<!-- Site settings: 2-column grid (Title+Description | Language+Preview) -->
								<div class="cs-site-settings">
									<div class="cs-site-settings__col">
										<div class="gh-field">
											<label>Title</label>
											<input type="text" class="form-control" v-model="clientOptions.main_title" placeholder="e.g., City of Springfield" />
										</div>
										<div class="gh-field">
											<label>Description</label>
											<textarea
												class="form-control"
												rows="4"
												v-model="clientOptions.main_description"
												placeholder="A short description shown in search results and social previews"
											></textarea>
										</div>
									</div>
									<div class="cs-site-settings__col">
										<div class="gh-field">
											<label>Language</label>
											<select class="form-select" v-model="clientOptions.main_language">
												<option value="en">English</option>
											</select>
										</div>
										<div class="gh-field">
											<label>Preview</label>
											<div class="cs-search-preview">
												<div class="cs-search-preview__head">
													<div class="cs-search-preview__favicon">
														<img
															v-if="clientOptions.theme_favicon_light"
															:src="clientOptions.theme_favicon_light"
															alt=""
														/>
														<span v-else class="cs-search-preview__favicon-fallback">{{ previewLetter }}</span>
													</div>
													<div class="cs-search-preview__url">{{ previewUrl }}</div>
												</div>
												<div class="cs-search-preview__title">
													{{ clientOptions.main_title || 'Site title' }}
												</div>
												<div
													class="cs-search-preview__desc"
													:class="{ 'cs-search-preview__desc--empty': !clientOptions.main_description }"
												>
													{{ clientOptions.main_description || 'Add a description to see it here.' }}
												</div>
											</div>
										</div>
									</div>
								</div>

								<hr class="cs-site-settings__divider" />

								<div class="cs-image-row">
									<div class="cs-image-row__info">
										<strong>Favicon</strong>
										<small>64 × 64 pixels</small>
										<p class="cs-image-row__desc">Small icon shown in the browser tab next to the page title.</p>
									</div>
									<div
										class="cs-image-slot cs-image-slot--square"
										:class="{ 'cs-image-slot--empty': !clientOptions.theme_favicon_light }"
									>
										<img
											v-if="clientOptions.theme_favicon_light"
											class="cs-image-slot__img"
											:src="clientOptions.theme_favicon_light"
											alt="Favicon"
										/>
										<div v-else class="cs-image-slot__empty-icon">
											<i class="fas fa-image"></i>
										</div>
										<div class="cs-image-slot__overlay">
											<button
												type="button"
												class="cs-image-slot__upload"
												@click="openMediaPicker('favicon')"
											>
												<i class="fas fa-cloud-upload-alt me-1"></i>Upload
											</button>
										</div>
										<button
											v-if="clientOptions.theme_favicon_light"
											type="button"
											class="cs-image-slot__remove"
											title="Remove favicon"
											@click="clearImage('favicon')"
										>
											<i class="fas fa-times"></i>
										</button>
									</div>
								</div>

								<div class="cs-image-row">
									<div class="cs-image-row__info">
										<strong>Social Preview</strong>
										<small>1200 × 630 pixels</small>
										<p class="cs-image-row__desc">Image shown when the site URL is shared on social networks (also used as Open Graph default).</p>
									</div>
									<div
										class="cs-image-slot cs-image-slot--wide"
										:class="{ 'cs-image-slot--empty': !clientOptions.theme_social_image }"
									>
										<img
											v-if="clientOptions.theme_social_image"
											class="cs-image-slot__img"
											:src="clientOptions.theme_social_image"
											alt="Social icon"
										/>
										<div v-else class="cs-image-slot__empty-icon">
											<i class="fas fa-image"></i>
										</div>
										<div class="cs-image-slot__overlay">
											<button
												type="button"
												class="cs-image-slot__upload"
												@click="openMediaPicker('social_icon')"
											>
												<i class="fas fa-cloud-upload-alt me-1"></i>Upload
											</button>
										</div>
										<button
											v-if="clientOptions.theme_social_image"
											type="button"
											class="cs-image-slot__remove"
											title="Remove social icon"
											@click="clearImage('social_icon')"
										>
											<i class="fas fa-times"></i>
										</button>
									</div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Basic -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Basic</h2>
							<p class="gh-intro__desc">Site-wide timezone and date format used when rendering dates on the public site.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>Timezone</label>
									<select class="form-select" v-model="clientOptions.main_timezone">
										<option value="">Default</option>
										<option v-for="(timezone, i) in timezones" :key="i" :value="timezone.value">{{ timezone.label }}</option>
									</select>
								</div>
								<div class="gh-field">
									<label>Date Format</label>
									<select class="form-select" v-model="clientOptions.main_date_format">
										<option v-for="(dateFormat, i) in dateFormats" :key="i" :value="dateFormat.value">{{ dateFormat.label }}</option>
									</select>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- HeyGov integration -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">HeyGov integration</h2>
							<p class="gh-intro__desc">All Juniper websites need a HeyGov instance for running Events and Meetings. Main API URL is <code>https://api.heygov.com</code>, APP URL is <code>https://app.heygov.com</code>. API Key and Jurisdiction are obtained from HeyGov.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>API Url</label>
									<input type="text" class="form-control" v-model="clientOptions.heygov_api_url" />
								</div>
								<div class="gh-field">
									<label>APP Url</label>
									<input type="text" class="form-control" v-model="clientOptions.heygov_app_url" />
								</div>
								<div class="gh-field">
									<label>API Key</label>
									<input type="text" class="form-control" v-model="clientOptions.heygov_api_key" />
								</div>
								<div class="gh-field">
									<label>Jurisdiction</label>
									<input type="text" class="form-control" v-model="clientOptions.heygov_jurisdiction" />
								</div>
								<div class="d-flex justify-content-end mt-2">
									<a
										class="cs-sync-btn"
										target="_blank"
										:href="heyGovAppUrl(clientOptions.heygov_app_url) + '/' + clientOptions.heygov_jurisdiction"
									>
										<img
											src="https://uploads-ssl.webflow.com/638a65ecc429342f8d6fd704/638a81dede3590b0a04459fc_HeyGov%20Jellybean%20logo.svg"
											width="20px"
											alt=""
										/>
										<span>Visit on HeyGov</span>
									</a>
								</div>
							</div>
						</section>
					</div>

					<!--
						Juniper API URL (dev only) — temporarily hidden. Re-enable when we
						actually need to switch between Juniper API endpoints per-client.
						The clientOptions.client_juniper_api_url field is still in the data
						model and gets saved/loaded normally; nothing else to wire when
						bringing this section back.

					<template v-if="authUser.email.includes('srdjan')">
						<hr class="gh-divider" />
						<div class="gh-layout">
							<aside class="gh-intro">
								<h2 class="gh-intro__title">Juniper API</h2>
								<p class="gh-intro__desc">Internal Juniper API endpoint used by this client. Visible to developers only.</p>
							</aside>
							<section class="gh-accounts">
								<div class="ada-card">
									<div class="gh-field">
										<label>Juniper API Url</label>
										<select class="form-select" v-model="clientOptions.client_juniper_api_url">
											<option value="https://cms6api-ibdts47yoa-uc.a.run.app/api">Production</option>
										</select>
									</div>
								</div>
							</section>
						</div>
					</template>
					-->

					<!-- Restore previous version (operator only) -->
					<template v-if="canRestore">
						<hr class="gh-divider" />
						<div class="gh-layout">
							<aside class="gh-intro">
								<h2 class="gh-intro__title">Restore previous version</h2>
								<p class="gh-intro__desc">
									Roll the live site back to the snapshot saved right before an upgrade was applied.
									Every <strong>Apply to Live</strong> stores a full snapshot of the previous live content.
								</p>
							</aside>
							<section class="gh-accounts">
								<div class="ada-card">
									<div class="cs-restore-warn">
										<i class="fas fa-exclamation-triangle"></i>
										<div>
											Restoring <strong>replaces the current live content</strong> (pages, news, departments,
											files, options, theme…) with the chosen snapshot. The current content is
											<strong>not</strong> archived first. Media files are kept, so images still resolve.
										</div>
									</div>

									<div v-if="restore.loading" class="cs-restore-hint">Loading snapshots…</div>
									<div v-else-if="!restore.archives.length" class="cs-restore-hint">
										No snapshots yet — one is created the first time an upgrade is applied.
									</div>
									<ul v-else class="cs-restore-list">
										<li v-for="a in restore.archives" :key="a.id" class="cs-restore-item">
											<span class="cs-restore-meta">
												<i class="fas fa-history"></i>
												{{ formatRestoreDate(a.archived_at) }}
												<small v-if="a.archived_by"> · by {{ a.archived_by.email }}</small>
												<small v-if="a.restored_at" class="cs-restore-done"> · restored {{ formatRestoreDate(a.restored_at) }}</small>
											</span>
											<button class="cs-restore-btn" :disabled="restore.busy" @click="openRestoreConfirm(a)">
												<i class="fas fa-undo"></i> Restore
											</button>
										</li>
									</ul>
								</div>
							</section>
						</div>

						<!-- confirm modal -->
						<div v-if="restore.showConfirm" class="cs-modal-backdrop">
							<div class="cs-modal">
								<div class="cs-modal__icon"><i class="fas fa-exclamation-triangle"></i></div>
								<h5 class="cs-modal__title">Restore this snapshot?</h5>
								<p class="cs-modal__msg">
									The current live content will be <strong>replaced</strong> with the snapshot from
									<strong>{{ formatRestoreDate(restore.selected && restore.selected.archived_at) }}</strong>.
									This is not automatically reversible.
								</p>
								<div class="cs-modal__actions">
									<button class="gh-save-btn cs-modal__cancel" :disabled="restore.busy" @click="closeRestoreConfirm">Cancel</button>
									<button class="cs-restore-confirm" :disabled="restore.busy" @click="doRestore">
										<span v-if="restore.busy" class="spinner-border spinner-border-sm me-2" role="status"></span>
										<i v-else class="fas fa-undo me-1"></i> Yes, restore
									</button>
								</div>
							</div>
						</div>
					</template>


					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 8: Integrations (renames the old Analytics tab — now holds Google Analytics + Mail provider; renders second-to-last per tab order above) -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'integrations'" class="col-md-12 mt-3 js-tab-content">
					<!--
						UI cleanup notes — all dead sections removed (none of these were
						read by any theme or by StaticController::getSiteMeta):

						- "SEO basics" (Meta Keywords + Allow Indexing)
							keys: seo_meta_keywords, seo_allow_indexing
							No <meta name="keywords"> or <meta name="robots"> emission anywhere.

						- "Social cards" (Open Graph + Twitter Card)
							keys: seo_og_title, seo_og_description, seo_twitter_card_type, seo_twitter_site
							Theme emits og:title/description/site_name/image automatically from
							main_title / main_description / theme_social_image (no separate OG
							fields needed). Zero Twitter meta tags are ever emitted.

						- "Verification codes" (Google + Bing site verification)
							keys: seo_google_verification, seo_bing_verification
							No <meta name="google-site-verification"> / "msvalidate.01" emission.

						All option keys are still in `clientOptions` data() so existing saved
						values aren't dropped. The SEO tab now contains only what actually
						works: Google Analytics. (Facebook Syndication was moved to the
						Modules tab → Integrations group — it's a feature integration, not
						SEO-related.)
					-->

					<!-- Google Analytics -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Google Analytics</h2>
							<p class="gh-intro__desc">Tracking code embedded on every public-site page.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>Google Analytic Code</label>
									<input type="text" class="form-control" v-model="clientOptions.google_analytic_code" />
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Mail provider (moved here from Audience tab) -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Mail provider</h2>
							<p class="gh-intro__desc">Outbound email is delivered through SendGrid or Mailgun. Choose one and supply the required keys.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="form-check form-check-inline mb-3">
									<input class="form-check-input" type="radio" v-model="clientOptions.mailer" id="sendgrid" value="sendgrid" />
									<label class="form-check-label" for="sendgrid"><img width="200" src="../../../assets/images/sendgrid.png" alt=""/></label>
								</div>
								<div class="form-check form-check-inline mb-3">
									<input class="form-check-input" type="radio" v-model="clientOptions.mailer" id="mailgun" value="mailgun" />
									<label class="form-check-label" for="mailgun"><img width="200" src="../../../assets/images/mailgun.png" alt=""/></label>
								</div>
								<div v-if="clientOptions.mailer == 'mailgun'" class="mailgun-options mb-2">
									<div class="gh-field">
										<label>Mailgun Key</label>
										<input type="text" class="form-control" v-model="clientOptions.mailer_mailgun_key" />
									</div>
									<div class="gh-field">
										<label>Mailgun Domain</label>
										<input type="text" class="form-control" v-model="clientOptions.mailer_mailgun_domain" />
									</div>
								</div>
								<div v-if="clientOptions.mailer == 'sendgrid'" class="sendgrid-options mb-2">
									<div class="gh-field">
										<label>Sendgrid Key</label>
										<input type="text" class="form-control" v-model="clientOptions.mailer_sendgrid_key" />
									</div>
								</div>
								<div class="gh-field">
									<label>Mail From</label>
									<input type="text" class="form-control" v-model="clientOptions.mail_from" />
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB: Domains (sits right after General per tab order) -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'domains'" class="col-md-12 mt-3 js-tab-content">
					<!-- Base domain (auto-generated staging URL) -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Base domain <span class="gh-intro__badge">read-only</span></h2>
							<p class="gh-intro__desc">Auto-generated Town Web address. Used as a fallback while DNS for your custom domains is propagating, and as the canonical internal URL for staging/preview.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="cs-domain-row cs-domain-row--locked">
									<font-awesome-icon :icon="['fas', 'lock']" class="cs-domain-row__icon" />
									<span class="cs-domain-row__name">{{ cleanedStagingUrl() || '—' }}</span>
									<small class="cs-domain-row__meta">Town Web</small>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Custom domains: locked primary (client.url) + array-stored extras -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">
								Custom domains
								<span class="cs-count-badge">{{ totalCustomDomains }}</span>
							</h2>
							<p class="gh-intro__desc">Map your own hostnames to this site. The primary domain is set during client provisioning and can't be changed from here. Additional domains can be added and removed at any time.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<!-- Primary (client.url) — locked, can expand DNS but not delete -->
								<div v-if="cleanedClientUrl()" class="mb-2">
									<div class="cs-domain-row cs-domain-row--locked" :class="{ 'cs-domain-row--expanded': expandedDomain === 'primary' }">
										<font-awesome-icon :icon="['fas', 'lock']" class="cs-domain-row__icon" />
										<span class="cs-domain-row__name">{{ cleanedClientUrl() }}</span>
										<small class="cs-domain-row__meta">Primary</small>
										<button
											type="button"
											class="cs-domain-row__expand"
											@click="toggleDnsInstructions('primary')"
											:title="expandedDomain === 'primary' ? 'Hide DNS instructions' : 'Show DNS instructions'"
										>
											<font-awesome-icon :icon="['fas', expandedDomain === 'primary' ? 'chevron-up' : 'chevron-down']" />
										</button>
									</div>
									<div v-show="expandedDomain === 'primary'" class="cs-tip mt-2">
										<div class="cs-tip__icon">
											<font-awesome-icon :icon="['fas', 'globe']" />
										</div>
										<div class="cs-tip__body">
											<strong>DNS configuration for {{ cleanedClientUrl() }}</strong>
											<p v-if="isApexDomain(cleanedClientUrl())">
												<em>{{ cleanedClientUrl() }}</em> is an <strong>apex</strong> domain. Add an <strong>A record</strong> pointing to the Town Web server IP, or an <strong>ALIAS / ANAME</strong> record if your DNS provider supports it.
											</p>
											<p v-else>
												Add this <strong>CNAME</strong> record at your DNS registrar:
											</p>
											<dl class="cs-dns-record">
												<dt>Type</dt>
												<dd><code>{{ isApexDomain(cleanedClientUrl()) ? 'A / ALIAS' : 'CNAME' }}</code></dd>
												<dt>Name</dt>
												<dd><code>{{ dnsRecordName(cleanedClientUrl()) }}</code></dd>
												<dt>Value</dt>
												<dd class="cs-dns-record__value">
													<code>{{ DNS_TARGET_HOST }}</code>
													<button type="button" class="cs-dns-record__copy" @click="copyToClipboard(DNS_TARGET_HOST)" title="Copy to clipboard">
														<font-awesome-icon :icon="['fas', 'copy']" />
													</button>
												</dd>
												<dt>TTL</dt>
												<dd><code>Auto</code> or <code>3600</code></dd>
											</dl>
											<p class="cs-dns-record__note">
												<small>DNS changes can take up to 48 hours to propagate worldwide. Most updates appear within a few minutes.</small>
											</p>
										</div>
									</div>
								</div>

								<!-- Additional custom domains from clientOptions.custom_domains array -->
								<template v-if="clientOptions.custom_domains && clientOptions.custom_domains.length > 0">
									<div v-for="(domain, idx) in clientOptions.custom_domains" :key="domain + '-' + idx" class="mb-2">
										<div class="cs-domain-row" :class="{ 'cs-domain-row--expanded': expandedDomain === idx }">
											<font-awesome-icon :icon="['fas', 'globe']" class="cs-domain-row__icon" />
											<span class="cs-domain-row__name">{{ domain }}</span>
											<button
												type="button"
												class="cs-domain-row__expand"
												@click="toggleDnsInstructions(idx)"
												:title="expandedDomain === idx ? 'Hide DNS instructions' : 'Show DNS instructions'"
											>
												<font-awesome-icon :icon="['fas', expandedDomain === idx ? 'chevron-up' : 'chevron-down']" />
											</button>
											<button
												type="button"
												class="cs-domain-row__delete"
												@click="removeCustomDomain(idx)"
												title="Remove domain"
											>
												<font-awesome-icon :icon="['fas', 'trash']" />
											</button>
										</div>

										<!-- DNS instructions card — visible only when this row is expanded -->
										<div v-show="expandedDomain === idx" class="cs-tip mt-2">
											<div class="cs-tip__icon">
												<font-awesome-icon :icon="['fas', 'globe']" />
											</div>
											<div class="cs-tip__body">
												<strong>DNS configuration for {{ domain }}</strong>
												<p v-if="isApexDomain(domain)">
													<em>{{ domain }}</em> is an <strong>apex</strong> domain. Add an <strong>A record</strong> pointing to the Town Web server IP, or an <strong>ALIAS / ANAME</strong> record if your DNS provider supports it.
												</p>
												<p v-else>
													Add this <strong>CNAME</strong> record at your DNS registrar (GoDaddy, Cloudflare, Namecheap, etc.):
												</p>
												<dl class="cs-dns-record">
													<dt>Type</dt>
													<dd><code>{{ isApexDomain(domain) ? 'A / ALIAS' : 'CNAME' }}</code></dd>
													<dt>Name</dt>
													<dd><code>{{ dnsRecordName(domain) }}</code></dd>
													<dt>Value</dt>
													<dd class="cs-dns-record__value">
														<code>{{ DNS_TARGET_HOST }}</code>
														<button
															type="button"
															class="cs-dns-record__copy"
															@click="copyToClipboard(DNS_TARGET_HOST)"
															title="Copy to clipboard"
														>
															<font-awesome-icon :icon="['fas', 'copy']" />
														</button>
													</dd>
													<dt>TTL</dt>
													<dd><code>Auto</code> or <code>3600</code></dd>
												</dl>
												<p class="cs-dns-record__note">
													<small>DNS changes can take up to 48 hours to propagate worldwide. Most updates appear within a few minutes.</small>
												</p>
											</div>
										</div>
									</div>
								</template>

								<!-- Inline add form (shown when "Add" pressed) -->
								<div v-if="addingDomain" class="cs-add-form">
									<h6 class="mb-3">Add custom domain</h6>
									<div class="gh-field">
										<label>Domain hostname</label>
										<input
											type="text"
											class="form-control"
											v-model="newDomain"
											placeholder="www.example.com"
											@keyup.enter="confirmAddDomain()"
										/>
										<div class="gh-help">Full hostname without protocol — e.g. <code>www.example.com</code> or <code>example.com</code>.</div>
									</div>
									<div class="d-flex justify-content-end gap-2 mt-3">
										<button @click="cancelAddDomain()" type="button" class="btn btn-outline-secondary btn-sm">Cancel</button>
										<button @click="confirmAddDomain()" type="button" class="btn btn-primary btn-sm" :disabled="!newDomain.trim()">
											<font-awesome-icon :icon="['fas', 'plus']" class="me-1" />Add domain
										</button>
									</div>
								</div>
								<div v-else class="d-flex justify-content-end mt-3">
									<button @click="startAddDomain()" type="button" class="cs-add-btn">
										<font-awesome-icon :icon="['fas', 'plus']" />
										<span>Add custom domain</span>
									</button>
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB: Posts — HIDDEN (tab button is commented out in the nav above) -->
				<!-- ============================================================ -->
				<!--
				<div v-show="activeTab === 'posts'" class="col-md-12 mt-3 js-tab-content">
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Posts</h2>
							<p class="gh-intro__desc">URL prefix and templates used for the Posts post-type (single + archive).</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>Posts Prefix</label>
									<input type="text" class="form-control" v-model="clientOptions.Post_prefix" />
									<div class="gh-help">All posts page is: <code>{{ client.url }}/{{ clientOptions.Post_prefix }}</code>. This change is visible after rebuilding the site.</div>
								</div>
								<div class="gh-field">
									<label>Posts Single Template</label>
									<select class="form-select" v-model="clientOptions.Post_single_template">
										<option value="default">default</option>
										<option v-for="(template, i) in postsSingleTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
									</select>
								</div>
								<div class="gh-field">
									<label>Posts Archive Template</label>
									<select class="form-select" v-model="clientOptions.Post_archive_template">
										<option value="default">default</option>
										<option v-for="(template, i) in postsArchiveTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
									</select>
								</div>
								<div class="gh-field">
									<label>Posts Per Page</label>
									<input type="number" min="1" max="50" class="form-control" v-model="clientOptions.Post_per_page" />
									<div class="gh-help">All post list pages will have <code>{{ clientOptions.Post_per_page }}</code> post/s per page.</div>
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>
				-->

				<!-- ============================================================ -->
				<!-- TAB 3a: News & Notices (split from old News & FAQs) -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'news'" class="col-md-12 mt-3 js-tab-content">
					<div v-if="isUnifiedDashboard" class="cs-tip mb-5">
						<div class="cs-tip__icon">
							<font-awesome-icon :icon="['fas', 'newspaper']" />
						</div>
						<div class="cs-tip__body">
							<strong>The news archive lives on the Unified Dashboard.</strong>
							<p>Pick a page below to serve as the public news archive — it renders through the <strong>News &amp; Notices</strong> element in the <strong>Page Builder</strong>, so its filters, pagination and card layout stay editable on the page itself. The theme's archive list is then no longer generated, and <em>News Prefix</em> follows that page's URL automatically. Single news pages keep using the theme template below.</p>
						</div>
					</div>

					<!-- News archive page (Unified Dashboard only) -->
					<div v-if="isUnifiedDashboard" class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Archive page <span class="gh-intro__badge">News &amp; Notices</span></h2>
							<p class="gh-intro__desc">The page that serves as the public news archive. Single news pages live under its URL, and everything linking to the archive — widgets, search, sitemap — points at it.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div v-if="!newsArchive.eligible.ok" class="cs-tip cs-tip--warning mb-3">
									<div class="cs-tip__icon">
										<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
									</div>
									<div class="cs-tip__body">
										<p class="mb-0">{{ newsArchive.eligible.reason }}</p>
									</div>
								</div>

								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'newspaper']" class="me-1 text-secondary" />
										News archive page
									</label>
									<select
										class="form-select"
										v-model="clientOptions.NewsNotice_archive_page_id"
										:disabled="newsArchive.loading || !newsArchive.eligible.ok"
										@change="onNewsArchivePageChange()"
									>
										<option value="">Theme-generated archive (no page)</option>
										<option v-for="page in selectableNewsArchivePages" :key="page.id" :value="page.id">
											{{ page.title }} — /{{ page.slug }}{{ page.missingElement ? ' (no News & Notices element!)' : '' }}
										</option>
									</select>
									<div class="gh-help" v-if="selectableNewsArchivePages.length">
										Only published top-level pages holding a <strong>News &amp; Notices</strong> element are listed. Changing the page
										changes the URL of every news article, so the whole site is rebuilt automatically.
									</div>
									<div class="gh-help" v-else>
										No page holds a <strong>News &amp; Notices</strong> element yet. Use <em>Create news page</em> below, or drop the
										element onto an existing page in the Page Builder and it will show up here.
									</div>
								</div>

								<!-- What is actually serving the archive URL right now -->
								<div v-if="newsArchive.page" class="gh-help mt-2">
									Serving <code>{{ newsArchive.page.url }}</code> — the theme archive list is
									<strong>{{ newsArchive.themeArchiveGenerated ? 'still generated' : 'no longer generated' }}</strong>.
								</div>
								<div v-else-if="clientOptions.NewsNotice_archive_page_id" class="cs-tip cs-tip--warning mt-3">
									<div class="cs-tip__icon">
										<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
									</div>
									<div class="cs-tip__body">
										<p class="mb-0">The selected page is not published (draft, archived or trashed), so the theme's archive is generated instead. Publish the page to hand the URL back to it.</p>
									</div>
								</div>

								<!-- Temporary convenience, like the events one: creating the page moves into the client launch flow later -->
								<template v-if="!newsArchive.nominatedPageId">
									<hr class="gh-divider" />
									<div class="d-flex align-items-center gap-3 flex-wrap">
										<button
											class="gh-save-btn gh-save-btn--sm"
											type="button"
											:disabled="newsArchive.creating || !newsArchive.eligible.ok"
											@click="createNewsArchivePage()"
										>
											<font-awesome-icon :icon="['fas', 'plus']" />
											{{ newsArchive.creating ? 'Creating…' : 'Create news page' }}
										</button>
										<span class="gh-help mb-0">Creates a published page holding a News &amp; Notices element and points the options at it. Rebuild the site to publish it.</span>
									</div>
								</template>
							</div>
						</section>
					</div>
					<hr v-if="isUnifiedDashboard" class="gh-divider" />

					<!-- Once a page serves the news archive (Unified Dashboard) its URL is the prefix and categories live in the element — only the single template is set here -->
					<template v-if="!newsArchiveOwnsPrefix">
					<!-- News Basic -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Basic <span class="gh-intro__badge">News &amp; Notices</span></h2>
							<p class="gh-intro__desc">URL prefix for the public news list page.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'link']" class="me-1 text-secondary" />
										News Prefix
									</label>
									<input type="text" class="form-control" v-model="clientOptions.NewsNotice_prefix" />
									<div class="gh-help">News list page is: <code>{{ client.url }}/{{ clientOptions.NewsNotice_prefix }}</code></div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- News Categories -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Categories <span class="gh-intro__badge">News &amp; Notices</span></h2>
							<p class="gh-intro__desc">Toggle a category on to make it subscribable. Edit or delete via the row actions.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="d-flex justify-content-between align-items-center mb-3">
									<small class="text-muted">Toggle category to make it subscribable</small>
									<button @click="addNewsCategoryFlag = true" class="btn btn-primary btn-sm">
										+ Add
									</button>
								</div>
								<div v-for="category in newsNoticeCategories" :key="category.id" class="mb-2">
									<div class="form-check form-switch d-flex justify-content-start align-items-center">
										<input class="form-check-input me-2" v-model="newsNoticeCategoriesSubscribable" :value="category.id" type="checkbox" />
										<label class="form-check-label">{{ category.name }}</label>
										<div class="ms-auto">
											<button @click="showUpdateCategoryOffcanvas(category, 'news')" class="btn btn-info btn-sm me-1" title="Update Category">
												<font-awesome-icon :icon="['fas', 'edit']" />
											</button>
											<button @click="deleteCategory(category, 'news')" class="btn btn-danger btn-sm" title="Delete category">
												<font-awesome-icon :icon="['fas', 'trash']" />
											</button>
										</div>
									</div>
								</div>
								<div v-if="addNewsCategoryFlag" class="card mt-3">
									<div class="card-body">
										<h6>Add News Notice Category</h6>
										<div class="form-group">
											<label class="mb-1">Name</label>
											<input class="form-control form-control-sm" type="text" v-model="newNewsCategory.name" />
										</div>
										<div class="d-flex justify-content-between align-items-center mt-3">
											<button @click="cancelAddNewNewsCategory()" class="btn btn-secondary btn-sm">Cancel</button>
											<button @click="storeNewNewsCategory()" class="btn btn-primary btn-sm">
												<font-awesome-icon
													:icon="['fas', storeCategoryLoading ? 'spinner' : 'save']"
													:class="[storeCategoryLoading ? 'fa-spin me-1' : 'me-1']"
												/>Save
											</button>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />
					</template>

					<!-- News Templates (merged: single page + archive) -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">{{ newsArchive.live ? 'Single page' : 'Templates' }} <span class="gh-intro__badge">News &amp; Notices</span></h2>
							<p v-if="newsArchive.live" class="gh-intro__desc">Template used to render the single-article pages. The archive listing is the page selected above.</p>
							<p v-else class="gh-intro__desc">Templates used to render the single-article page and the archive listing, plus archive-only options (per-page count, JSON support, pagination type).</p>
						</aside>
						<section class="gh-accounts">
							<!-- Card 1: Single page template -->
							<div class="ada-card">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'file-alt']" class="me-1 text-secondary" />
										Single page template
									</label>
									<div class="input-group">
										<select class="form-select" v-model="clientOptions.NewsNotice_single_template">
											<option value="default">default</option>
											<option v-for="(template, i) in newsSingleTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
										</select>
										<button class="btn btn-primary" type="button" @click="loadTemplateVariables('news', 'single')" title="Configure template variables">Conf</button>
									</div>
								</div>
							</div>

							<!-- Card 2: Archive template + listing options — only read by the theme-generated archive -->
							<div v-if="!newsArchive.live" class="ada-card mt-3">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'th-list']" class="me-1 text-secondary" />
										Archive template
									</label>
									<div class="input-group">
										<select class="form-select" v-model="clientOptions.NewsNotice_archive_template">
											<option value="default">default</option>
											<option v-for="(template, i) in newsArchiveTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
										</select>
										<button class="btn btn-primary" type="button" @click="loadTemplateVariables('news', 'archive')" title="Configure template variables">Conf</button>
									</div>
								</div>

								<!-- Archive listing options — compact row -->
								<div class="cs-compact-row">
									<div class="cs-compact-field cs-compact-field--xs">
										<label>
											<font-awesome-icon :icon="['fas', 'list-ol']" class="me-1 text-secondary" />
											Per page
										</label>
										<input type="number" min="1" max="50" class="form-control" v-model="clientOptions.NewsNotice_per_page" />
									</div>
									<div class="cs-compact-field cs-compact-field--sm">
										<label>Pagination</label>
										<select class="form-select" v-model="clientOptions.NewsNotice_pagination_type">
											<option value="pages">Pages</option>
											<option v-if="clientOptions.NewsNotice_use_json" value="infinite">Infinite</option>
										</select>
									</div>
									<label class="cs-compact-toggle form-check form-switch" title="Use JSON data source (supported on the 2025 theme only)">
										<input class="form-check-input" type="checkbox" v-model="clientOptions.NewsNotice_use_json" />
										<span class="form-check-label">Use JSON</span>
									</label>
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 3b: FAQs (split from old News & FAQs) -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'faqs'" class="col-md-12 mt-3 js-tab-content">
					<div v-if="isUnifiedDashboard" class="cs-tip cs-tip--warning">
						<div class="cs-tip__icon">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
						</div>
						<div class="cs-tip__body">
							<strong>This settings page will be deprecated.</strong>
							<p>On the Unified Dashboard, <em>FAQs</em> are managed directly through <strong>Pages → Page Builder</strong>. You can keep editing here for now, but new clients should configure FAQs via Page Builder elements.</p>
						</div>
					</div>

					<!-- FAQs Basic -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Basic <span class="gh-intro__badge">FAQs</span></h2>
							<p class="gh-intro__desc">URL prefix for the FAQs archive plus accordion-archive presentation toggles (theme-gated).</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'link']" class="me-1 text-secondary" />
										Faqs Prefix
									</label>
									<input type="text" class="form-control" v-model="clientOptions.Faq_prefix" />
									<div class="gh-help">All faqs page is: <code>{{ client.url }}/{{ clientOptions.Faq_prefix }}</code>. This change is visible after rebuilding the site.</div>
								</div>
								<!-- Accordion presentation (theme-gated): only renders on themes that ship `faqs/archive/accordions.php`. On other themes StaticController falls back to the regular archive template, so the toggles below are disabled. -->
								<div class="mt-3 pt-3 border-top">
									<div class="d-flex align-items-center mb-2">
										<h6 class="m-0 me-2" style="font-size: 0.9rem; font-weight: 600;">Accordion archive</h6>
										<span
											v-if="themeCapabilities"
											class="badge"
											:class="themeCapabilities.faq_accordions ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'"
											style="font-weight: 500; font-size: 0.7rem"
										>
											<font-awesome-icon :icon="themeCapabilities.faq_accordions ? 'check-circle' : 'times-circle'" class="me-1" />
											{{ themeCapabilities.faq_accordions ? 'theme supports' : 'theme does not support' }}
										</span>
									</div>
									<small
										v-if="themeCapabilities && !themeCapabilities.faq_accordions"
										class="d-block text-danger mb-2"
										style="font-size: 0.78rem"
									>
										The active theme (<code>{{ themeCapabilities.theme }}</code>) has no <code>faqs/archive/accordions.php</code> template — these toggles are disabled and the archive renders with the regular template.
									</small>
									<div class="form-check mb-2">
										<input
											class="form-check-input"
											type="checkbox"
											id="faqUseAccordions"
											v-model="clientOptions.Faq_archive_use_accordions"
											:disabled="themeCapabilities && themeCapabilities.faq_accordions === false"
										/>
										<label class="form-check-label" for="faqUseAccordions">Use Accordion Archiving</label>
									</div>
									<div class="form-check">
										<input
											class="form-check-input"
											type="checkbox"
											id="faqCategorizeAccordions"
											v-model="clientOptions.Faq_categorize_accordions"
											:disabled="themeCapabilities && themeCapabilities.faq_accordions === false"
										/>
										<label class="form-check-label" for="faqCategorizeAccordions">Use categorization of FAQs</label>
									</div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- FAQs Categories -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Categories <span class="gh-intro__badge">FAQs</span></h2>
							<p class="gh-intro__desc">Manage the categories used to group FAQs in the archive.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="d-flex justify-content-between align-items-center mb-3">
									<h5 class="p-0 m-0">Faqs Categories</h5>
									<button @click="addFaqsCategoryFlag = true" class="btn btn-primary btn-sm">+ Add</button>
								</div>
								<draggable v-model="faqsCategories" handle=".cs-cat-row__handle" @end="updateFaqsCategoriesOrder()" class="cs-cat-list">
									<div v-for="category in faqsCategories" :key="category.id" class="cs-cat-row">
										<span class="cs-cat-row__handle" title="Drag to reorder">
											<font-awesome-icon :icon="['fas', 'arrows-alt']" />
										</span>
										<span class="cs-cat-row__name flex-grow-1">{{ category.name }}</span>
										<div class="cs-cat-row__actions">
											<button @click="showUpdateCategoryOffcanvas(category, 'faqs')" class="cs-cat-row__action" title="Edit category">
												<font-awesome-icon :icon="['fas', 'edit']" />
											</button>
											<button @click="deleteCategory(category, 'faqs')" class="cs-cat-row__action cs-cat-row__action--danger" title="Delete category">
												<font-awesome-icon :icon="['fas', 'trash']" />
											</button>
										</div>
									</div>
								</draggable>
								<div v-if="faqsCategories.length == 0" class="bg-primary-light rounded shadow-sm text-center p-3">
									You don't have any 'FAQs' category created yet
								</div>
								<div v-if="addFaqsCategoryFlag" class="card mt-3">
									<div class="card-body">
										<h6>Add Faqs Category</h6>
										<div class="form-group">
											<label class="mb-1">Name</label>
											<input class="form-control form-control-sm" type="text" v-model="newFaqsCategory.name" />
										</div>
										<div class="d-flex justify-content-between align-items-center mt-3">
											<button @click="cancelAddingFaqsCategory()" class="btn btn-secondary btn-sm">Cancel</button>
											<button @click="storeFaqsCategory()" class="btn btn-primary btn-sm">
												<font-awesome-icon
													:icon="['fas', storeFaqsCategoryLoading ? 'spinner' : 'save']"
													:class="[storeFaqsCategoryLoading ? 'fa-spin me-1' : 'me-1']"
												/>
												Save
											</button>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- FAQs Templates (split into 2 cards: single + archive, with all archive options merged into the archive card) -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Templates <span class="gh-intro__badge">FAQs</span></h2>
							<p class="gh-intro__desc">Templates used to render the single-FAQ page and the archive listing, plus archive-only options (per-page, pagination, JSON, page title, sort order, accordion-specific controls).</p>
						</aside>
						<section class="gh-accounts">
							<!-- Card 1: Single page template -->
							<div class="ada-card">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'file-alt']" class="me-1 text-secondary" />
										Single page template
									</label>
									<select class="form-select" v-model="clientOptions.Faq_single_template">
										<option value="default">default</option>
										<option v-for="(template, i) in faqsSingleTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
									</select>
								</div>
							</div>

							<!-- Card 2: Archive template + all archive-only options -->
							<div class="ada-card mt-3">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'th-list']" class="me-1 text-secondary" />
										Archive template
									</label>
									<div class="input-group">
										<select class="form-select" v-model="clientOptions.Faq_archive_template">
											<option value="default">default</option>
											<option v-for="(template, i) in faqsArchiveTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
										</select>
										<button class="btn btn-primary" type="button" @click="loadTemplateVariables('faqs', 'archive')" title="Configure template variables">Conf</button>
									</div>
								</div>

								<!-- Listing controls — single compact row: Per page · Order by · Direction · Pagination · Use JSON -->
								<div class="cs-compact-row">
									<div class="cs-compact-field cs-compact-field--xs">
										<label>
											<font-awesome-icon :icon="['fas', 'list-ol']" class="me-1 text-secondary" />
											Per page
										</label>
										<input type="number" min="1" max="50" class="form-control" v-model="clientOptions.Faq_per_page" />
									</div>
									<div class="cs-compact-field cs-compact-field--md">
										<label>
											<font-awesome-icon
												:icon="['fas', clientOptions.Faq_archive_order_column === 'title' ? 'font' : 'calendar-alt']"
												class="me-1 text-secondary"
											/>
											Order by
										</label>
										<select class="form-select" v-model="clientOptions.Faq_archive_order_column">
											<option value="created_at">Created date</option>
											<option value="title">Name</option>
										</select>
									</div>
									<div class="cs-compact-field cs-compact-field--md">
										<label>
											<font-awesome-icon
												:icon="['fas', clientOptions.Faq_archive_order === 'DESC' ? 'sort-amount-down' : 'sort-amount-up']"
												class="me-1 text-secondary"
											/>
											Direction
										</label>
										<select class="form-select" v-model="clientOptions.Faq_archive_order">
											<option value="ASC">Ascending (oldest first)</option>
											<option value="DESC">Descending (newest first)</option>
										</select>
									</div>
									<div class="cs-compact-field cs-compact-field--md">
										<label>Pagination</label>
										<select class="form-select" v-model="clientOptions.Faq_pagination_type">
											<option v-if="!clientOptions.Faq_show_all_categories" value="pages">Pages</option>
											<option v-if="clientOptions.Faq_use_json" value="infinite">Infinite</option>
										</select>
									</div>
									<label class="cs-compact-toggle form-check form-switch" title="Use JSON data source (supported on the 2025 theme only)">
										<input class="form-check-input" type="checkbox" v-model="clientOptions.Faq_use_json" />
										<span class="form-check-label">Use JSON</span>
									</label>
								</div>

								<!-- Accordion-specific controls (only when JSON + accordion template active) -->
								<div
									v-if="
										clientOptions.Faq_use_json &&
											clientOptions.Faq_archive_template &&
											clientOptions.Faq_archive_template.toLowerCase().includes('accordion')
									"
									class="cs-compact-row"
								>
									<label class="cs-compact-toggle form-check form-switch" style="margin-left: 0;">
										<input class="form-check-input" type="checkbox" v-model="clientOptions.Faq_show_all_categories" />
										<span class="form-check-label">Show all categories on one page</span>
									</label>
									<div class="cs-compact-field cs-compact-field--xs" style="margin-left: auto;">
										<label>Columns/row</label>
										<input type="number" min="1" max="2" class="form-control" v-model="clientOptions.Faq_columns_per_row" />
									</div>
								</div>

								<!-- Archive page title (used by every theme) -->
								<div class="gh-field cs-compact-row" style="display: block;">
									<label>Archive page title</label>
									<input type="text" class="form-control" v-model="clientOptions.Faq_archive_page_title" />
									<div class="gh-help">Default is <code>"Faqs"</code>. Leave empty to hide the title.</div>
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 5: Departments -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'departments'" class="col-md-12 mt-3 js-tab-content">
					<div v-if="isUnifiedDashboard" class="cs-tip cs-tip--warning">
						<div class="cs-tip__icon">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
						</div>
						<div class="cs-tip__body">
							<strong>This settings page will be deprecated.</strong>
							<p>On the Unified Dashboard, <em>Departments</em> are managed directly through <strong>Pages → Page Builder</strong>. You can keep editing here for now, but new clients should configure Departments via Page Builder elements.</p>
						</div>
					</div>

					<!-- Basic — town-web only: unified sites always build /departments, /boards, /commissions, /committees and have documents + quick menu on -->
					<div v-if="!isUnifiedDashboard" class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Basic <span class="gh-intro__badge">Departments</span></h2>
							<p class="gh-intro__desc">URL prefix and theme-specific feature toggles for department documents and quick menus.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'link']" class="me-1 text-secondary" />
										Departments Prefix
									</label>
									<input type="text" class="form-control" v-model="clientOptions.Department_prefix" />
									<div class="gh-help">Departments list page is: <code>{{ client.url }}/{{ clientOptions.Department_prefix }}</code></div>
								</div>
								<div v-if="themeSupportsDepartmentDocuments" class="form-group mb-2">
									<div class="cs-tip">
										<div class="cs-tip__icon">
											<font-awesome-icon :icon="['fas', 'info-circle']" />
										</div>
										<div class="cs-tip__body">
											<strong>This theme supports department documents.</strong>
											Enable the toggle below so Town Web dashboard users can attach documents to individual departments.
										</div>
									</div>
									<div class="form-check form-switch d-flex justify-content-start align-items-center">
										<input class="form-check-input me-2" v-model="clientOptions.Department_single_use_documents" type="checkbox" />
										<label class="form-check-label">Use documents</label>
									</div>
								</div>
								<div v-if="themeSupportsDepartmentQuickMenu" class="form-group mb-2">
									<div class="cs-tip">
										<div class="cs-tip__icon">
											<font-awesome-icon :icon="['fas', 'info-circle']" />
										</div>
										<div class="cs-tip__body">
											<strong>This theme supports a department quick menu.</strong>
											Enable the toggle below so Town Web dashboard users can attach a quick menu to individual departments.
										</div>
									</div>
									<div class="form-check form-switch d-flex justify-content-start align-items-center">
										<input class="form-check-input me-2" v-model="clientOptions.Department_single_use_quick_menu" type="checkbox" />
										<label class="form-check-label">Use quick menu</label>
									</div>
								</div>
							</div>
						</section>
					</div>
					<hr v-if="!isUnifiedDashboard" class="gh-divider" />

					<!-- "Department source" section was removed — it's now implicitly managed by the dashboard choice in General → Dashboard (Unified → Department_source_heygov=true, Town Web → false). See the watcher on `clientOptions.main_dashboard` in <script>. -->

					<!-- Homepage Sections -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Homepage sections</h2>
							<p class="gh-intro__desc">Order departments are displayed in sections rendered on the homepage.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-grid gh-grid--2">
									<div class="gh-field">
										<label>
											<font-awesome-icon
												:icon="['fas', clientOptions.Department_homepage_order_column === 'title' ? 'font' : 'calendar-alt']"
												class="me-1 text-secondary"
											/>
											Order By
										</label>
										<select class="form-select" v-model="clientOptions.Department_homepage_order_column">
											<option value="created_at">Created Date</option>
											<option value="title">Name</option>
										</select>
									</div>
									<div class="gh-field">
										<label>
											<font-awesome-icon
												:icon="['fas', clientOptions.Department_homepage_order === 'DESC' ? 'sort-amount-down' : 'sort-amount-up']"
												class="me-1 text-secondary"
											/>
											Order Direction
										</label>
										<select class="form-select" v-model="clientOptions.Department_homepage_order">
											<option value="ASC">Ascending (oldest first)</option>
											<option value="DESC">Descending (newest first)</option>
										</select>
									</div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Departments Templates (split into 2 cards: single + archive, with all archive options merged into the archive card) -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Templates <span class="gh-intro__badge">Departments</span></h2>
							<p class="gh-intro__desc">Templates used to render the single-department page and the archive listing, plus archive-only options (per-page, sort order). Archive renders at <code>/{{ clientOptions.Department_prefix || 'departments' }}</code>.</p>
						</aside>
						<section class="gh-accounts">
							<!-- Card 1: Single page template -->
							<div class="ada-card">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'file-alt']" class="me-1 text-secondary" />
										Single page template
									</label>
									<div class="input-group">
										<select class="form-select" v-model="clientOptions.Department_single_template">
											<option value="default">default</option>
											<option v-for="(template, i) in departmentsSingleTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
										</select>
										<button class="btn btn-primary" type="button" @click="loadTemplateVariables('departments', 'single')" title="Configure template variables">Conf</button>
									</div>
								</div>
								<div class="form-check form-switch d-flex justify-content-start align-items-center mt-2">
									<input class="form-check-input me-2" v-model="clientOptions.Department_single_show_default_image" type="checkbox" id="deptShowDefaultImg" />
									<label class="form-check-label" for="deptShowDefaultImg">Show default featured image on single page</label>
								</div>
							</div>

							<!-- Card 2: Archive template + listing options -->
							<div class="ada-card mt-3">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'th-list']" class="me-1 text-secondary" />
										Archive template
									</label>
									<div class="input-group">
										<select class="form-select" v-model="clientOptions.Department_archive_template">
											<option value="default">default</option>
											<option v-for="(template, i) in departmentsArchiveTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
										</select>
										<button class="btn btn-primary" type="button" @click="loadTemplateVariables('departments', 'archive')" title="Configure template variables">Conf</button>
									</div>
								</div>

								<!-- Listing controls — single compact row: Per page · Sort By · Sort Order (town-web only: unified archives list every entity, no pagination) -->
								<div v-if="!isUnifiedDashboard" class="cs-compact-row">
									<div class="cs-compact-field cs-compact-field--xs">
										<label>
											<font-awesome-icon :icon="['fas', 'list-ol']" class="me-1 text-secondary" />
											Per page
										</label>
										<input type="number" min="1" max="50" class="form-control" v-model="clientOptions.Department_per_page" />
									</div>
									<div class="cs-compact-field cs-compact-field--md">
										<label>
											<font-awesome-icon
												:icon="['fas', clientOptions.Department_archive_order_column === 'title' ? 'font' : 'calendar-alt']"
												class="me-1 text-secondary"
											/>
											Sort By
										</label>
										<select class="form-select" v-model="clientOptions.Department_archive_order_column">
											<option value="created_at">Date Created</option>
											<option value="title">Department Name</option>
										</select>
									</div>
									<div class="cs-compact-field cs-compact-field--md">
										<label>
											<font-awesome-icon
												:icon="['fas', clientOptions.Department_archive_order === 'DESC' ? 'sort-amount-down' : 'sort-amount-up']"
												class="me-1 text-secondary"
											/>
											Sort Order
										</label>
										<select class="form-select" v-model="clientOptions.Department_archive_order">
											<option value="ASC">Ascending (oldest first)</option>
											<option value="DESC">Descending (newest first)</option>
										</select>
									</div>
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 6a: Events -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'events'" class="col-md-12 mt-3 js-tab-content">
					<div v-if="isUnifiedDashboard" class="cs-tip mb-5">
						<div class="cs-tip__icon">
							<font-awesome-icon :icon="['fas', 'calendar-alt']" />
						</div>
						<div class="cs-tip__body">
							<strong>Events live on the Unified Dashboard.</strong>
							<p>Pick a page below to serve as the public events archive — it renders through the <strong>Calendar</strong> element in the <strong>Page Builder</strong>, so every embed option (view, department filter, subscribe, recordings) stays editable on the page itself. The theme's old fixed-iframe archive is then no longer generated, and <em>Events Prefix</em> follows that page's URL automatically.</p>
						</div>
					</div>

					<!-- Events archive page (Unified Dashboard only) -->
					<div v-if="isUnifiedDashboard" class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Archive page <span class="gh-intro__badge">Events</span></h2>
							<p class="gh-intro__desc">The page that serves as the public events archive. Everything linking to events — homepage calendar, widgets, search, sitemap — points at it.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div v-if="!eventsArchive.eligible.ok" class="cs-tip cs-tip--warning mb-3">
									<div class="cs-tip__icon">
										<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
									</div>
									<div class="cs-tip__body">
										<p class="mb-0">{{ eventsArchive.eligible.reason }}</p>
									</div>
								</div>

								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'calendar-alt']" class="me-1 text-secondary" />
										Events archive page
									</label>
									<select
										class="form-select"
										v-model="clientOptions.Event_archive_page_id"
										:disabled="eventsArchive.loading || !eventsArchive.eligible.ok"
										@change="onEventsArchivePageChange()"
									>
										<option value="">Theme-generated archive (no page)</option>
										<option v-for="page in selectableArchivePages" :key="page.id" :value="page.id">
											{{ page.title }} — /{{ page.slug }}{{ page.missingCalendar ? ' (no Calendar element!)' : '' }}
										</option>
									</select>
									<div class="gh-help" v-if="selectableArchivePages.length">
										Only published top-level pages holding a <strong>Calendar</strong> element are listed — a page without one
										would serve the events URL with no calendar on it, and a page attached to a department is served under
										<code>{{ clientOptions.Department_prefix }}/…</code> so it cannot own that URL.
									</div>
									<div class="gh-help" v-else>
										No page holds a <strong>Calendar</strong> element yet. Use <em>Create events page</em> below, or drop the
										Calendar element onto an existing page in the Page Builder and it will show up here.
									</div>
								</div>

								<!-- What is actually serving the archive URL right now -->
								<div v-if="eventsArchive.page" class="gh-help mt-2">
									Serving <code>{{ eventsArchive.page.url }}</code> — the theme archive is
									<strong>{{ eventsArchive.themePageGenerated ? 'still generated' : 'no longer generated' }}</strong>.
									Rebuild the site to publish the change.
								</div>
								<div v-else-if="clientOptions.Event_archive_page_id" class="cs-tip cs-tip--warning mt-3">
									<div class="cs-tip__icon">
										<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
									</div>
									<div class="cs-tip__body">
										<p class="mb-0">The selected page is not published (draft, archived or trashed), so the theme's archive is generated instead. Publish the page to hand the URL back to it.</p>
									</div>
								</div>

								<!-- Temporary convenience: creating the page moves into the client launch flow later, leaving only the picker above. Gone once a page is picked — there is nothing left to create then. -->
								<template v-if="!eventsArchive.nominatedPageId">
									<hr class="gh-divider" />
									<div class="d-flex align-items-center gap-3 flex-wrap">
										<button
											class="btn btn-primary"
											type="button"
											:disabled="eventsArchive.creating || !eventsArchive.eligible.ok"
											@click="createEventsArchivePage()"
										>
											<font-awesome-icon :icon="['fas', 'plus']" class="me-1" />
											{{ eventsArchive.creating ? 'Creating…' : 'Create events page' }}
										</button>
										<span class="gh-help mb-0">Creates a published page holding a Calendar element and points the options at it.</span>
									</div>
								</template>
							</div>
						</section>
					</div>
					<hr v-if="isUnifiedDashboard && !eventsArchive.live" class="gh-divider" />

					<!-- Legacy: settings that only drive the theme-generated archive. Hidden entirely once a page owns the events archive (nothing reads them then); framed as going away for the Unified Dashboard clients that still fall back to it, and rendered plainly for town-web clients, which depend on it. -->
					<div v-if="!isUnifiedDashboard || !eventsArchive.live" :class="isUnifiedDashboard ? 'cs-legacy' : ''">
						<div v-if="isUnifiedDashboard" class="cs-legacy__head">
							<span class="cs-legacy__icon">
								<font-awesome-icon :icon="['fas', 'history']" />
							</span>
							<span class="cs-legacy__text">
								<strong>Legacy settings <span class="cs-legacy__badge">being removed</span></strong>
								<small>
									Still in use because no archive page is serving events yet. Pick one above and these fields stop
									mattering — they will be dropped from this page.
								</small>
							</span>
						</div>

						<div :class="isUnifiedDashboard ? 'cs-legacy__body' : ''">
							<!-- Events Basic -->
							<div class="gh-layout">
								<aside class="gh-intro">
									<h2 class="gh-intro__title">Basic <span class="gh-intro__badge">Events</span></h2>
									<p class="gh-intro__desc">URL prefix for the public Events archive page.</p>
								</aside>
								<section class="gh-accounts">
									<div class="ada-card">
										<div class="gh-field">
											<label>
												<font-awesome-icon :icon="['fas', 'link']" class="me-1 text-secondary" />
												Events Prefix
											</label>
											<input type="text" class="form-control" v-model="clientOptions.Event_prefix" />
											<div class="gh-help">All events page is: <code>{{ client.url }}/{{ clientOptions.Event_prefix }}</code>. This change is visible after rebuilding the site.</div>
										</div>
									</div>
								</section>
							</div>

							<hr class="gh-divider" />

							<!-- Events Templates — only read by the theme-generated archive -->
							<div class="gh-layout">
								<aside class="gh-intro">
									<h2 class="gh-intro__title">Templates <span class="gh-intro__badge">Events</span></h2>
									<p class="gh-intro__desc">Archive listing template used to render the Events page. Events data is sourced from HeyGov.</p>
								</aside>
								<section class="gh-accounts">
									<div class="ada-card">
										<div class="gh-field">
											<label>
												<font-awesome-icon :icon="['fas', 'th-list']" class="me-1 text-secondary" />
												Archive template
											</label>
											<div class="input-group">
												<select class="form-select" v-model="clientOptions.Event_archive_template">
													<option value="default">default</option>
													<option v-for="(template, i) in eventsArchiveTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
												</select>
												<button class="btn btn-primary" type="button" @click="loadTemplateVariables('events', '')" title="Configure template variables">Conf</button>
											</div>
										</div>
									</div>
								</section>
							</div>
						</div>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 6b: Meetings -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'meetings'" class="col-md-12 mt-3 js-tab-content">
					<!-- Unified Dashboard: full-width note explaining that meetings render via the Page Builder element; the predefined page below is opt-in. -->
					<div v-if="isUnifiedDashboard" class="cs-tip mb-5">
						<div class="cs-tip__icon">
							<font-awesome-icon :icon="['fas', 'th-large']" />
						</div>
						<div class="cs-tip__body">
							<strong>Meetings now live on the Unified Dashboard.</strong>
							<p>This settings page is no longer used by the Unified Dashboard. To control how meetings appear on the public site, create or edit a page on the new dashboard and drop in the <strong>Meetings</strong> element from the <strong>Page Builder</strong> — it pulls all meeting data straight from HeyGov. The classic predefined meetings page is opt-in below and is only generated in the static build when turned on.</p>
						</div>
					</div>

					<div v-if="isUnifiedDashboard" class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Predefined page <span class="gh-intro__badge">Meetings</span></h2>
							<p class="gh-intro__desc"><strong>Use this only if absolutely necessary.</strong> The classic predefined meetings page is kept for backwards compatibility — new sites should render meetings through the Page Builder element instead.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<label class="form-check form-switch">
									<input class="form-check-input" type="checkbox" v-model="clientOptions.Meeting_use_predefined_page" @change="saveOptions()" />
									<span class="form-check-label">Use predefined meetings page</span>
								</label>
								<div class="gh-help">When off, the meetings page is not generated in the static build (and is left out of the sitemap). Rebuild the site after changing this.</div>
							</div>
						</section>
					</div>
					<hr v-if="isUnifiedDashboard && clientOptions.Meeting_use_predefined_page" class="gh-divider" />

					<template v-if="!isUnifiedDashboard || clientOptions.Meeting_use_predefined_page">
					<!-- Meetings Basic -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Basic <span class="gh-intro__badge">Meetings</span></h2>
							<p class="gh-intro__desc">URL prefix for the public Meeting Repository page.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'link']" class="me-1 text-secondary" />
										Meetings Prefix
									</label>
									<input type="text" class="form-control" v-model="clientOptions.Meeting_prefix" />
									<div class="gh-help">All meeting page is: <code>{{ client.url }}/{{ clientOptions.Meeting_prefix }}</code>. This change is visible after rebuilding the site.</div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Meetings Templates -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Templates <span class="gh-intro__badge">Meetings</span></h2>
							<p class="gh-intro__desc">Template used to render the Meeting Repository page. Meeting data is sourced from HeyGov.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'th-list']" class="me-1 text-secondary" />
										Repository template
									</label>
									<select class="form-select" v-model="clientOptions.Meeting_template">
										<option value="default">default</option>
										<option v-for="(template, i) in meetingArchiveTemplatesAvailable" :key="i" :value="template">{{ template }}</option>
									</select>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Meetings table columns -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Table columns <span class="gh-intro__badge">Meetings</span></h2>
							<p class="gh-intro__desc">Pick which columns appear in the public meetings table.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="cs-toggle-grid">
									<label v-for="column in meetingTableColumns" :key="column.id" class="cs-toggle-cell form-check form-switch">
										<input class="form-check-input" type="checkbox" v-model="meetingTableColumnsActive" :value="column.id" />
										<span class="form-check-label">{{ column.label }}</span>
									</label>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Meetings Categories -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Categories <span class="gh-intro__badge">Meetings</span></h2>
							<p class="gh-intro__desc">Manage categories used to group meetings. Drag to reorder. Toggle a category on to make it subscribable. When Unified Dashboard is active, categories are managed in HeyGov.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div v-if="isUnifiedDashboard" class="cs-unified-banner">
									<img
										src="https://uploads-ssl.webflow.com/638a65ecc429342f8d6fd704/638a81dede3590b0a04459fc_HeyGov%20Jellybean%20logo.svg"
										width="22px"
										class="me-2"
										alt=""
									/>
									<div>
										<strong>Unified Dashboard active</strong>
										<small class="d-block">Meeting categories are managed through HeyGov.</small>
									</div>
								</div>
								<template v-else>
									<div class="d-flex justify-content-end mb-3">
										<button @click="addMeetingCategoryFlag = true" class="cs-add-btn">
											<font-awesome-icon :icon="['fas', 'plus']" />
											<span>Add category</span>
										</button>
									</div>
									<draggable v-model="meetingRepositoryCategories" handle=".cs-cat-row__handle" @end="updateMeetingCategoriesOrder()" class="cs-cat-list">
										<div
											v-for="category in meetingRepositoryCategories"
											:key="category.id"
											class="cs-cat-row"
										>
											<span class="cs-cat-row__handle" title="Drag to reorder">
												<font-awesome-icon :icon="['fas', 'arrows-alt']" />
											</span>
											<label class="cs-cat-row__toggle form-check form-switch">
												<input class="form-check-input" type="checkbox" v-model="meetingRepositoryCategoriesSubscribable" :value="category.id" />
												<span class="cs-cat-row__name">{{ category.name }}</span>
											</label>
											<div class="cs-cat-row__actions">
												<button
													@click="showUpdateCategoryOffcanvas(category, 'meet')"
													class="cs-cat-row__action"
													title="Edit category"
												>
													<font-awesome-icon :icon="['fas', 'edit']" />
												</button>
												<button
													@click="deleteCategory(category, 'meet')"
													class="cs-cat-row__action cs-cat-row__action--danger"
													title="Delete category"
												>
													<font-awesome-icon :icon="['fas', 'trash']" />
												</button>
											</div>
										</div>
									</draggable>
									<div v-if="meetingRepositoryCategories.length === 0" class="cs-empty-state">
										No categories yet. Click <strong>Add category</strong> to create one or sync from HeyGov below.
									</div>
									<div v-if="addMeetingCategoryFlag" class="cs-add-form">
										<h6 class="mb-3">Add meeting category</h6>
										<div class="gh-field">
											<label>Name</label>
											<input class="form-control" type="text" v-model="newMeetingCategory.name" placeholder="e.g., Town Council" />
										</div>
										<div class="d-flex justify-content-end gap-2 mt-3">
											<button @click="cancelAddNewMeetingCategory()" class="btn btn-outline-secondary btn-sm">Cancel</button>
											<button @click="storeNewMeetingCategory()" class="btn btn-primary btn-sm">
												<font-awesome-icon
													:icon="['fas', storeMeetingCategoryLoading ? 'spinner' : 'save']"
													:class="[storeMeetingCategoryLoading ? 'fa-spin me-1' : 'me-1']"
												/>Save
											</button>
										</div>
									</div>
								</template>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Meetings Sync -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Sync from HeyGov <span class="gh-intro__badge">Meetings</span></h2>
							<p class="gh-intro__desc">Pull the latest meeting categories from HeyGov. Useful when categories were created or edited outside of MC2.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="d-flex justify-content-end">
									<button
										class="cs-sync-btn"
										@click="syncHeygovMeetingRepositoryCategories()"
										:disabled="isUnifiedDashboard || meetingCategoriesLoading"
									>
										<font-awesome-icon
											:icon="['fas', meetingCategoriesLoading ? 'spinner' : 'sync']"
											:class="meetingCategoriesLoading ? 'fa-spin' : ''"
										/>
										<span>{{ meetingCategoriesLoading ? 'Syncing…' : 'Sync now' }}</span>
									</button>
								</div>
								<div v-if="isUnifiedDashboard" class="gh-help mt-2 text-end">
									Unified Dashboard is active &mdash; sync is managed by HeyGov.
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
					</template>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 7a: Subscribers (renamed from "Audience") -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'subscribers'" class="col-md-12 mt-3 js-tab-content">
					<!-- Subscribers basic -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Basic <span class="gh-intro__badge">Subscribers</span></h2>
							<p class="gh-intro__desc">URL prefix for the subscribe page plus the mail/SMS templates used for the notifications sent out to subscribers. If a template is left blank, the system falls back to a default. Click the <font-awesome-icon :icon="['fas', 'info-circle']" /> next to a field to see its default and the variables it supports.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<!-- Prefix + Mail From — stacked -->
								<div class="gh-field">
									<label>
										<font-awesome-icon :icon="['fas', 'link']" class="me-1 text-secondary" />
										Subscribe Page Prefix
									</label>
									<input type="text" class="form-control" v-model="clientOptions.Subscriber_prefix" />
									<div class="gh-help">Subscribe page is: <code>{{ client.url }}/{{ clientOptions.Subscriber_prefix }}</code></div>
								</div>
								<div class="gh-field">
									<label>Mail From</label>
									<input type="text" class="form-control" v-model="clientOptions.mail_from" />
								</div>

								<!-- Templates: pill-tab switcher, full width below Mail From -->
								<div class="cs-sub-templates">
									<div class="cs-sub-tabs" role="tablist" aria-label="Subscriber template type">
										<button
											type="button"
											role="tab"
											class="cs-sub-tabs__tab"
											:class="{ 'cs-sub-tabs__tab--active': subTemplateTab === 'subscription' }"
											@click="subTemplateTab = 'subscription'"
										>
											<font-awesome-icon :icon="['fas', 'envelope']" class="me-1" />
											Subscription
										</button>
										<button
											type="button"
											role="tab"
											class="cs-sub-tabs__tab"
											:class="{ 'cs-sub-tabs__tab--active': subTemplateTab === 'alert' }"
											@click="subTemplateTab = 'alert'"
										>
											<font-awesome-icon :icon="['fas', 'bell']" class="me-1" />
											Alert
										</button>
									</div>

									<!-- Subscription tab: regular (non-emergency) notice notifications -->
									<div v-show="subTemplateTab === 'subscription'">
										<p class="cs-sub-tabs__hint">Templates used for regular notifications — sent to subscribers when a notice, meeting or agenda is published. Leave blank to fall back to the system default.</p>
										<div class="gh-field">
											<label>
												Subscription subject (email)
												<button type="button" class="cs-tpl-info" data-bs-toggle="tooltip" data-bs-placement="top"
													title="Show the default template and the variables you can use"
													@click="openTemplateHelp('subscription_mail_subject_template')">
													<font-awesome-icon :icon="['fas', 'info-circle']" />
												</button>
											</label>
											<input type="text" class="form-control" v-model="clientOptions.subscription_mail_subject_template" :placeholder="templateHelp.subscription_mail_subject_template.default" />
										</div>
										<div class="gh-field">
											<label>
												Subscription email body
												<button type="button" class="cs-tpl-info" data-bs-toggle="tooltip" data-bs-placement="top"
													title="Show the default template and the variables you can use"
													@click="openTemplateHelp('subscription_mail_template')">
													<font-awesome-icon :icon="['fas', 'info-circle']" />
												</button>
											</label>
											<textarea rows="5" class="form-control" v-model="clientOptions.subscription_mail_template" placeholder="Leave blank to use the default template — click the info icon to see it." />
										</div>
										<div class="gh-field">
											<label>
												Subscription SMS body
												<button type="button" class="cs-tpl-info" data-bs-toggle="tooltip" data-bs-placement="top"
													title="Show the default template and the variables you can use"
													@click="openTemplateHelp('subscription_sms_template')">
													<font-awesome-icon :icon="['fas', 'info-circle']" />
												</button>
											</label>
											<textarea rows="4" class="form-control" v-model="clientOptions.subscription_sms_template" placeholder="Leave blank to use the default template — click the info icon to see it." />
										</div>
									</div>

									<!-- Alert tab: emergency-notification mails/SMS sent to subscribers -->
									<div v-show="subTemplateTab === 'alert'">
										<p class="cs-sub-tabs__hint">Templates used for emergency alerts — sent only for records flagged as an emergency notification. Leave blank to fall back to the system default.</p>
										<div class="gh-field">
											<label>
												Alert subject (email)
												<button type="button" class="cs-tpl-info" data-bs-toggle="tooltip" data-bs-placement="top"
													title="Show the default template and the variables you can use"
													@click="openTemplateHelp('alert_mail_subject_template')">
													<font-awesome-icon :icon="['fas', 'info-circle']" />
												</button>
											</label>
											<input type="text" class="form-control" v-model="clientOptions.alert_mail_subject_template" :placeholder="templateHelp.alert_mail_subject_template.default" />
										</div>
										<div class="gh-field">
											<label>
												Alert email body
												<button type="button" class="cs-tpl-info" data-bs-toggle="tooltip" data-bs-placement="top"
													title="Show the default template and the variables you can use"
													@click="openTemplateHelp('alert_mail_template')">
													<font-awesome-icon :icon="['fas', 'info-circle']" />
												</button>
											</label>
											<textarea rows="5" class="form-control" v-model="clientOptions.alert_mail_template" :placeholder="templateHelp.alert_mail_template.default" />
										</div>
										<div class="gh-field">
											<label>
												Alert SMS body
												<button type="button" class="cs-tpl-info" data-bs-toggle="tooltip" data-bs-placement="top"
													title="Show the default template and the variables you can use"
													@click="openTemplateHelp('alert_sms_template')">
													<font-awesome-icon :icon="['fas', 'info-circle']" />
												</button>
											</label>
											<textarea rows="4" class="form-control" v-model="clientOptions.alert_sms_template" :placeholder="templateHelp.alert_sms_template.default" />
										</div>
									</div>
								</div>

								<!-- Template help modal — shows the default template and the -->
								<!-- supported variables. Opened from the info icon in each label. -->
								<div v-if="templateHelpKey" class="cs-modal-backdrop" @click.self="closeTemplateHelp">
									<div class="cs-modal cs-modal--wide">
										<div class="cs-tplmodal__head">
											<h5 class="cs-tplmodal__title">{{ activeTemplateHelp.title }}</h5>
											<button type="button" class="cs-tplmodal__close" @click="closeTemplateHelp" aria-label="Close">
												<font-awesome-icon :icon="['fas', 'times']" />
											</button>
										</div>

										<p class="cs-tplmodal__desc">{{ activeTemplateHelp.desc }}</p>

										<div class="cs-tplmodal__section">
											<div class="cs-tplmodal__label">
												Default template
												<span class="cs-tplmodal__hint">used when this field is left blank</span>
											</div>
											<pre class="cs-tplmodal__code">{{ activeTemplateHelp.default }}</pre>
											<button type="button" class="gh-save-btn gh-save-btn--sm" @click="applyTemplateDefault">
												<i class="fas fa-file-import"></i>
												Copy default into the field
											</button>
										</div>

										<div class="cs-tplmodal__section">
											<div class="cs-tplmodal__label">Available variables</div>
											<table class="cs-tplmodal__vars">
												<tbody>
													<tr v-for="v in activeTemplateHelp.vars" :key="v.code">
														<td><code>{{ v.code }}</code></td>
														<td>{{ v.desc }}</td>
													</tr>
												</tbody>
											</table>
											<p v-if="activeTemplateHelp.note" class="cs-tplmodal__note">
												<i class="fas fa-exclamation-triangle"></i>
												{{ activeTemplateHelp.note }}
											</p>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Subscribers list -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">
								Subscribers list
								<span class="cs-count-badge">{{ subscribersTotal }}</span>
							</h2>
							<p class="gh-intro__desc">Read-only view of currently subscribed contacts. {{ isUnifiedDashboard ? 'Sourced from HeyGov.' : 'Sourced from Juniper.' }}</p>
						</aside>
						<section class="gh-accounts">
							<!-- Flush card + table + footer bar — the same shell as the -->
							<!-- Deploy page's "Recent Deployments" list (.cd-card/.cd-table). -->
							<div class="ada-card ada-card--flush">
								<div v-if="loadingSubscribers && subscribers.length === 0" class="cs-table-empty">Loading…</div>
								<div v-else-if="subscribers.length === 0" class="cs-table-empty">No subscribers yet.</div>
								<template v-else>
									<div class="table-responsive">
										<table class="table align-middle mb-0 cs-subs-table">
											<thead>
												<tr>
													<th>Name</th>
													<th>Email</th>
													<th>Phone</th>
													<th class="cs-subs-table__status-col">Status</th>
												</tr>
											</thead>
											<tbody>
												<tr v-for="subscriber in subscribers" :key="subscriber.id">
													<td class="cs-subs-table__primary">{{ subscriber.name || '—' }}</td>
													<td>{{ subscriber.email || '—' }}</td>
													<td>{{ subscriber.phone || '—' }}</td>
													<td>
														<span class="cs-status-pill" :class="statusPillClass(subscriber.status)">
															{{ subscriber.status || 'unknown' }}
														</span>
													</td>
												</tr>
											</tbody>
										</table>
									</div>
									<div v-if="hasMoreSubscribers || loadingMoreSubscribers" class="cs-table-footer">
										<button
											type="button"
											class="cs-load-more-btn"
											:disabled="loadingMoreSubscribers || !hasMoreSubscribers"
											@click="loadMoreSubscribers()"
										>
											<i v-if="loadingMoreSubscribers" class="fas fa-spinner fa-spin me-1"></i>
											{{ loadingMoreSubscribers ? 'Loading…' : 'Load more' }}
										</button>
										<span class="text-muted small">{{ subscribers.length }} of {{ subscribersTotal }}</span>
									</div>
									<div v-else-if="subscribersTotal > SUBSCRIBERS_PAGE_SIZE" class="cs-table-footer">
										<span class="text-muted small">{{ subscribers.length }} of {{ subscribersTotal }}</span>
									</div>
								</template>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!--
						"Subscribers source" section was removed — it's now implicitly
						managed by the dashboard choice in General → Dashboard (Unified →
						Subscriber_source_heygov=true, Town Web → false). See the watcher
						on `clientOptions.main_dashboard` in <script>.
					-->

					<!-- WordPress import -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Import from WordPress</h2>
							<p class="gh-intro__desc">Import subscribers from a WordPress site running the <code>tw-subscribers</code> plugin. Duplicates are skipped automatically.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="cs-tip">
									<div class="cs-tip__icon">
										<font-awesome-icon :icon="['fas', 'info-circle']" />
									</div>
									<div class="cs-tip__body">
										<strong>How importing works.</strong>
										Enter the WordPress site URL below (e.g., <code>https://heyvile.org</code>). The importer skips existing subscribers and ignores duplicates. It also tries to match subscribable categories — if none match, the subscriber is added to <em>all</em> subscribable categories.
									</div>
								</div>
								<div class="gh-field">
									<label>WordPress Website</label>
									<input class="form-control" v-model="wordpressImport.website" type="text" placeholder="https://heyvile.org" />
								</div>
								<button @click="importWordpressSubscribers()" class="btn btn-primary">
									<font-awesome-icon v-if="importSubscribersLoading" :icon="['fas', 'spinner']" :class="['fa-spin']" />
									Import
								</button>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 7b: Contact (split out of the former "Audience" tab) -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'contact'" class="col-md-12 mt-3 js-tab-content">
					<div v-if="isUnifiedDashboard" class="cs-tip cs-tip--warning">
						<div class="cs-tip__icon">
							<font-awesome-icon :icon="['fas', 'exclamation-triangle']" />
						</div>
						<div class="cs-tip__body">
							<strong>This settings page will be deprecated.</strong>
							<p>On the Unified Dashboard, the public contact form is replaced by <em>HeyGov Forms</em>. Create your form in HeyGov and drop it onto a page via the <strong>Page Builder</strong>. You can keep editing the legacy fields here for now, but new clients should use HeyGov Forms instead.</p>
						</div>
					</div>

					<!-- Contact form -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Contact form</h2>
							<p class="gh-intro__desc">Recipient address and copy used when visitors submit the public contact form. You must paste the form HTML below to use the Page Builder Contact Form element.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="cs-tip">
									<div class="cs-tip__icon">
										<font-awesome-icon :icon="['fas', 'info-circle']" />
									</div>
									<div class="cs-tip__body">
										<strong>Before you can use the Page Builder Contact Form element</strong>, paste the full Contact Form HTML markup into the field below. The element is rendered from this source.
									</div>
								</div>
								<div class="gh-field">
									<label>Contact Email</label>
									<input type="text" class="form-control" v-model="clientOptions.contact_email" />
									<div class="gh-help">Submissions from the contact form will be sent to this address.</div>
								</div>
								<div class="gh-field">
									<label>Contact Subject</label>
									<input type="text" class="form-control" v-model="clientOptions.contact_subject" />
									<div class="gh-help">Subject line of the email sent from the contact form.</div>
								</div>
								<div class="gh-field">
									<label>Contact Success Message</label>
									<input type="text" class="form-control" v-model="clientOptions.contact_success_message" />
									<div class="gh-help">Message shown to visitors after they submit the form.</div>
								</div>
								<div class="gh-field">
									<label>Contact Form HTML</label>
									<textarea rows="10" class="form-control" v-model="clientOptions.contact_form_html" />
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB 8: Modules -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'modules'" class="col-md-12 mt-3 js-tab-content">
					<!-- Business Directory Settings -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Business Directory</h2>
							<p class="gh-intro__desc">Toggle the Business Directory module on for this client, then configure its URL and pagination.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="form-check form-switch d-flex justify-content-start align-items-center mb-2">
									<input
										class="form-check-input me-2"
										v-model="clientOptions.business_directory_enabled"
										type="checkbox"
										id="businessDirectoryEnabled"
										:disabled="themeCapabilities && themeCapabilities.business_directory === false"
									/>
									<label class="form-check-label" for="businessDirectoryEnabled">Enable Business Directory in Dashboard</label>
									<span
										v-if="themeCapabilities"
										class="badge ms-2"
										:class="themeCapabilities.business_directory ? 'bg-success-subtle text-success' : 'bg-danger-subtle text-danger'"
										style="font-weight: 500; font-size: 0.7rem"
									>
										<font-awesome-icon :icon="themeCapabilities.business_directory ? 'check-circle' : 'times-circle'" class="me-1" />
										{{ themeCapabilities.business_directory ? 'theme supports' : 'theme does not support' }}
									</span>
								</div>
								<small class="text-muted">When disabled, Business Directory is hidden in `dashboard-app` for this client.</small>
								<small v-if="themeCapabilities && !themeCapabilities.business_directory" class="d-block text-danger mt-1">
									The active theme (<code>{{ themeCapabilities.theme }}</code>) has no business-directory templates — the toggle is disabled and static generation skips this section.
								</small>
								<hr />
								<h6 class="gh-section__title">
									URL &amp; Pagination
									<small v-if="!clientOptions.business_directory_enabled" class="text-muted ms-2 fw-normal" style="font-size: 0.8rem">
										(enable Business Directory to edit)
									</small>
								</h6>
								<div class="gh-field">
									<label for="businessDirectoryPrefix">Page Prefix</label>
									<input id="businessDirectoryPrefix" type="text" class="form-control" v-model="clientOptions.BusinessDirectory_prefix" placeholder="business" :disabled="!clientOptions.business_directory_enabled" />
									<small class="d-block text-muted">Archive: <code>{{ client.url }}/{{ clientOptions.BusinessDirectory_prefix || 'business' }}/</code></small>
									<small class="d-block text-muted">Single: <code>{{ client.url }}/{{ clientOptions.BusinessDirectory_prefix || 'business' }}/{slug}/</code></small>
								</div>
								<div class="gh-grid gh-grid--2">
									<div class="gh-field">
										<label for="businessDirectoryPerPage">Items per page</label>
										<input id="businessDirectoryPerPage" type="number" min="1" class="form-control" v-model.number="clientOptions.BusinessDirectory_per_page" placeholder="12" :disabled="!clientOptions.business_directory_enabled" />
									</div>
									<div class="gh-field">
										<label for="businessDirectoryPaginationType">Pagination type</label>
										<select id="businessDirectoryPaginationType" class="form-select" v-model="clientOptions.BusinessDirectory_pagination_type" :disabled="!clientOptions.business_directory_enabled">
											<option value="pages">Pages</option>
											<option value="infinite">Load more</option>
										</select>
									</div>
								</div>
							</div>
						</section>
					</div>
					<hr class="gh-divider" />

					<!-- Business Directory Categories -->
					<div v-if="clientOptions.business_directory_enabled" class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Business Directory categories</h2>
							<p class="gh-intro__desc">Manage the categories used to group business listings.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<BusinessDirectoryCategoriesManager :client-id="$route.params.id" />
							</div>
						</section>
					</div>
					<hr v-if="clientOptions.business_directory_enabled" class="gh-divider" />

					<!-- Business Directory Import -->
					<div v-if="clientOptions.business_directory_enabled" class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Import CSV</h2>
							<p class="gh-intro__desc">Bulk-import business listings from a CSV file.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<BusinessDirectoryImporter :client-id="$route.params.id" />
							</div>
						</section>
					</div>
					<hr v-if="clientOptions.business_directory_enabled" class="gh-divider" />

					<!-- Business Directory Danger Zone -->
					<div v-if="clientOptions.business_directory_enabled" class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Danger zone</h2>
							<p class="gh-intro__desc">Destructive Business Directory actions. Use with care.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<BusinessDirectoryDangerZone :client-id="$route.params.id" />
							</div>
						</section>
					</div>

					<hr class="gh-divider" />

					<!-- Page revisions -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Page revisions</h2>
							<p class="gh-intro__desc">Keep a history of what each page looked like when it went live, let staff save changes without publishing them, and optionally require an editor's approval before a change goes live.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="form-check form-switch d-flex justify-content-start align-items-center mb-2">
									<input
										class="form-check-input me-2"
										v-model="clientOptions.page_revisions_enabled"
										type="checkbox"
										id="pageRevisionsEnabled"
									/>
									<label class="form-check-label" for="pageRevisionsEnabled">Enable Page Revisions</label>
								</div>
								<small class="text-muted">Records every published version of a page, and lets staff save unpublished changes that the website keeps serving the old version for until they publish.</small>
								<hr />
								<h6 class="gh-section__title">
									Edit approvals
									<small v-if="!clientOptions.page_revisions_enabled" class="text-muted ms-2 fw-normal" style="font-size: 0.8rem">
										(enable Page Revisions to edit)
									</small>
								</h6>
								<div class="form-check form-switch d-flex justify-content-start align-items-center mb-2">
									<input
										class="form-check-input me-2"
										v-model="clientOptions.page_edit_approvals_enabled"
										type="checkbox"
										id="pageEditApprovalsEnabled"
										:disabled="!clientOptions.page_revisions_enabled"
									/>
									<label class="form-check-label" for="pageEditApprovalsEnabled">Require approval for page edits</label>
								</div>
								<small class="text-muted">Staff who cannot publish send their changes for review instead of putting them live. Editors and admins keep publishing directly, and they are the ones who approve or reject.</small>
								<small class="d-block text-muted mt-2">
									Only applies to the unified dashboard — page edits made in <code>dashboard-app</code> still publish directly.
								</small>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />

					<!-- Search -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Advanced search</h2>
							<p class="gh-intro__desc">Enable the advanced search experience on the public site.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="form-check form-switch d-flex justify-content-start align-items-center">
									<input class="form-check-input me-2" v-model="clientOptions.advanced_search_active" type="checkbox" />
									<label class="form-check-label">Activate Advanced Search</label>
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />

					<!-- Facebook Syndication -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Facebook syndication</h2>
							<p class="gh-intro__desc">Automatically syndicate news posts to a Facebook Page. Requires a Page ID and a page-access token (obtained via "Get Token" once Page ID is set).</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="form-check form-switch d-flex justify-content-start align-items-center mb-3">
									<input class="form-check-input me-2" v-model="clientOptions.social_networks_fb_active" type="checkbox" id="fbSyndicationToggle" />
									<label class="form-check-label" for="fbSyndicationToggle">
										Activate Facebook Syndication
									</label>
								</div>
								<div v-if="clientOptions.social_networks_fb_active">
									<div class="gh-field">
										<label>Facebook Page ID</label>
										<input type="text" class="form-control" v-model="clientOptions.social_networks_fb_page_id" />
									</div>
									<div class="gh-field">
										<label>Facebook Page Access Token</label>
										<div class="input-group">
											<input
												id="pageToken"
												type="text"
												class="form-control"
												v-model="clientOptions.social_networks_fb_page_access_token"
												aria-describedby="fbPageToken"
											/>
											<button class="btn btn-outline-secondary" @click="getFbPageToken()" type="button" id="fbPageToken">
												<font-awesome-icon v-if="fetchingFbPageToken" :icon="['fas', 'spinner']" :class="['fa-spin']" />
												Get Token
											</button>
										</div>
									</div>
									<div class="form-check form-switch d-flex justify-content-start align-items-center mt-3">
										<input class="form-check-input me-2" v-model="clientOptions.NewsNotice_social_networks_fb_active" type="checkbox" id="fbNewsSyndicationToggle" />
										<label class="form-check-label" for="fbNewsSyndicationToggle">News Notices FB Syndication</label>
									</div>
								</div>
							</div>
						</section>
					</div>


					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB: Security (Page Protection + Cloudflare Turnstile) -->
				<!-- ============================================================ -->
				<div v-show="activeTab === 'security'" class="col-md-12 mt-3 js-tab-content">
					<!-- Page Protection -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Page protection</h2>
							<p class="gh-intro__desc">When enabled, individual pages can have a password set on them in the dashboard, restricting public access.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="cs-tip">
									<div class="cs-tip__icon">
										<font-awesome-icon :icon="['fas', 'info-circle']" />
									</div>
									<div class="cs-tip__body">
										<strong>What this does.</strong>
										When enabled, individual pages get a <em>Password</em> field in the page editor. Visitors must enter the correct password to view the page; otherwise they see a password prompt instead of the content.
									</div>
								</div>
								<div class="form-check form-switch d-flex justify-content-start align-items-center">
									<input class="form-check-input me-2" v-model="clientOptions.Page_protection_enabled" type="checkbox" id="pageProtectionToggle" />
									<label class="form-check-label" for="pageProtectionToggle">
										<strong>Enable Page Protection</strong>
									</label>
								</div>
								<small class="text-muted">Allow pages to be password protected</small>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />

					<!-- Cloudflare Turnstile -->
					<div class="gh-layout">
						<aside class="gh-intro">
							<h2 class="gh-intro__title">Cloudflare Turnstile</h2>
							<p class="gh-intro__desc">Bot-protection challenge attached to public forms. Both the site key (public) and secret key (server-side) come from your Cloudflare dashboard.</p>
						</aside>
						<section class="gh-accounts">
							<div class="ada-card">
								<div class="d-flex align-items-center mb-3">
									<img src="https://www.cloudflare.com/img/logo-web-badges/cf-logo-on-white-bg.svg" width="120px" class="me-3" alt="Cloudflare" />
									<h5 class="m-0">Cloudflare Turnstile</h5>
								</div>
								<div class="form-check form-switch d-flex justify-content-start align-items-center mb-3">
									<input class="form-check-input me-2" v-model="clientOptions.security_turnstile_enabled" type="checkbox" id="turnstileToggle" />
									<label class="form-check-label" for="turnstileToggle">
										<strong>Enable Turnstile Protection</strong>
									</label>
								</div>
								<div v-if="clientOptions.security_turnstile_enabled">
									<div class="gh-field">
										<label for="turnstileSiteKey">Turnstile Site Key</label>
										<input
											type="text"
											class="form-control"
											id="turnstileSiteKey"
											v-model="clientOptions.security_turnstile_site_key"
											placeholder="Enter your Turnstile Site Key"
										/>
										<small class="text-muted">This key is visible to users in your HTML.</small>
									</div>
									<div class="gh-field">
										<label for="turnstileSecretKey">Turnstile Secret Key</label>
										<input
											type="password"
											class="form-control"
											id="turnstileSecretKey"
											v-model="clientOptions.security_turnstile_secret_key"
											placeholder="Enter your Turnstile Secret Key"
										/>
										<small class="text-muted">This key is kept secure on the server.</small>
									</div>
									<div class="cs-tip mt-3">
										<div class="cs-tip__icon">
											<font-awesome-icon :icon="['fas', 'lock']" />
										</div>
										<div class="cs-tip__body">
											<strong>Integration instructions</strong>
											<p class="mb-2 mt-1">Paste this snippet inside any form on your site to render the Turnstile challenge:</p>
											<div class="cs-code-block">
												<code>&lt;div class="cf-turnstile" data-sitekey="{{ clientOptions.security_turnstile_site_key }}"&gt;&lt;/div&gt;</code>
												<button @click="copyTurnstileCode" class="cs-code-block__copy" title="Copy to clipboard">
													<font-awesome-icon :icon="['fas', 'copy']" />
													<span>Copy</span>
												</button>
											</div>
											<a href="https://developers.cloudflare.com/turnstile/" target="_blank" class="cs-tip__link">
												<font-awesome-icon :icon="['fas', 'external-link-alt']" class="me-1" />
												View full Cloudflare Turnstile documentation
											</a>
										</div>
									</div>
								</div>
							</div>
						</section>
					</div>

					<hr class="gh-divider" />
					<div class="gh-save-row">
						<button class="gh-save-btn" :disabled="updateLoading" @click="saveOptions">
							<i class="fas fa-save"></i>
							{{ updateLoading ? 'Saving…' : 'Save changes' }}
						</button>
					</div>
				</div>

				<!-- ============================================================ -->
				<!-- TAB: Audits — moved to Tools & Insights → Accessibility      -->
				<!-- (ClientAccessibility.vue). Kept commented so it isn't mounted -->
				<!-- here a second time (it loads its own settings/reports).      -->
				<!-- ============================================================ -->
				<!--
				<div v-show="activeTab === 'audits'" class="col-md-12 mt-3 js-tab-content">
					<ClientAuditsTab :client-id="$route.params.id" :main-dashboard="clientOptions.main_dashboard || 'town-web-dashboard'" />
				</div>
				-->
			</div>
		</template>
		<!-- ── Skeleton Loader ────────────────────────────────────────── -->
		<div v-else class="jcs-skeleton" aria-busy="true" aria-live="polite">
			<div class="row mb-3 mt-4">
				<!-- Tabs strip skeleton -->
				<div class="col-md-12 mt-3">
					<div class="jcs-skeleton__tabs">
						<div
							v-for="i in 12"
							:key="'sk-tab-' + i"
							class="jcs-skeleton__tab jcs-skeleton-shimmer"
							:class="{ 'jcs-skeleton__tab--active': i === 1 }"
						></div>
					</div>
				</div>

				<!-- Tab content skeleton: 3 gh-layout sections -->
				<div class="col-md-12 mt-3 js-tab-content">
					<div v-for="section in 3" :key="'sk-sec-' + section">
						<div class="gh-layout">
							<aside class="gh-intro">
								<div class="jcs-skeleton-line jcs-skeleton-line--section-title jcs-skeleton-shimmer"></div>
								<div class="jcs-skeleton-line jcs-skeleton-line--section-sub jcs-skeleton-shimmer"></div>
								<div class="jcs-skeleton-line jcs-skeleton-line--section-sub2 jcs-skeleton-shimmer"></div>
							</aside>
							<section class="gh-accounts">
								<div class="ada-card jcs-skeleton__card">
									<div class="jcs-skeleton__field">
										<div class="jcs-skeleton-line jcs-skeleton-line--label jcs-skeleton-shimmer"></div>
										<div class="jcs-skeleton-line jcs-skeleton-line--input jcs-skeleton-shimmer"></div>
									</div>
									<div class="jcs-skeleton__field">
										<div class="jcs-skeleton-line jcs-skeleton-line--label jcs-skeleton-shimmer"></div>
										<div class="jcs-skeleton-line jcs-skeleton-line--textarea jcs-skeleton-shimmer"></div>
									</div>
									<div class="jcs-skeleton__field">
										<div class="jcs-skeleton-line jcs-skeleton-line--label jcs-skeleton-shimmer"></div>
										<div class="jcs-skeleton-line jcs-skeleton-line--input jcs-skeleton-shimmer"></div>
									</div>
								</div>
							</section>
						</div>
						<hr v-if="section < 3" class="gh-divider" />
					</div>
				</div>
			</div>
		</div>

		<!-- Offcanvas: Template Variables -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasRight" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Template Variables</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<div>
					<div v-if="variablesTemplateType == 'single' && variablesModel == 'Department'">
						<div class="variable-wrapper">
							<div class="template-variable-item" v-for="(item, i) in departmentSingleTemplateVariables" :key="item.code + '-item-' + i">
								<CustomizerProperty :data="item" v-on:showMediaLibraryModal="showMediaModal($event)" v-on:showDataSelectModal="showDataSelectModal($event)" />
							</div>
						</div>
					</div>
					<div v-if="variablesTemplateType == 'archive' && variablesModel == 'Department'">
						<div class="variable-wrapper">
							<div class="template-variable-item" v-for="(item, i) in departmentArchiveTemplateVariables" :key="item.code + '-item-' + i">
								<CustomizerProperty :data="item" v-on:showMediaLibraryModal="showMediaModal($event)" v-on:showDataSelectModal="showDataSelectModal($event)" />
							</div>
						</div>
					</div>
					<div v-if="variablesModel == 'Event'">
						<div class="variable-wrapper">
							<div class="template-variable-item" v-for="(item, i) in eventArchiveTemplateVariables" :key="item.code + '-item-' + i">
								<CustomizerProperty :data="item" v-on:showMediaLibraryModal="showMediaModal($event)" v-on:showDataSelectModal="showDataSelectModal($event)" />
							</div>
						</div>
					</div>
				</div>
				<div class="mt-3">
					<button @click="saveOptions()" class="btn btn-primary float-end">
						<font-awesome-icon :icon="['fas', updateLoading ? 'spinner' : 'save']" :class="[updateLoading ? 'fa-spin' : '']" />
						Update
					</button>
				</div>
			</div>
		</div>
		<!-- Offcanvas Meeting Category Update -->
		<div class="offcanvas offcanvas-end" tabindex="-1" id="offcanvasCategoriesUpdate" aria-labelledby="offcanvasRightLabel">
			<div class="offcanvas-header">
				<h5 id="offcanvasRightLabel">Update Meeting Category</h5>
				<button type="button" class="btn-close text-reset" data-bs-dismiss="offcanvas" aria-label="Close"></button>
			</div>
			<div class="offcanvas-body">
				<div v-if="selectedCategory">
					<div class="form-group">
						<label>Name</label>
						<input type="text" v-model="selectedCategory.name" class="form-control" />
					</div>
					<div class="form-group">
						<label>Slug</label>
						<input type="text" v-model="selectedCategory.slug" class="form-control" />
					</div>
				</div>
				<div class="mt-3">
					<button @click="updateCategory()" class="btn btn-primary float-end">
						<font-awesome-icon :icon="['fas', updateLoading ? 'spinner' : 'save']" :class="[updateLoading ? 'fa-spin' : '']" />
						Update
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import { mapGetters, mapState } from 'vuex'
import juniperApi from '@/api/juniperApi'
import { heyGovAppUrl } from '@/utils/dashboardLoginUrl'
import CustomizerProperty from '../../../components/juniper/customizer/CustomizerProperty.vue'
import BusinessDirectoryImporter from '../../../components/juniper/business-directory/BusinessDirectoryImporter.vue'
import BusinessDirectoryCategoriesManager from '../../../components/juniper/business-directory/BusinessDirectoryCategoriesManager.vue'
import BusinessDirectoryDangerZone from '../../../components/juniper/business-directory/BusinessDirectoryDangerZone.vue'
// ClientAuditsTab now lives on the Accessibility page (ClientAccessibility.vue).
import MediaLibraryModal from '@/components/juniper/media/MediaLibraryModal'
import { Offcanvas, Modal, Tooltip } from 'bootstrap'
import draggable from 'vuedraggable'
//import restApi from '../../../api/index'

/* ─── Subscriber notification templates — help data ───────────────────────
   The subscriber mail/SMS templates are rendered by juniper-api, not here.
   These constants mirror what that code actually substitutes so the help
   modal can never advertise a variable the notification ignores:

     defaults   → juniper-api/app/Http/Traits/Subscribable.php
                  ::prepareAttributes()  (also duplicated in
                  MeetingRepositoryController::prepareAttributes())
     variables  → juniper-api/app/Notifications/SubscribableCreated.php
                  ::prepareMailContent() / ::prepareSMSContent()

   Note the odd names: {{post name}} has a space (not an underscore) and
   {{_short_post_url_}} is wrapped in underscores — both are matched
   literally by str_replace() on the API side, so they cannot be "tidied up"
   here without breaking every stored template. */

const DEFAULT_MAIL_TEMPLATE = `{{subscriber_name}}:
Here is a posting from the latest notice called: <strong>{{post name}}</strong> {{meeting_date}}

Click here to view the PDF (when available):
<strong>{{shortpost_pdfurl}}</strong>

Click here to {{_short_post_url_}} info found on the page.

<em>{{post_contents}}</em>

&nbsp;

No longer want to receive these notices? {{unsubscribe_link}}`

const DEFAULT_SMS_TEMPLATE = `{{post name}} {{meeting_date}}
View: {{_short_post_url_}}
PDFs: {{shortpost_pdfurl}}`

const TPL_VARS = {
	subscriber_name: {
		code: '{{subscriber_name}}',
		desc: "The subscriber's name. On a test send it falls back to the email address the test was routed to.",
	},
	post_name: {
		code: '{{post name}}',
		desc: 'Title of the notice / meeting. Note the space in the name — it is not an underscore.',
	},
	post_name_sms: {
		code: '{{post name}}',
		desc: 'Title of the notice / meeting, truncated to 45 characters and stripped of special characters to keep the SMS deliverable.',
	},
	meeting_date: {
		code: '{{meeting_date}}',
		desc: 'Meeting date as YYYY-MM-DD. Replaced with nothing for records that have no date (e.g. news notices).',
	},
	shortpost_pdfurl: {
		code: '{{shortpost_pdfurl}}',
		desc: 'Attached documents — agenda, agenda pack and minutes — as a comma-separated list of links. Empty when nothing is attached yet.',
	},
	shortpost_pdfurl_sms: {
		code: '{{shortpost_pdfurl}}',
		desc: 'Attached documents (agenda, agenda pack, minutes) as a comma-separated list of shortened URLs. Empty when nothing is attached yet.',
	},
	short_post_url: {
		code: '{{_short_post_url_}}',
		desc: 'Shortened link to the item on the website, rendered as a "Link" anchor. Reads naturally as "Click here to {{_short_post_url_}} info found on the page."',
	},
	short_post_url_sms: {
		code: '{{_short_post_url_}}',
		desc: 'Shortened link to the item on the website, as a plain URL.',
	},
	post_contents: {
		code: '{{post_contents}}',
		desc: 'Full body of the notice. This is the editor content, so it carries HTML — in an SMS the tags come through as text, keep that in mind.',
	},
	unsubscribe_link: {
		code: '{{unsubscribe_link}}',
		desc: 'Per-subscriber unsubscribe link, rendered as a "Click" anchor. Empty on test sends, which have no subscriber record.',
	},
}

export default {
	name: 'ClientSettings',
	data() {
		return {
			loading: false,
			updateLoading: false,
			meetingCategoriesLoading: false,
			importSubscribersLoading: false,
			// Subscribers → Basic tab selector for the templates sub-panel.
			// 'subscription' = regular notice notifications (non-emergency),
			// 'alert'        = emergency-notification mails/SMS.
			// See juniper-api Subscribable::prepareAttributes() — the branch is
			// decided by $model->isEmergencyNotification().
			subTemplateTab: 'subscription',
			// Which template field's help modal is open (option key), or null.
			templateHelpKey: null,
			// Per-field help shown in that modal: description, the default the
			// API falls back to when the option is blank, and the variables the
			// notification actually substitutes.
			//
			// Source of truth for defaults:
			//   juniper-api/app/Http/Traits/Subscribable.php::prepareAttributes()
			// Source of truth for variables:
			//   juniper-api/app/Notifications/SubscribableCreated.php
			//   ::prepareMailContent() (email) / ::prepareSMSContent() (SMS)
			// Keep this in sync when either side changes — a variable that isn't
			// substituted there is delivered to the subscriber as literal text.
			templateHelp: {
				subscription_mail_subject_template: {
					title: 'Subscription subject (email)',
					desc: 'Subject line of the regular notification email — sent to subscribers when a notice, meeting or agenda they follow is published.',
					default: 'Notice: {{post name}}',
					vars: [TPL_VARS.post_name],
					note: 'Only {{post name}} is replaced in the subject line. Any other variable is delivered as literal text.',
				},
				subscription_mail_template: {
					title: 'Subscription email body',
					desc: 'Body of the regular notification email. HTML is allowed, so you can add a logo, links or styling.',
					default: DEFAULT_MAIL_TEMPLATE,
					vars: [
						TPL_VARS.subscriber_name,
						TPL_VARS.post_name,
						TPL_VARS.meeting_date,
						TPL_VARS.shortpost_pdfurl,
						TPL_VARS.short_post_url,
						TPL_VARS.post_contents,
						TPL_VARS.unsubscribe_link,
					],
				},
				subscription_sms_template: {
					title: 'Subscription SMS body',
					desc: 'Text message sent for a regular notification. Plain text only — keep it short, links are automatically shortened.',
					default: DEFAULT_SMS_TEMPLATE,
					vars: [
						TPL_VARS.post_name_sms,
						TPL_VARS.meeting_date,
						TPL_VARS.short_post_url_sms,
						TPL_VARS.shortpost_pdfurl_sms,
						TPL_VARS.post_contents,
					],
					note: '{{subscriber_name}} and {{unsubscribe_link}} are email-only — in an SMS they are delivered as literal text.',
				},
				alert_mail_subject_template: {
					title: 'Alert subject (email)',
					desc: 'Subject line of the emergency-alert email. Used only for records flagged as an emergency notification.',
					default: 'Emergency Alert',
					vars: [TPL_VARS.post_name],
					note: 'Only {{post name}} is replaced in the subject line. Any other variable is delivered as literal text.',
				},
				alert_mail_template: {
					title: 'Alert email body',
					desc: 'Body of the emergency-alert email. HTML is allowed. The default sends just the alert text, with no wrapper.',
					default: '{{post_contents}}',
					vars: [
						TPL_VARS.subscriber_name,
						TPL_VARS.post_name,
						TPL_VARS.meeting_date,
						TPL_VARS.shortpost_pdfurl,
						TPL_VARS.short_post_url,
						TPL_VARS.post_contents,
						TPL_VARS.unsubscribe_link,
					],
				},
				alert_sms_template: {
					title: 'Alert SMS body',
					desc: 'Text message sent for an emergency alert. Plain text only. The default sends just the alert text.',
					default: '{{post_contents}}',
					vars: [
						TPL_VARS.post_name_sms,
						TPL_VARS.meeting_date,
						TPL_VARS.short_post_url_sms,
						TPL_VARS.shortpost_pdfurl_sms,
						TPL_VARS.post_contents,
					],
					note: '{{subscriber_name}} and {{unsubscribe_link}} are email-only — in an SMS they are delivered as literal text.',
				},
			},
			// Restore (rollback) — operator-only section in the General tab.
			restore: { archives: [], loading: false, busy: false, showConfirm: false, selected: null },
			client: {},
			// Per-theme capability flags loaded from
			// GET /clients/{id}/theme-capabilities. Populated in created()
			// so feature toggles (Business Directory etc.) can show a
			// "theme supports / does not support" badge and be disabled
			// when the active theme is missing the required templates.
			// Shape: { theme: 'default', capabilities: { business_directory: true } }
			// Reduced for template convenience to: { theme, business_directory }
			themeCapabilities: null,
			activeTab: 'general',
			timezones: [
				{ label: '(GMT-12:00) International Date Line West', value: 'Etc/GMT+12' },
				{ label: '(GMT-11:00) Midway Island, Samoa', value: 'Pacific/Midway' },
				{ label: '(GMT-10:00) Hawaii', value: 'Pacific/Honolulu' },
				{ label: '(GMT-09:00) Alaska', value: 'US/Alaska' },
				{ label: '(GMT-08:00) Pacific Time (US & Canada)', value: 'America/Los_Angeles' },
				{ label: '(GMT-08:00) Tijuana, Baja California', value: 'America/Tijuana' },
				{ label: '(GMT-07:00) Arizona', value: 'US/Arizona' },
				{ label: '(GMT-07:00) Chihuahua, La Paz, Mazatlan', value: 'America/Chihuahua' },
				{ label: '(GMT-07:00) Mountain Time (US & Canada)', value: 'US/Mountain' },
				{ label: '(GMT-06:00) Central America', value: 'America/Managua' },
				{ label: '(GMT-06:00) Central Time (US & Canada)', value: 'US/Central' },
				{ label: '(GMT-06:00) Guadalajara, Mexico City, Monterrey', value: 'America/Mexico_City' },
				{ label: '(GMT-06:00) Saskatchewan', value: 'Canada/Saskatchewan' },
				{ label: '(GMT-05:00) Bogota, Lima, Quito, Rio Branco', value: 'America/Bogota' },
				{ label: '(GMT-05:00) Eastern Time (US & Canada)', value: 'US/Eastern' },
				{ label: '(GMT-05:00) Indiana (East)', value: 'US/East-Indiana' },
				{ label: '(GMT-04:00) Atlantic Time (Canada)', value: 'Canada/Atlantic' },
				{ label: '(GMT-04:00) Caracas, La Paz', value: 'America/Caracas' },
				{ label: '(GMT-04:00) Manaus', value: 'America/Manaus' },
				{ label: '(GMT-04:00) Santiago', value: 'America/Santiago' },
				{ label: '(GMT-03:30) Newfoundland', value: 'Canada/Newfoundland' },
				{ label: '(GMT-03:00) Brasilia', value: 'America/Sao_Paulo' },
				{ label: '(GMT-03:00) Buenos Aires, Georgetown', value: 'America/Argentina/Buenos_Aires' },
				{ label: '(GMT-03:00) Greenland', value: 'America/Godthab' },
				{ label: '(GMT-03:00) Montevideo', value: 'America/Montevideo' },
				{ label: '(GMT-02:00) Mid-Atlantic', value: 'America/Noronha' },
				{ label: '(GMT-01:00) Cape Verde Is.', value: 'Atlantic/Cape_Verde' },
				{ label: '(GMT-01:00) Azores', value: 'Atlantic/Azores' },
				{ label: '(GMT+00:00) Casablanca, Monrovia, Reykjavik', value: 'Africa/Casablanca' },
				{
					label: '(GMT+00:00) Greenwich Mean Time : Dublin, Edinburgh, Lisbon, London',
					value: 'Etc/Greenwich' },
				{ label: '(GMT+01:00) Amsterdam, Berlin, Bern, Rome, Stockholm, Vienna', value: 'Europe/Amsterdam' },
				{ label: '(GMT+01:00) Belgrade, Bratislava, Budapest, Ljubljana, Prague', value: 'Europe/Belgrade' },
				{ label: '(GMT+01:00) Brussels, Copenhagen, Madrid, Paris', value: 'Europe/Brussels' },
				{ label: '(GMT+01:00) Sarajevo, Skopje, Warsaw, Zagreb', value: 'Europe/Sarajevo' },
				{ label: '(GMT+01:00) West Central Africa', value: 'Africa/Lagos' },
				{ label: '(GMT+02:00) Amman', value: 'Asia/Amman' },
				{ label: '(GMT+02:00) Athens, Bucharest, Istanbul', value: 'Europe/Athens' },
				{ label: '(GMT+02:00) Beirut', value: 'Asia/Beirut' },
				{ label: '(GMT+02:00) Cairo', value: 'Africa/Cairo' },
				{ label: '(GMT+02:00) Harare, Pretoria', value: 'Africa/Harare' },
				{ label: '(GMT+02:00) Helsinki, Kyiv, Riga, Sofia, Tallinn, Vilnius', value: 'Europe/Helsinki' },
				{ label: '(GMT+02:00) Jerusalem', value: 'Asia/Jerusalem' },
				{ label: '(GMT+02:00) Minsk', value: 'Europe/Minsk' },
				{ label: '(GMT+02:00) Windhoek', value: 'Africa/Windhoek' },
				{ label: '(GMT+03:00) Kuwait, Riyadh, Baghdad', value: 'Asia/Kuwait' },
				{ label: '(GMT+03:00) Moscow, St. Petersburg, Volgograd', value: 'Europe/Moscow' },
				{ label: '(GMT+03:00) Nairobi', value: 'Africa/Nairobi' },
				{ label: '(GMT+03:00) Tbilisi', value: 'Asia/Tbilisi' },
				{ label: '(GMT+03:30) Tehran', value: 'Asia/Tehran' },
				{ label: '(GMT+04:00) Abu Dhabi, Muscat', value: 'Asia/Muscat' },
				{ label: '(GMT+04:00) Baku', value: 'Asia/Baku' },
				{ label: '(GMT+04:00) Yerevan', value: 'Asia/Yerevan' },
				{ label: '(GMT+04:30) Kabul', value: 'Asia/Kabul' },
				{ label: '(GMT+05:00) Yekaterinburg', value: 'Asia/Yekaterinburg' },
				{ label: '(GMT+05:00) Islamabad, Karachi, Tashkent', value: 'Asia/Karachi' },
				{ label: '(GMT+05:30) Chennai, Kolkata, Mumbai, New Delhi', value: 'Asia/Calcutta' },
				{ label: '(GMT+05:30) Sri Jayawardenapura', value: 'Asia/Calcutta' },
				{ label: '(GMT+05:45) Kathmandu', value: 'Asia/Katmandu' },
				{ label: '(GMT+06:00) Almaty, Novosibirsk', value: 'Asia/Almaty' },
				{ label: '(GMT+06:00) Astana, Dhaka', value: 'Asia/Dhaka' },
				{ label: '(GMT+06:30) Yangon (Rangoon)', value: 'Asia/Rangoon' },
				{ label: '(GMT+07:00) Bangkok, Hanoi, Jakarta', value: 'Asia/Bangkok' },
				{ label: '(GMT+07:00) Krasnoyarsk', value: 'Asia/Krasnoyarsk' },
				{ label: '(GMT+08:00) Beijing, Chongqing, Hong Kong, Urumqi', value: 'Asia/Hong_Kong' },
				{ label: '(GMT+08:00) Kuala Lumpur, Singapore', value: 'Asia/Kuala_Lumpur' },
				{ label: '(GMT+08:00) Irkutsk, Ulaan Bataar', value: 'Asia/Irkutsk' },
				{ label: '(GMT+08:00) Perth', value: 'Australia/Perth' },
				{ label: '(GMT+08:00) Taipei', value: 'Asia/Taipei' },
				{ label: '(GMT+09:00) Osaka, Sapporo, Tokyo', value: 'Asia/Tokyo' },
				{ label: '(GMT+09:00) Seoul', value: 'Asia/Seoul' },
				{ label: '(GMT+09:00) Yakutsk', value: 'Asia/Yakutsk' },
				{ label: '(GMT+09:30) Adelaide', value: 'Australia/Adelaide' },
				{ label: '(GMT+09:30) Darwin', value: 'Australia/Darwin' },
				{ label: '(GMT+10:00) Brisbane', value: 'Australia/Brisbane' },
				{ label: '(GMT+10:00) Canberra, Melbourne, Sydney', value: 'Australia/Canberra' },
				{ label: '(GMT+10:00) Hobart', value: 'Australia/Hobart' },
				{ label: '(GMT+10:00) Guam, Port Moresby', value: 'Pacific/Guam' },
				{ label: '(GMT+10:00) Vladivostok', value: 'Asia/Vladivostok' },
				{ label: '(GMT+11:00) Magadan, Solomon Is., New Caledonia', value: 'Asia/Magadan' },
				{ label: '(GMT+12:00) Auckland, Wellington', value: 'Pacific/Auckland' },
				{ label: '(GMT+12:00) Fiji, Kamchatka, Marshall Is.', value: 'Pacific/Fiji' },
				{ label: "(GMT+13:00) Nuku'alofa", value: 'Pacific/Tongatapu' },
			],
			dateFormats: [
				{
					label: 'Month-Day-Year with leading zeros (03/17/2023)',
					value: 'MM/DD/YY' },
				{
					label: 'Day-Month-Year with leading zeros (17/03/2023)',
					value: 'DD/MM/YY' },
				{
					label: 'Year-Month-Day with leading zeros (2023/03/17)',
					value: 'YY/MM/DD' },
				{
					label: 'Month name-Day-Year with no leading zeros (March 17, 2023)',
					value: 'Month D, Yr' },
				{
					label: 'Month-Day-Year with no leading zeros (3/17/2023)',
					value: 'M/D/YY' },
				{
					label: 'Day-Month-Year with no leading zeros (17/3/2023)',
					value: 'D/M/YY' },
				{
					label: 'Year-Month-Day with no leading zeros (2023/3/17)',
					value: 'YY/M/D' },
				{
					label: 'Month-Day-Year with spaces instead of leading zeros ( 3/17/2023)',
					value: 'bM/bD/YY' },
				{
					label: 'Custom',
					value: 'custom' },
			],
			clientOptions: {
				main_dashboard: 'town-web-dashboard',
				business_directory_enabled: false,
				// Page revisions. Both default off: options store booleans as "1"/""
				// and get_option() falls back to the default whenever the stored
				// value is falsy, so an unset option and an explicit off are the
				// same thing — a default-on flag could never be turned off.
				page_revisions_enabled: false,
				page_edit_approvals_enabled: false,
				main_title: '',
				main_description: '',
				main_language: 'en',
				// Custom domains — JSON-stored array of additional hostnames mapped
				// to this client. The primary domain (`client.url`) is implicit and
				// not stored here. Each entry is a plain string like
				// 'www.example.com' or 'example.com'.
				custom_domains: [],
				main_timezone: 'US/Central',
				main_date_format: 'MM/DD/YY',
				theme_favicon_light: '',
				theme_favicon_dark: '',
				theme_social_image: '',
				// Legacy keys kept so existing saved values don't get clobbered on
				// load — they are no longer bound to any UI. The static rendering
				// pipeline reads main_description / theme_favicon_light /
				// theme_social_image instead.
				main_favicon: '',
				seo_og_image: '',
				seo_meta_description: '',
				seo_meta_keywords: '',
				seo_og_title: '',
				seo_og_description: '',
				seo_twitter_card_type: '',
				seo_twitter_site: '',
				seo_google_verification: '',
				seo_bing_verification: '',
				seo_allow_indexing: true,
				Department_source_heygov: false,
				Subscriber_source_heygov: false,
				Post_single_template: 'default',
				Post_archive_template: 'default',
				Post_per_page: 6,
				Post_prefix: 'posts',
				NewsNotice_single_template: 'default',
				NewsNotice_archive_template: 'default',
				NewsNotice_per_page: 6,
				NewsNotice_prefix: 'news-and-notices',
				// Unified Dashboard only: id of the Page Builder page that serves as
				// the public news archive. When set, the theme's archive list is no
				// longer generated and NewsNotice_prefix follows that page's slug
				// (both handled by juniper-api). Must stay after NewsNotice_prefix:
				// options are saved in order, so the page wins over a stale prefix.
				NewsNotice_archive_page_id: '',
				Department_single_template: 'default',
				Department_archive_template: 'default',
				Department_per_page: 6,
				Department_prefix: 'departments',
				Department_archive_order_column: 'created_at',
				Department_archive_order: 'ASC',
				Faq_single_template: 'default',
				Faq_archive_template: 'default',
				Faq_archive_page_title: 'Faqs',
				Faq_per_page: 6,
				Faq_prefix: 'faqs',
				Event_single_template: 'default',
				Event_archive_template: 'default',
				Event_per_page: 6,
				Event_prefix: 'events',
				// Unified Dashboard only: id of the Page Builder page that serves
				// as the public events archive. When set, the theme's fixed-iframe
				// archive is no longer generated and Event_prefix follows that
				// page's slug (both handled by juniper-api).
				Event_archive_page_id: '',
				Meeting_single_template: 'default',
				Meeting_archive_template: 'default',
				Meeting_per_page: 6,
				Meeting_prefix: 'meetings',
				// Unified Dashboard only: opt back into the classic predefined
				// meetings page. Off = the page is not generated in the static
				// build at all (meetings render via the Page Builder element).
				Meeting_use_predefined_page: false,
				security_turnstile_enabled: false,
				security_turnstile_site_key: '',
				security_turnstile_secret_key: '' },
			postsSingleTemplatesAvailable: [],
			postsArchiveTemplatesAvailable: [],
			newsSingleTemplatesAvailable: [],
			newsArchiveTemplatesAvailable: [],
			departmentsSingleTemplatesAvailable: [],
			departmentsArchiveTemplatesAvailable: [],
			faqsSingleTemplatesAvailable: [],
			faqsArchiveTemplatesAvailable: [],
			eventsSingleTemplatesAvailable: [],
			eventsArchiveTemplatesAvailable: [],
			eventArchiveTemplateVariables: [],
			// News archive page state, from GET /clients/{id}/news-archive-page
			newsArchive: {
				loading: false,
				creating: false,
				live: false,
				page: null,
				themeArchiveGenerated: true,
				eligible: { ok: true, reason: null },
				// Published top-level pages holding a NewsNotices element (news_pages)
				newsPages: [],
				nominatedPageId: null,
			},
			// Events archive page state, from GET /clients/{id}/events-archive-page
			eventsArchive: {
				loading: false,
				creating: false,
				live: false,
				page: null,
				themePageGenerated: true,
				eligible: { ok: true, reason: null },
				// Published top-level pages holding a Calendar element — the only
				// pages that can serve as the archive (from calendar_pages).
				calendarPages: [],
				// The page the options point at, live or not. Distinct from `live`:
				// a nominated page that is still a draft is not serving the URL, but
				// the client is already set up and must not get a second page.
				nominatedPageId: null,
			},
			meetingSingleTemplatesAvailable: [],
			meetingArchiveTemplatesAvailable: [],
			departmentSingleTemplateVariables: [],
			departmentArchiveTemplateVariables: [],
			$offcanvasConfig: null,
			offcanvasCategoriesUpdate: null,
			// MediaLibraryModal — shared modal for picking favicon, social icon,
			// or any future image field. `mediaTarget` tells the insert handler
			// which clientOptions field to write the picked URL into.
			mediaLibraryModal: null,
			mediaTarget: null,
			meetingRepositoryCategories: [],
			meetingRepositoryCategoriesSubscribable: [],
			addMeetingCategoryFlag: false,
			newMeetingCategory: { name: '' },
			storeMeetingCategoryLoading: false,
			newsNoticeCategories: [],
			newsNoticeCategoriesSubscribable: [],
			// Subscribers list — server-side load-more pagination. GET /subscribers
			// honours per_page/page (offset+limit, see HasFiltersInRequest) and
			// returns the unfiltered total in the x-wp-total response header, so
			// each click fetches exactly one more page instead of slicing a list
			// that was already fully downloaded.
			subscribers: [],
			subscribersPage: 1,
			subscribersTotal: 0,
			loadingSubscribers: false,
			loadingMoreSubscribers: false,
			SUBSCRIBERS_PAGE_SIZE: 10,
			// Domains tab — local UI state for adding/expanding custom domains.
			// The data itself lives in clientOptions.custom_domains (JSON array).
			addingDomain: false,
			newDomain: '',
			expandedDomain: null,
			// Placeholder DNS target hostname that custom domains should point at.
			// Replace this with the real per-client target once the
			// Cloudflare Pages / Plesk integration is wired up.
			DNS_TARGET_HOST: 'target.townweb.com',
			addNewsCategoryFlag: false,
			newNewsCategory: { name: '' },
			storeCategoryLoading: false,
			faqsCategories: [],
			addFaqsCategoryFlag: false,
			newFaqsCategory: { name: '' },
			storeFaqsCategoryLoading: false,
			meetingTableColumns: [
				{
					id: 'meeting_date',
					label: 'Meeting Date' },
				{
					id: 'title',
					label: 'Name' },
				{
					id: 'agenda',
					label: 'Agenda' },
				{
					id: 'agenda_packet',
					label: 'Agenda Packet' },
				{
					id: 'minutes',
					label: 'Meeting Minutes' },
				{
					id: 'audio',
					label: 'Audio' },
				{
					id: 'video',
					label: 'Video' },
				{
					id: 'additional_documents',
					label: 'Additional Documents' },
				{
					id: 'notes',
					label: 'Notes' },
			],
			meetingTableColumnsActive: [],
			fetchingFbPageToken: false,
			wordpressImport: {
				website: '' },
			variablesTemplateType: '',
			variablesModel: '',
			selectedCategory: null,
			selectedCategoryType: null,
			themeSupportsDepartmentDocuments: false,
			themeSupportsDepartmentQuickMenu: false }
	},
	components: { draggable, CustomizerProperty, BusinessDirectoryImporter, BusinessDirectoryCategoriesManager, BusinessDirectoryDangerZone, MediaLibraryModal },
	computed: {
		...mapState(['authUser']),
		// Restore is an operator-only action — gated to emails starting with "srdjan"
		// here in the UI and again on the backend (rollback returns 403 otherwise).
		canRestore() {
			const email = this.authUser && this.authUser.email
			return !!email && email.toLowerCase().startsWith('srdjan')
		},
		...mapGetters(['isAuth']),
		isUnifiedDashboard() {
			return this.clientOptions.main_dashboard === 'unified-dashboard'
		},
		// Pages the events archive can be pointed at: published, top-level, and
		// actually holding a Calendar element — anything else would serve an
		// events URL with no calendar on it. juniper-api does the filtering
		// (calendar_pages); a department-attached page is excluded there because
		// it is served under {Department_prefix}/{dept}/{slug}.
		//
		// The page currently in use is kept in the list even if it no longer has
		// a Calendar element (someone removed it in the builder), so the picker
		// still shows what is configured instead of silently reading as "none".
		// Same rules as selectableArchivePages, for the news archive
		selectableNewsArchivePages() {
			const pages = this.newsArchive.newsPages
			const live = this.newsArchive.page
			if (live && !pages.some(page => page.id === live.id)) {
				return [...pages, { id: live.id, title: live.title, slug: live.slug, missingElement: true }]
			}
			return pages
		},
		newsArchiveOwnsPrefix() {
			return this.isUnifiedDashboard && this.newsArchive.live
		},
		selectableArchivePages() {
			const pages = this.eventsArchive.calendarPages
			const live = this.eventsArchive.page
			if (live && !pages.some(page => page.id === live.id)) {
				return [...pages, { id: live.id, title: live.title, slug: live.slug, missingCalendar: true }]
			}
			return pages
		},
		// Cleaned domain shown in the search-result preview card (no protocol,
		// no trailing slash). Falls back to staging URL or a placeholder.
		previewUrl() {
			const raw = (this.client && (this.client.url || this.client.staging_url)) || ''
			return raw.replace(/^https?:\/\//, '').replace(/\/$/, '') || 'example.com'
		},
		// First letter of the site title (uppercase) used as the avatar
		// fallback when no favicon is set — mirrors how HeyGov draws a
		// gradient circle with a single letter.
		previewLetter() {
			const t = (this.clientOptions.main_title || this.client?.name || '?').trim()
			return t.charAt(0).toUpperCase() || '?'
		},
		// More rows left on the server than we have fetched so far. Guarded on
		// subscribersTotal so the footer stays hidden until the first response
		// (and its x-wp-total header) has arrived.
		hasMoreSubscribers() {
			return this.subscribersTotal > 0 && this.subscribers.length < this.subscribersTotal
		},
		// Total count of custom domains shown in the Domains tab — the
		// primary (client.url) counts as 1 when set, plus any extras stored
		// in clientOptions.custom_domains.
		totalCustomDomains() {
			const extras = Array.isArray(this.clientOptions.custom_domains) ? this.clientOptions.custom_domains.length : 0
			return (this.cleanedClientUrl() ? 1 : 0) + extras
		},
		// Help entry for the currently open template modal. Falls back to an
		// empty shape so the modal markup never touches undefined while the
		// v-if is being torn down.
		activeTemplateHelp() {
			return this.templateHelp[this.templateHelpKey] ?? { title: '', desc: '', default: '', vars: [], note: '' }
		},
	},
	watch: {
		// Approvals have nowhere to store a change without revisions, so turning
		// the master switch off clears the dependent one instead of leaving a
		// checked-but-disabled box behind.
		'clientOptions.page_revisions_enabled'(enabled) {
			if (!enabled) {
				this.clientOptions.page_edit_approvals_enabled = false
			}
		},
		// Dashboard ↔ data-source coupling. The Unified Dashboard only works
		// with HeyGov-sourced data, so toggling main_dashboard automatically
		// mirrors that into Department_source_heygov / Subscriber_source_heygov.
		// This makes the source toggles redundant in the UI — they're managed
		// implicitly. (UI sections that exposed these toggles were removed.)
		// Picking the Unified Dashboard also switches Page Revisions and edit
		// approvals on (they can still be turned off afterwards); switching back
		// to Town Web leaves them as they are.
		'clientOptions.main_dashboard'(newVal, oldVal) {
			const useHeyGov = newVal === 'unified-dashboard'
			this.clientOptions.Department_source_heygov = useHeyGov
			this.clientOptions.Subscriber_source_heygov = useHeyGov
			if (useHeyGov && oldVal !== undefined && oldVal !== newVal) {
				this.clientOptions.page_revisions_enabled = true
				this.clientOptions.page_edit_approvals_enabled = true
			}
		},
	},
	mounted() {
		this.$offcanvasConfig = new Offcanvas(document.getElementById('offcanvasRight'))
		this.offcanvasCategoriesUpdate = new Offcanvas(document.getElementById('offcanvasCategoriesUpdate'))
		this.mediaLibraryModal = new Modal('#mediaLibraryModal')
		this.initTemplateTooltips()
	},
	created() {
		this.getClient()
		this.loadThemeCapabilities()
		this.fetchRestoreArchives()
	},
	methods: {
		heyGovAppUrl,
		// ─── Restore / rollback (operator only) ──────────────────────────
		async fetchRestoreArchives() {
			if (!this.canRestore) return
			this.restore.loading = true
			try {
				const { data } = await juniperApi.get(`/clients/${this.$route.params.id}/upgrade/archives`)
				// archives() returns a paginator → rows are under data.data
				this.restore.archives = data?.data ?? (Array.isArray(data) ? data : [])
			} catch (e) {
				this.restore.archives = []
			} finally {
				this.restore.loading = false
			}
		},
		openRestoreConfirm(archive) {
			this.restore.selected = archive
			this.restore.showConfirm = true
		},
		closeRestoreConfirm() {
			this.restore.showConfirm = false
			this.restore.selected = null
		},
		async doRestore() {
			if (!this.restore.selected) return
			this.restore.busy = true
			try {
				await juniperApi.post(`/clients/${this.$route.params.id}/upgrade/rollback`, {
					archive_id: this.restore.selected.id,
				})
				this.restore.showConfirm = false
				this.$swal.fire({ icon: 'success', title: 'Restore complete.', timer: 2500, showConfirmButton: false })
				await this.fetchRestoreArchives()
			} catch (err) {
				this.$swal.fire({
					icon: 'error',
					title: 'Restore failed.',
					text: err?.response?.data?.message ?? err?.response?.data?.error ?? err.message,
					showConfirmButton: true,
				})
			} finally {
				this.restore.busy = false
			}
		},
		formatRestoreDate(value) {
			return value ? new Date(value).toLocaleString() : '—'
		},

		// ─── Subscriber template help ────────────────────────────────────
		// Info icon next to each mail/SMS template field opens a modal with
		// the API-side default and the variables that field supports.
		openTemplateHelp(key) {
			// Hide the hover tooltip before the modal covers it, otherwise it
			// stays pinned on screen after the icon loses the mouse.
			const tip = Tooltip.getInstance(document.activeElement)
			if (tip) tip.hide()
			this.templateHelpKey = key
		},
		closeTemplateHelp() {
			this.templateHelpKey = null
		},
		// Write the default into the field so the operator can edit from it
		// instead of retyping. Leaving the field blank has the same delivered
		// result, but a filled-in field is easier to tweak.
		applyTemplateDefault() {
			const key = this.templateHelpKey
			if (!key) return
			this.clientOptions[key] = this.templateHelp[key].default
			this.closeTemplateHelp()
		},
		// Bootstrap tooltips on the template info icons. They render once with
		// the tab, so a single pass in mounted() is enough.
		initTemplateTooltips() {
			this.$nextTick(() => {
				this.$el.querySelectorAll('.cs-tpl-info[data-bs-toggle="tooltip"]').forEach((el) => {
					const existing = Tooltip.getInstance(el)
					if (existing) existing.dispose()
					new Tooltip(el)
				})
			})
		},

		// ─── Media library picker ────────────────────────────────────────
		// Shared MediaLibraryModal opens for any image-pickable field on the
		// page. The caller passes a target key (e.g. 'favicon'), the modal
		// emits the picked URL via mediaInsert, and we write it back to the
		// corresponding clientOptions field.
		openMediaPicker(target) {
			this.mediaTarget = target
			this.mediaLibraryModal.show()
		},
		onMediaInsert(url) {
			if (this.mediaTarget === 'favicon') {
				this.clientOptions.theme_favicon_light = url
			} else if (this.mediaTarget === 'social_icon') {
				this.clientOptions.theme_social_image = url
			}
			this.mediaLibraryModal.hide()
			this.mediaTarget = null
		},
		onMediaHide() {
			this.mediaTarget = null
		},
		clearImage(target) {
			if (target === 'favicon') {
				this.clientOptions.theme_favicon_light = ''
			} else if (target === 'social_icon') {
				this.clientOptions.theme_social_image = ''
			}
		},

		// Get client
		getClient() {
			this.loading = true
			juniperApi
				.get('/clients/' + this.$route.params.id)
				.then(({ data }) => {
					this.client = data
					this.getClientOptions()
					this.getMeetingRepositoryCategories()
					this.getNewsNoticeCategories()
					this.getFaqsCategories()
					this.getSubscribers()
					this.getThemeTemplates()
					this.loadEventsArchiveState()
					this.loadNewsArchiveState()
					// NOTE: loading is cleared by getClientOptions() so the skeleton
					// stays visible until clientOptions arrive. Clearing it here
					// would flash a blank page between the two API responses.
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		// Load per-theme capability flags (business_directory etc.) so the
		// Settings UI can show "theme supports / does not support" badges
		// next to feature toggles and disable toggles whose underlying
		// templates aren't present in the active theme's storage.
		// Failure is non-fatal — badges just don't render.
		async loadThemeCapabilities() {
			try {
				const { data } = await juniperApi.get('/clients/' + this.$route.params.id + '/theme-capabilities')
				this.themeCapabilities = {
					theme: data.theme,
					business_directory: !!(data.capabilities && data.capabilities.business_directory),
					faq_accordions: !!(data.capabilities && data.capabilities.faq_accordions),
				}
			} catch (e) {
				console.warn('theme-capabilities load failed', e)
				this.themeCapabilities = null
			}
		},

		// Get client options
		getClientOptions() {
			// loading is set true by getClient(); leave it set so the skeleton
			// stays visible across both /clients/:id and /options/:id calls.
			this.loading = true
			juniperApi
				.post('/options/' + this.$route.params.id)
				.then(({ data }) => {
					Object.keys(data).forEach(k => {
						if (k.includes('theme_') && !['theme_favicon_light', 'theme_favicon_dark', 'theme_social_image'].includes(k)) {
							delete data[k]
						}
					})
					this.clientOptions = { ...this.clientOptions, ...data }
					if (this.clientOptions.subscribable_terms) {
						if (this.clientOptions.subscribable_terms['App\\Models\\MeetingRepositoryCategory']) {
							this.meetingRepositoryCategoriesSubscribable = this.clientOptions.subscribable_terms['App\\Models\\MeetingRepositoryCategory']
						}
						if (this.clientOptions.subscribable_terms['App\\Models\\NewsNoticeCategory']) {
							this.newsNoticeCategoriesSubscribable = this.clientOptions.subscribable_terms['App\\Models\\NewsNoticeCategory']
						}
					}
					if (this.clientOptions.meeting_table_active_columns) {
						this.meetingTableColumnsActive = this.clientOptions.meeting_table_active_columns
					}
					if (this.clientOptions.template_variables_departments_single_json) {
						this.departmentSingleTemplateVariables = JSON.parse(this.clientOptions.template_variables_departments_single_json)
					}
					if (this.clientOptions.template_variables_departments_archive_json) {
						this.departmentArchiveTemplateVariables = JSON.parse(this.clientOptions.template_variables_departments_archive_json)
					}
					if (this.clientOptions.template_variables_events_archive_json) {
						this.eventArchiveTemplateVariables = JSON.parse(this.clientOptions.template_variables_events_archive_json)
					}

					this.loading = false
				})
				.catch(error => {
					this.loading = false
					console.log(error)
				})
		},

		// ─── Events archive page ─────────────────────────────────────────
		// Which page (if any) is actually serving the events archive URL, plus
		// eligibility and the pages that can be picked for the job.
		loadEventsArchiveState() {
			this.eventsArchive.loading = true
			juniperApi
				.get(`/clients/${this.$route.params.id}/events-archive-page`)
				.then(({ data }) => {
					this.eventsArchive.live = !!data.live
					this.eventsArchive.page = data.page ?? null
					this.eventsArchive.themePageGenerated = !!data.theme_page_generated
					this.eventsArchive.eligible = data.eligible ?? { ok: true, reason: null }
					this.eventsArchive.calendarPages = data.calendar_pages ?? []
					this.eventsArchive.nominatedPageId = data.nominated_page_id ?? null
				})
				.catch(error => {
					console.log(error)
				})
				.then(() => {
					this.eventsArchive.loading = false
				})
		},

		// Persist the picked page. Event_prefix is deliberately not touched here:
		// juniper-api derives it from the page's slug (Option model sync), so the
		// options are re-read afterwards to show the resulting URL.
		async onEventsArchivePageChange() {
			await this.saveOptions()
			this.getClientOptions()
			this.loadEventsArchiveState()
		},

		createEventsArchivePage() {
			this.eventsArchive.creating = true
			juniperApi
				.post(`/clients/${this.$route.params.id}/events-archive-page`)
				.then(({ data }) => {
					this.$swal.fire({
						icon: data.created ? 'success' : 'info',
						title: data.message,
						timer: 4000 })
					this.getClientOptions()
					this.loadEventsArchiveState()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error?.response?.data?.message || 'Could not create the events page' })
					console.log(error)
				})
				.then(() => {
					this.eventsArchive.creating = false
				})
		},

		// ─── News archive page ───────────────────────────────────────────
		// Same flow as the events archive page above.
		loadNewsArchiveState() {
			this.newsArchive.loading = true
			juniperApi
				.get(`/clients/${this.$route.params.id}/news-archive-page`)
				.then(({ data }) => {
					this.newsArchive.live = !!data.live
					this.newsArchive.page = data.page ?? null
					this.newsArchive.themeArchiveGenerated = !!data.theme_archive_generated
					this.newsArchive.eligible = data.eligible ?? { ok: true, reason: null }
					this.newsArchive.newsPages = data.news_pages ?? []
					this.newsArchive.nominatedPageId = data.nominated_page_id ?? null
				})
				.catch(error => {
					console.log(error)
				})
				.then(() => {
					this.newsArchive.loading = false
				})
		},

		// NewsNotice_prefix is derived by juniper-api from the page's slug (and a
		// new prefix rebuilds the site there), so options are re-read afterwards
		async onNewsArchivePageChange() {
			await this.saveOptions()
			this.getClientOptions()
			this.loadNewsArchiveState()
		},

		createNewsArchivePage() {
			this.newsArchive.creating = true
			juniperApi
				.post(`/clients/${this.$route.params.id}/news-archive-page`)
				.then(({ data }) => {
					this.$swal.fire({
						icon: data.created ? 'success' : 'info',
						title: data.message,
						timer: 4000 })
					this.getClientOptions()
					this.loadNewsArchiveState()
				})
				.catch(error => {
					this.$swal.fire({
						icon: 'error',
						title: error?.response?.data?.message || 'Could not create the news page' })
					console.log(error)
				})
				.then(() => {
					this.newsArchive.creating = false
				})
		},

		saveOptions() {
			this.updateLoading = true
			// Handle template variables
			if (this.variablesTemplateType == 'single' && this.variablesModel == 'Department') {
				this.clientOptions.template_variables_departments_single_json = this.departmentSingleTemplateVariables
			}
			if (this.variablesTemplateType == 'archive' && this.variablesModel == 'Department') {
				this.clientOptions.template_variables_departments_archive_json = this.departmentArchiveTemplateVariables
			}
			if (this.variablesModel == 'Event') {
				this.clientOptions.template_variables_events_archive_json = this.eventArchiveTemplateVariables
			}

			if (!this.clientOptions.subscribable_terms) {
				this.clientOptions.subscribable_terms = {}
			}

			if (this.clientOptions.heygov_jurisdiction) {
				this.clientOptions.heygov_jurisdiction = this.clientOptions.heygov_jurisdiction.toLowerCase().trim()
			}
			this.clientOptions.subscribable_terms['App\\Models\\MeetingRepositoryCategory'] = this.meetingRepositoryCategoriesSubscribable
			this.clientOptions.subscribable_terms['App\\Models\\NewsNoticeCategory'] = this.newsNoticeCategoriesSubscribable

			this.clientOptions.meeting_table_active_columns = this.meetingTableColumnsActive

			// Returned so callers that need to read options back can wait for the
			// write to land (see onEventsArchivePageChange).
			return juniperApi
				.post('/options/' + this.$route.params.id + '/save', { params: this.clientOptions })
				.then(({ data }) => {
					this.updateLoading = false
					this.$swal.fire({
						icon: 'success',
						title: data.message,
						timer: 2000 })
				})
				.catch(error => {
					this.updateLoading = false
					console.log(error)
				})
		},

		loadTemplateVariables(object, templateType) {
			let selectedTemplate = 'default'
			if (object == 'departments' && templateType == 'single') {
				selectedTemplate = this.clientOptions.Department_single_template
			}
			if (object == 'departments' && templateType == 'archive') {
				selectedTemplate = this.clientOptions.Department_archive_template
			}
			if (object == 'events') {
				selectedTemplate = this.clientOptions.Event_archive_template
			}

			juniperApi
				.post(`themes/${this.$route.params.id}/templates/variables/${object}`, {
					selected_template: selectedTemplate,
					template_type: templateType })
				.then(({ data }) => {
					const of = new Offcanvas(document.getElementById('offcanvasRight'))
					of.show()
					if (object == 'departments') {
						if (templateType == 'single') {
							this.departmentSingleTemplateVariables = data
							this.variablesTemplateType = 'single'
							this.variablesModel = 'Department'
						}
						if (templateType == 'archive') {
							this.departmentArchiveTemplateVariables = data
							this.variablesTemplateType = 'archive'
							this.variablesModel = 'Department'
						}
					}
					if (object == 'events') {
						this.eventArchiveTemplateVariables = data
						this.variablesModel = 'Event'
					}
				})
				.catch(error => {
					console.log(error)
				})
		},

		getMeetingRepositoryCategories() {
			juniperApi
				.get(`/meeting-repository-categories`, { params: { client_id: this.$route.params.id, orderby: 'order', order: 'asc' } })
				.then(({ data }) => {
					this.meetingRepositoryCategories = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		// Get all news and notices categories for client
		getNewsNoticeCategories() {
			juniperApi
				.get(`/news-notice-categories`, { params: { client_id: this.$route.params.id } })
				.then(({ data }) => {
					this.newsNoticeCategories = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		syncHeygovMeetingRepositoryCategories() {
			this.meetingCategoriesLoading = true
			let jurisdiction = this.clientOptions.heygov_jurisdiction
			if (jurisdiction) {
				juniperApi
					.post(`/meeting-repository-categories/sync`, {
						client_id: this.$route.params.id,
						jurisdiction_id: jurisdiction })
					.then(({ data }) => {
						this.meetingRepositoryCategories = data
						this.meetingCategoriesLoading = false
					})
					.catch(error => {
						console.log(error)
					})
			}
		},

		// One page of subscribers. `orderby` is required, not cosmetic: the API
		// adds no default ORDER BY (see HasFiltersInRequest::addFilters), and
		// LIMIT/OFFSET without one lets MySQL return rows in any order, so
		// consecutive pages could repeat or skip subscribers.
		fetchSubscribersPage(page) {
			return juniperApi.get(`/subscribers`, {
				params: {
					client_id: this.$route.params.id,
					per_page: this.SUBSCRIBERS_PAGE_SIZE,
					page,
					orderby: 'id',
					order: 'asc',
				},
			})
		},

		// First page. Replaces the list and records the total so the footer
		// knows whether more pages exist.
		getSubscribers() {
			this.loadingSubscribers = true
			this.subscribersPage = 1
			return this.fetchSubscribersPage(1)
				.then(res => {
					this.subscribers = res.data
					this.subscribersTotal = this.readTotalHeader(res, res.data.length)
				})
				.catch(error => {
					console.log(error)
				})
				.finally(() => {
					this.loadingSubscribers = false
				})
		},

		// x-wp-total carries the count before offset/limit is applied. The
		// header needs `access-control-expose-headers` to survive CORS — if a
		// proxy strips it, fall back to what we actually received so the UI
		// degrades to "no more pages" instead of paging on a NaN total.
		readTotalHeader(res, fallback) {
			const total = parseInt(res?.headers?.['x-wp-total'], 10)
			return Number.isNaN(total) ? fallback : total
		},

		// ─── Domains tab ─────────────────────────────────────────────────
		// The primary domain is implicit (`client.url`) and not stored.
		// Additional custom domains live in `clientOptions.custom_domains`
		// as a JSON array of host strings. The Domains tab UI lets the
		// user add/remove and shows DNS instructions per-entry.

		startAddDomain() {
			this.addingDomain = true
			this.newDomain = ''
		},
		cancelAddDomain() {
			this.addingDomain = false
			this.newDomain = ''
		},
		confirmAddDomain() {
			const cleaned = String(this.newDomain || '').trim().toLowerCase().replace(/^https?:\/\//, '').replace(/\/$/, '')
			if (!cleaned) return
			if (!Array.isArray(this.clientOptions.custom_domains)) {
				this.$set(this.clientOptions, 'custom_domains', [])
			}
			// Prevent duplicates (case-insensitive)
			if (this.clientOptions.custom_domains.map(d => d.toLowerCase()).includes(cleaned)) {
				if (this.$toast) this.$toast.warning('Domain already added')
				return
			}
			this.clientOptions.custom_domains.push(cleaned)
			this.addingDomain = false
			this.newDomain = ''
			// Auto-expand the newly added domain so DNS instructions are visible
			this.expandedDomain = this.clientOptions.custom_domains.length - 1
		},
		removeCustomDomain(idx) {
			if (!Array.isArray(this.clientOptions.custom_domains)) return
			this.clientOptions.custom_domains.splice(idx, 1)
			if (this.expandedDomain === idx) {
				this.expandedDomain = null
			} else if (this.expandedDomain !== null && this.expandedDomain > idx) {
				this.expandedDomain -= 1
			}
		},
		toggleDnsInstructions(idx) {
			this.expandedDomain = this.expandedDomain === idx ? null : idx
		},
		// Crude apex/subdomain detector. "example.com" → apex (2 parts).
		// "www.example.com" → subdomain (3+ parts). Doesn't handle .co.uk
		// type multi-part TLDs perfectly but is good enough for the hint.
		isApexDomain(domain) {
			if (!domain) return false
			return String(domain).split('.').length <= 2
		},
		// The "Name" value for the DNS record. For apex it's "@", for
		// subdomains it's just the leading label (or labels).
		dnsRecordName(domain) {
			if (!domain) return '@'
			const parts = String(domain).split('.')
			if (parts.length <= 2) return '@'
			return parts.slice(0, -2).join('.')
		},
		// Strip protocol + trailing slash from the client's PRIMARY URL
		// (`client.url` — set during provisioning, e.g. "cityofjuniper.com").
		// Falls back to staging URL only if no primary is set.
		cleanedClientUrl() {
			const raw = (this.client && this.client.url) || ''
			return raw.replace(/^https?:\/\//, '').replace(/\/$/, '')
		},
		// Same, but for the auto-generated base domain (staging URL —
		// e.g. "jp0572stg.townweb.site"). Shown in the Base domain section.
		cleanedStagingUrl() {
			const raw = (this.client && this.client.staging_url) || ''
			return raw.replace(/^https?:\/\//, '').replace(/\/$/, '')
		},
		copyToClipboard(text) {
			if (navigator.clipboard && navigator.clipboard.writeText) {
				navigator.clipboard.writeText(text).then(
					() => { if (this.$toast) this.$toast.success('Copied to clipboard') },
					() => { if (this.$toast) this.$toast.error('Copy failed') },
				)
			}
		},

		// Fetch the next page and append it. Re-entrant clicks are ignored while
		// a request is in flight, and the page counter only advances on success
		// so a failed request can be retried without skipping a page.
		loadMoreSubscribers() {
			if (this.loadingMoreSubscribers || !this.hasMoreSubscribers) return

			const nextPage = this.subscribersPage + 1
			this.loadingMoreSubscribers = true

			return this.fetchSubscribersPage(nextPage)
				.then(res => {
					this.subscribers = this.subscribers.concat(res.data)
					this.subscribersTotal = this.readTotalHeader(res, this.subscribers.length)
					this.subscribersPage = nextPage
				})
				.catch(error => {
					console.log(error)
				})
				.finally(() => {
					this.loadingMoreSubscribers = false
				})
		},

		// Map a subscriber status string (e.g. "active", "pending", "unsubscribed")
		// to one of the cs-status-pill modifier classes. Unknown statuses fall
		// back to a neutral gray pill.
		statusPillClass(status) {
			const s = String(status || '').toLowerCase()
			if (s === 'active' || s === 'subscribed' || s === 'confirmed') return 'cs-status-pill--active'
			if (s === 'pending' || s === 'unconfirmed') return 'cs-status-pill--pending'
			if (s === 'unsubscribed' || s === 'cancelled' || s === 'canceled') return 'cs-status-pill--inactive'
			if (s === 'failed' || s === 'bounced' || s === 'error') return 'cs-status-pill--error'
			return 'cs-status-pill--neutral'
		},

		cancelAddNewMeetingCategory() {
			this.addMeetingCategoryFlag = false
			this.newMeetingCategory = { name: '' }
		},

		storeNewMeetingCategory() {
			this.storeMeetingCategoryLoading = true
			let jurisdiction = this.clientOptions.heygov_jurisdiction
			if (jurisdiction) {
				juniperApi
					.post(`/meeting-repository-categories/storeHeyGov`, {
						client_id: this.$route.params.id,
						jurisdiction_id: jurisdiction,
						name: this.newMeetingCategory.name })
					.then(() => {
						this.syncHeygovMeetingRepositoryCategories()
						this.storeMeetingCategoryLoading = false
						this.newMeetingCategory = { name: '' }
						this.addMeetingCategoryFlag = false
					})
					.catch(error => {
						console.log(error)
					})
			}
		},

		cancelAddNewNewsCategory() {
			this.addNewsCategoryFlag = false
			this.newNewsCategory = { name: '' }
		},

		storeNewNewsCategory() {
			this.storeCategoryLoading = true
			juniperApi
				.post(`/news-notice-categories`, { client_id: this.$route.params.id, name: this.newNewsCategory.name })
				.then(() => {
					this.getNewsNoticeCategories()
					this.addNewsCategoryFlag = false
					this.newNewsCategory = { name: '' }
					this.storeCategoryLoading = false
				})
				.catch(error => {
					this.storeCategoryLoading = false
					console.log(error)
				})
		},

		getFaqsCategories() {
			juniperApi
				.get(`/faq-categories`, { params: { client_id: this.$route.params.id, orderby: 'order', order: 'asc' } })
				.then(({ data }) => {
					this.faqsCategories = data
				})
				.catch(error => {
					console.log(error)
				})
		},

		cancelAddingFaqsCategory() {
			this.addFaqsCategoryFlag = false
			this.newFaqsCategory = { name: '' }
		},

		storeFaqsCategory() {
			this.storeFaqsCategoryLoading = true
			juniperApi
				.post(`/faq-categories`, { client_id: this.$route.params.id, name: this.newFaqsCategory.name })
				.then(() => {
					this.getFaqsCategories()
					this.addFaqsCategoryFlag = false
					this.newFaqsCategory = { name: '' }
					this.storeFaqsCategoryLoading = false
				})
				.catch(error => {
					this.storeFaqsCategoryLoading = false
					console.log(error)
				})
		},

		getFbPageToken() {
			this.fetchingFbPageToken = true
			juniperApi
				.post(`/facebook/page-token`, { client_id: this.$route.params.id, pageId: this.clientOptions.social_networks_fb_page_id })
				.then(({ data }) => {
					this.fetchingFbPageToken = false
					this.clientOptions.social_networks_fb_page_access_token = data
				})
				.catch(error => {
					this.fetchingFbPageToken = false
					console.log(error)
				})
		},

		// Get client
		importWordpressSubscribers() {
			this.importSubscribersLoading = true
			juniperApi
				.get('/subscribers/import-wordpress', { params: { website: this.wordpressImport.website, clientId: this.$route.params.id } })
				.then(({ data }) => {
					console.log(data)
					this.$swal.fire({
						icon: 'success',
						title: data.message,
						timer: 6000 })
					this.importSubscribersLoading = false
				})
				.catch(error => {
					this.importSubscribersLoading = false
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		updateFaqsCategoriesOrder() {
			juniperApi
				.post('/faq-categories/updateOrder', { categories: this.faqsCategories, client_id: this.$route.params.id })
				.catch(error => {
					this.$swal.fire({ icon: 'error', title: error.message, timer: 6000 })
				})
		},

		updateMeetingCategoriesOrder() {
			juniperApi
				.post('/meeting-repository-categories/updateOrder', { categories: this.meetingRepositoryCategories, clientId: this.$route.params.id })
				.then(() => {})
				.catch(error => {
					this.importSubscribersLoading = false
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},

		showUpdateCategoryOffcanvas(category, type) {
			this.offcanvasCategoriesUpdate.show()
			this.selectedCategory = category
			this.selectedCategoryType = type
		},

		updateCategory() {
			console.log(this.selectedCategory)

			if (this.selectedCategoryType == 'meet') {
				let jurisdiction = this.clientOptions.heygov_jurisdiction
				if (jurisdiction) {
					juniperApi
						.post(`/meeting-repository-categories/updateHeyGov`, {
							client_id: this.$route.params.id,
							jurisdiction_id: jurisdiction,
							name: this.selectedCategory.name,
							slug: this.selectedCategory.slug,
							id: this.selectedCategory.id })
						.then(() => {
							this.syncHeygovMeetingRepositoryCategories()
							this.selectedCategory = null
							this.offcanvasCategoriesUpdate.hide()
						})
						.catch(error => {
							console.log(error)
						})
				}
			} else if (this.selectedCategoryType == 'news') {
				juniperApi
					.put(`/news-notice-categories/${this.selectedCategory.id}`, {
						client_id: this.$route.params.id,
						name: this.selectedCategory.name,
						slug: this.selectedCategory.slug })
					.then(() => {
						this.getNewsNoticeCategories()
						this.selectedCategory = null
						this.offcanvasCategoriesUpdate.hide()
					})
					.catch(error => {
						console.log(error)
					})
			} else if (this.selectedCategoryType == 'faqs') {
				juniperApi
					.put(`/faq-categories/${this.selectedCategory.id}`, {
						client_id: this.$route.params.id,
						name: this.selectedCategory.name,
						slug: this.selectedCategory.slug })
					.then(() => {
						this.getFaqsCategories()
						this.selectedCategory = null
						this.offcanvasCategoriesUpdate.hide()
					})
					.catch(error => {
						console.log(error)
					})
			}
		},

		deleteCategory(category, type) {
			this.$swal({
				title: 'Are you sure?',
				showCancelButton: true,
				icon: 'warning',
				text: `Are you sure you want to delete category ${category.name}?`,
				confirmButtonText: 'Yes, delete it!',
				showLoaderOnConfirm: true,
				toast: false,
				position: 'center',
				showConfirmButton: true,
				timer: undefined,
				timerProgressBar: false,
				confirmButtonColor: '#0FCB4F',
				cancelButtonColor: '#EC2641',
				preConfirm: async () => {
					try {
						let res
						if (type == 'meet') {
							res = await juniperApi.delete(`/meeting-repository-categories/${category.id}`, {
								data: { client_id: this.$route.params.id, jurisdiction_id: this.clientOptions.heygov_jurisdiction } })
						} else if (type == 'news') {
							res = await juniperApi.delete(`/news-notice-categories/${category.id}`, {
								data: { client_id: this.$route.params.id } })
						} else if (type == 'faqs') {
							res = await juniperApi.delete(`/faq-categories/${category.id}`, {
								data: { client_id: this.$route.params.id } })
						}

						return res
					} catch (error) {
						this.$swal.showValidationMessage(`Request failed: ${error}`)
					}
				},
				allowOutsideClick: () => !this.$swal.isLoading() }).then(async result => {
				if (result.isConfirmed) {
					if (result.value && result.value.data == 'success') {
						if (type == 'meet') {
							this.getMeetingRepositoryCategories()
						} else if (type == 'news') {
							this.getNewsNoticeCategories()
						} else if (type == 'faqs') {
							this.getFaqsCategories()
						}
						this.$swal('Deleted!', `Category ${category.name} has been deleted permanently!`, 'success').then(() => {})
					} else {
						this.$swal('Request failed!', "There was a problem, category wasn't deleted.", 'error')
					}
				}
			})
		},

		//
		getThemeTemplates() {
			juniperApi
				.post('/themes/' + this.$route.params.id + '/theme-templates')
				.then(({ data }) => {
					console.log('templates')
					console.log(data)
					this.departmentsSingleTemplatesAvailable = data.departments.single
					this.departmentsArchiveTemplatesAvailable = data.departments.archive
					this.newsSingleTemplatesAvailable = data.news.single
					this.newsArchiveTemplatesAvailable = data.news.archive
					this.faqsSingleTemplatesAvailable = data.faqs.single
					this.faqsArchiveTemplatesAvailable = data.faqs.archive
					this.themeSupportsDepartmentDocuments = data.theme.config.additional_options?.departmentDocuments
					this.themeSupportsDepartmentQuickMenu = data.theme.config.additional_options?.departmentQuickMenu
				})
				.catch(error => {
					this.importSubscribersLoading = false
					this.$swal.fire({
						icon: 'error',
						title: error.message,
						timer: 6000 })
					console.log(error)
				})
		},
		copyTurnstileCode() {
			const code = `<div class="cf-turnstile"
     data-sitekey="${this.clientOptions.security_turnstile_site_key}">
</div>`
			navigator.clipboard
				.writeText(code)
				.then(() => {
					this.$swal.fire({
						icon: 'success',
						title: 'Copied to clipboard!',
						timer: 2000,
						showConfirmButton: false })
				})
				.catch(err => {
					console.error('Failed to copy: ', err)
					this.$swal.fire({
						icon: 'error',
						title: 'Failed to copy',
						timer: 2000,
						showConfirmButton: false })
				})
		} } }
</script>
<style scoped>
#clientSettings {
	padding: 0 0 60px;
	margin-top: 0 !important;
}

/* ─── Restore (rollback) section ─────────────────────────────────────── */
.cs-restore-warn {
	display: flex;
	gap: 10px;
	align-items: flex-start;
	background: #fffbeb;
	border: 1px solid #fde68a;
	color: #92400e;
	border-radius: 10px;
	padding: 10px 12px;
	font-size: 13px;
	margin-bottom: 14px;
}
.cs-restore-warn > i {
	margin-top: 2px;
}
.cs-restore-hint {
	color: #9ca3af;
	font-size: 13px;
	padding: 6px 0;
}
.cs-restore-list {
	list-style: none;
	padding: 0;
	margin: 0;
}
.cs-restore-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 12px;
	padding: 10px 0;
	border-bottom: 1px solid #f1f3f5;
}
.cs-restore-item:last-child {
	border-bottom: none;
}
.cs-restore-meta {
	font-size: 13px;
	color: #374151;
}
.cs-restore-meta > i {
	color: #9ca3af;
	margin-right: 6px;
}
.cs-restore-meta small {
	color: #9ca3af;
}
.cs-restore-done {
	color: #16a34a !important;
}
.cs-restore-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	background: #fff;
	border: 1px solid #f59e0b;
	color: #b45309;
	border-radius: 8px;
	padding: 5px 12px;
	font-size: 13px;
	font-weight: 600;
	cursor: pointer;
	flex-shrink: 0;
}
.cs-restore-btn:hover { background: #fffbeb; }
.cs-restore-btn:disabled { opacity: 0.5; cursor: not-allowed; }

.cs-modal-backdrop {
	position: fixed;
	inset: 0;
	z-index: 1090;
	background: rgba(15, 23, 42, 0.45);
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 20px;
}
.cs-modal {
	background: #fff;
	border-radius: 16px;
	padding: 26px;
	width: 100%;
	max-width: 440px;
	text-align: center;
	box-shadow: 0 20px 50px rgba(15, 23, 42, 0.25);
}
.cs-modal__icon {
	font-size: 32px;
	color: #d97706;
	margin-bottom: 8px;
}
.cs-modal__title {
	font-weight: 700;
	margin: 0 0 6px;
}
.cs-modal__msg {
	color: #4b5563;
	font-size: 14px;
	margin: 0 0 16px;
}
.cs-modal__actions {
	display: flex;
	justify-content: center;
	gap: 10px;
}
.cs-modal__cancel {
	background: #fff !important;
	color: #475569 !important;
	border: 1px solid #e0e1e1 !important;
}
.cs-restore-confirm {
	display: inline-flex;
	align-items: center;
	background: #dc2626;
	color: #fff;
	border: none;
	border-radius: 10px;
	padding: 8px 18px;
	font-weight: 600;
	cursor: pointer;
}
.cs-restore-confirm:hover { background: #b91c1c; }
.cs-restore-confirm:disabled { opacity: 0.6; cursor: not-allowed; }

.jc-view {
	width: 100%;
}

.jcs-top-tabs {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	border: 0;
	padding: 6px;
	border-radius: 18px;
	background: #eceef2;
	margin-bottom: 0;
}

.jcs-top-tabs .nav-link {
	border: 0;
	border-radius: 14px;
	padding: 9px 14px;
	font-weight: 600;
	font-size: 13px;
	color: #5c6474;
	background: transparent;
	line-height: 1.2;
	box-shadow: none;
}

.jcs-top-tabs .nav-link.active {
	background: #fff;
	color: #111827;
	box-shadow: 0 1px 2px rgba(17, 24, 39, 0.1);
}

.jcs-top-tabs .nav-link:not(.active) {
	border: 0 !important;
	box-shadow: none !important;
}

.jcs-top-tabs .nav-link:hover {
	color: #111827;
	background: rgba(255, 255, 255, 0.6);
	border: 0;
	box-shadow: none;
}

.jcs-top-tabs .nav-link:focus-visible {
	outline: 2px solid #93c5fd;
	outline-offset: 2px;
}

.wordpress-subscribers-improt {
	padding: 0.75rem;
	border-radius: 1rem;
	border: 1px solid #eee;
}
</style>

<style scoped lang="scss">
$gh-border: #e6e8ec;
$gh-border-soft: #f0f1f4;
$gh-text: #2b303b;
$gh-text-soft: #4a5260;
$gh-text-muted: #8a909c;
$gh-bg: #fff;
$gh-bg-soft: #f8f9fb;
$gh-bg-hover: #f3f5f9;
$gh-accent: #2b6cff;
$gh-accent-soft: #ebf2ff;
$gh-success: #1ec872;
$gh-danger: #c93434;
$gh-danger-soft: #ffe9e9;

.js-tab-content {
	padding: 15px;
}

.gh-layout {
	display: grid;
	grid-template-columns: 30% 1fr;
	gap: 1.5rem;
	align-items: start;
}

::v-deep .card {
	background-color: transparent !important;
	border-color: $gh-border;
	border-radius: 14px;
}

::v-deep .card-header {
	background-color: transparent !important;
	border-bottom-color: $gh-border-soft;
}

.gh-intro {
	padding: 0.25rem 1rem 0 0;
}

.gh-intro__title {
	font-size: 1.5rem;
	font-weight: 700;
	color: $gh-text;
	margin: 0 0 0.5rem;
	letter-spacing: -0.01em;
}

/* Compact horizontal row for small controls (number inputs, narrow selects,
   toggles) that don't deserve full card width. Sits visually below the main
   form fields, separated by a hairline border. Children use one of three
   width modifiers (--xs ~5rem, --sm ~10rem, --md ~14rem). The toggle pushes
   to the right edge of the row. */

.cs-compact-row {
	display: flex;
	flex-wrap: wrap;
	align-items: flex-end;
	gap: 0.85rem 1rem;
	margin-top: 1.1rem;
	padding-top: 1rem;
	border-top: 1px solid $gh-border-soft;
}

.cs-compact-field {
	display: flex;
	flex-direction: column;
	min-width: 0;
}

.cs-compact-field--xs { width: 5rem; }
.cs-compact-field--sm { width: 10rem; }
.cs-compact-field--md { width: 14rem; }
.cs-compact-field--lg { width: 18rem; }

.cs-compact-field label {
	font-size: 0.72rem;
	font-weight: 600;
	color: $gh-text-soft;
	margin-bottom: 0.3rem;
}

.cs-compact-field .form-control,
.cs-compact-field .form-select {
	font-size: 0.9rem;
	padding: 0.4rem 0.65rem;
	border-radius: 8px;
	border-color: $gh-border;
}

.cs-compact-toggle {
	display: inline-flex;
	align-items: center;
	gap: 0.5rem;
	margin: 0 0 0 auto;
	padding-bottom: 0.45rem;
	cursor: pointer;
	user-select: none;
}

.cs-compact-toggle .form-check-input {
	margin: 0;
}

.cs-compact-toggle .form-check-label {
	font-size: 0.88rem;
	font-weight: 500;
	color: $gh-text;
	margin: 0;
	cursor: pointer;
}

/* ─── Domain rows + DNS record card (Domains tab) ─────────────────────
   Card-row per mapped hostname with monospace name + chevron toggle for
   DNS instructions + trash for delete. Locked variant (--locked) for the
   primary domain (client.url) — no chevron, no delete, soft background. */

.cs-domain-row {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.65rem 0.9rem;
	border: 1px solid $gh-border-soft;
	border-radius: 10px;
	background: white;
	transition: border-color 0.15s ease, background 0.15s ease;
}

.cs-domain-row:hover:not(.cs-domain-row--locked) {
	border-color: $gh-border;
}

.cs-domain-row--expanded {
	border-color: lighten($gh-accent, 25%);
}

.cs-domain-row--locked {
	background: $gh-bg-soft;
}

.cs-domain-row__icon {
	color: $gh-text-soft;
	flex-shrink: 0;
}

.cs-domain-row--locked .cs-domain-row__icon {
	color: $gh-text-muted;
}

.cs-domain-row__name {
	flex: 1;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
	font-size: 0.9rem;
	color: $gh-text;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.cs-domain-row__meta {
	font-size: 0.7rem;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: $gh-text-muted;
}

.cs-domain-row__expand,
.cs-domain-row__delete {
	width: 30px;
	height: 30px;
	border-radius: 7px;
	border: none;
	background: transparent;
	color: $gh-text-soft;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: 0.8rem;
	transition: background 0.12s ease, color 0.12s ease;
}

.cs-domain-row__expand:hover {
	background: rgba(15, 23, 42, 0.06);
	color: $gh-text;
}

.cs-domain-row__delete:hover {
	background: rgba(220, 38, 38, 0.1);
	color: #dc2626;
}

/* DNS record table — definition list rendered as 2-column grid:
   Type / Name / Value / TTL on the left, values on the right. */
.cs-dns-record {
	display: grid;
	grid-template-columns: 5rem 1fr;
	gap: 0.5rem 1rem;
	margin: 0.75rem 0 0;
	padding: 0.85rem 1rem;
	background: white;
	border: 1px solid lighten($gh-accent, 28%);
	border-radius: 8px;
	font-size: 0.85rem;
}

.cs-dns-record dt {
	font-size: 0.72rem;
	font-weight: 700;
	letter-spacing: 0.05em;
	text-transform: uppercase;
	color: $gh-text-muted;
	align-self: center;
	margin: 0;
}

.cs-dns-record dd {
	margin: 0;
	color: $gh-text;
	align-self: center;
	min-width: 0;
}

.cs-dns-record code {
	background: $gh-bg-soft;
	padding: 0.15rem 0.5rem;
	border-radius: 5px;
	font-size: 0.82rem;
	color: $gh-text;
	border: 1px solid $gh-border-soft;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
}

.cs-dns-record__value {
	display: flex;
	align-items: center;
	gap: 0.5rem;
}

.cs-dns-record__copy {
	width: 26px;
	height: 26px;
	border-radius: 6px;
	border: 1px solid $gh-border-soft;
	background: white;
	color: $gh-text-soft;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: 0.72rem;
	transition: background 0.12s ease, color 0.12s ease;
}

.cs-dns-record__copy:hover {
	background: $gh-accent-soft;
	color: $gh-accent;
}

.cs-dns-record__note {
	grid-column: 1 / -1;
	margin: 0.4rem 0 0;
	color: $gh-text-muted;
	font-size: 0.78rem;
}

/* ─── Tip callout (reusable info banner) ─────────────────────────────
   Modern replacement for the old Bootstrap `.alert .alert-primary` info
   boxes. Round icon avatar on the left, body text on the right; sits
   inside a soft accent-tinted card with a tight border. Use for
   educational hints inside a section. Inline <code> and <em> get
   subtle treatment so they read well against the soft background. */
.cs-tip {
	display: flex;
	align-items: flex-start;
	gap: 0.85rem;
	padding: 0.9rem 1rem;
	background: $gh-accent-soft;
	border: 1px solid lighten($gh-accent, 28%);
	border-radius: 10px;
	margin-bottom: 1.1rem;
}

.cs-tip__icon {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: linear-gradient(135deg, $gh-accent 0%, lighten($gh-accent, 12%) 100%);
	color: white;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 0.85rem;
	box-shadow: 0 1px 2px rgba(43, 108, 255, 0.2);
}

.cs-tip__body {
	flex: 1;
	font-size: 0.85rem;
	line-height: 1.5;
	color: $gh-text;
	min-width: 0;
}

.cs-tip__body strong {
	font-weight: 700;
	color: $gh-text;
}

.cs-tip__body em {
	font-style: italic;
	color: $gh-accent;
}

.cs-tip__body code {
	background: white;
	padding: 0.05rem 0.4rem;
	border-radius: 4px;
	font-size: 0.78rem;
	border: 1px solid lighten($gh-accent, 30%);
}

.cs-tip__body p {
	margin: 0;
	font-size: 0.85rem;
	color: $gh-text;
}

/* Warning-toned variant of .cs-tip — used for deprecation notices and
   other "heads up" messages where the user shouldn't miss it. */
.cs-tip--warning {
	background: #fffbeb;
	border-color: #fde68a;
}

.cs-tip--warning .cs-tip__icon {
	background: linear-gradient(135deg, #d97706 0%, #f59e0b 100%);
	box-shadow: 0 1px 2px rgba(217, 119, 6, 0.25);
}

.cs-tip--warning .cs-tip__body em {
	color: #d97706;
	font-style: normal;
	font-weight: 600;
}

.cs-tip--warning .cs-tip__body code {
	border-color: #fde68a;
}

/* ─── Legacy block ──────────────────────────────────────────────────────
   Wraps settings that are on their way out (currently the theme-generated
   events archive: prefix + archive template). Visually set apart with a
   hatched, desaturated frame so nobody mistakes it for current config. Only
   rendered while the replacement is not yet in place. */
.cs-legacy {
	border: 1px dashed #d3d7e0;
	border-radius: 12px;
	background: repeating-linear-gradient(
		135deg,
		#fafbfc,
		#fafbfc 10px,
		#f4f5f8 10px,
		#f4f5f8 20px
	);
	margin: 1.4rem 0 0.4rem;
	overflow: hidden;
}

.cs-legacy__head {
	display: flex;
	align-items: flex-start;
	gap: 0.85rem;
	padding: 0.9rem 1rem;
}

.cs-legacy__icon {
	flex-shrink: 0;
	width: 28px;
	height: 28px;
	border-radius: 50%;
	background: linear-gradient(135deg, #6b7280 0%, #9ca3af 100%);
	color: #fff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 0.8rem;
}

.cs-legacy__text {
	flex: 1;
	min-width: 0;
	display: flex;
	flex-direction: column;
	gap: 0.15rem;
}

.cs-legacy__text strong {
	display: flex;
	align-items: center;
	gap: 0.5rem;
	font-size: 0.9rem;
	font-weight: 700;
	color: $gh-text-soft;
}

.cs-legacy__text small {
	font-size: 0.8rem;
	line-height: 1.45;
	color: $gh-text-muted;
}

.cs-legacy__badge {
	flex-shrink: 0;
	padding: 0.1rem 0.45rem;
	border-radius: 999px;
	background: #fef3c7;
	border: 1px solid #fde68a;
	color: #92400e;
	font-size: 0.68rem;
	font-weight: 700;
	letter-spacing: 0.02em;
	text-transform: uppercase;
}

/* The wrapped settings themselves: dimmed, and the hatching hidden behind a
   flat surface so the fields stay readable while expanded. */
.cs-legacy__body {
	padding: 0.25rem 1rem 1.1rem;
	background: rgba(255, 255, 255, 0.75);
	border-top: 1px dashed #dfe3ea;
	opacity: 0.9;
}

/* ─── Replaced-state empty view ─────────────────────────────────────────
   Used when an entire tab's content is no longer applicable (e.g. Meetings
   under Unified Dashboard) and we want to show a single centered card
   explaining what to do instead. Larger and more prominent than .cs-tip. */
.cs-replaced-state {
	display: flex;
	flex-direction: column;
	align-items: center;
	text-align: center;
	padding: 3rem 2rem;
	background: $gh-bg-soft;
	border: 1px solid $gh-border;
	border-radius: 14px;
	max-width: 580px;
	margin: 2rem auto;
}

.cs-replaced-state__icon {
	width: 60px;
	height: 60px;
	border-radius: 50%;
	background: linear-gradient(135deg, $gh-accent 0%, lighten($gh-accent, 12%) 100%);
	color: white;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 1.5rem;
	margin-bottom: 1.25rem;
	box-shadow: 0 4px 12px rgba(43, 108, 255, 0.25);
}

.cs-replaced-state__title {
	font-size: 1.2rem;
	font-weight: 700;
	color: $gh-text;
	margin: 0 0 0.75rem;
	letter-spacing: -0.01em;
}

.cs-replaced-state__desc {
	font-size: 0.9rem;
	color: $gh-text-soft;
	line-height: 1.55;
	margin: 0;
	max-width: 480px;
}

/* Inline link inside a .cs-tip body — accent color, underline on hover */
.cs-tip__link {
	display: inline-flex;
	align-items: center;
	margin-top: 0.65rem;
	font-size: 0.82rem;
	font-weight: 600;
	color: $gh-accent;
	text-decoration: none;
	transition: color 0.12s ease;
}

.cs-tip__link:hover {
	color: darken($gh-accent, 10%);
	text-decoration: underline;
}

/* Code block with inline copy button — used inside .cs-tip when you need
   to show a paste-this snippet. Dark background, monospace, copy button
   pinned to top-right corner. */
.cs-code-block {
	position: relative;
	background: #1f2937;
	color: #f1f5f9;
	border-radius: 8px;
	padding: 0.7rem 0.85rem;
	padding-right: 4.5rem;
	font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, monospace;
	font-size: 0.78rem;
	line-height: 1.45;
	overflow-x: auto;
	white-space: nowrap;
}

.cs-code-block code {
	background: transparent;
	border: none;
	padding: 0;
	color: inherit;
	font-size: inherit;
}

.cs-code-block__copy {
	position: absolute;
	top: 0.4rem;
	right: 0.4rem;
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	background: rgba(255, 255, 255, 0.1);
	border: 1px solid rgba(255, 255, 255, 0.18);
	color: #cbd5e1;
	font-size: 0.72rem;
	font-weight: 500;
	padding: 0.25rem 0.55rem;
	border-radius: 6px;
	cursor: pointer;
	transition: background 0.12s ease, color 0.12s ease, border-color 0.12s ease;
}

.cs-code-block__copy:hover {
	background: rgba(255, 255, 255, 0.2);
	color: white;
	border-color: rgba(255, 255, 255, 0.3);
}

/* ─── Count badge next to a section title ─────────────────────────────
   Small numeric pill that follows an intro title, e.g.
   "Subscribers list 〔42〕". Uses accent palette so it reads as a stat. */
.cs-count-badge {
	display: inline-block;
	vertical-align: middle;
	margin-left: 0.5rem;
	padding: 0.18rem 0.55rem;
	font-size: 0.75rem;
	font-weight: 700;
	line-height: 1;
	color: $gh-accent;
	background: $gh-accent-soft;
	border-radius: 999px;
	letter-spacing: 0;
	position: relative;
	top: -0.25em;
}

/* ─── Subscribers table ───────────────────────────────────────────────
   Same shell as the Deploy page's "Recent Deployments" list: the table sits
   flush against a bordered card (no card padding), with the row count and
   "Load more" in a tinted footer bar. Declarations mirror .cd-table in
   ClientDeploy.vue — the markup also carries Bootstrap's .table class, as
   there, so the two render identically. */

.cs-subs-table {
	font-size: 13px;
	margin-bottom: 0;
}

.cs-subs-table thead th {
	font-size: 13px;
	font-weight: 600;
	text-transform: none;
	letter-spacing: 0;
	color: #6c757d;
	text-align: left;
	padding: 4px 10px 4px 0.5rem;
	border-bottom: 1px solid #dee2e6;
	background: transparent;
}

.cs-subs-table tbody td {
	padding: 6px 10px 6px 0.5rem;
	color: $gh-text;
	border-color: #f1f3f5;
	vertical-align: middle;
}

.cs-subs-table tbody tr:last-child td {
	border-bottom: none;
}

.cs-subs-table tbody tr:hover td {
	background: #eef2f7;
}

/* Name is the row's primary identifier — same weight treatment as the
   report name column in the audits table. */
.cs-subs-table__primary {
	font-weight: 600;
	color: $gh-text-soft;
	word-break: break-word;
}

.cs-subs-table__status-col {
	width: 6.5rem;
	white-space: nowrap;
}

/* Empty state — plain centered text inside the flush card, matching
   .cd-empty rather than the dashed box used elsewhere on this page. */
.cs-table-empty {
	padding: 2rem 1rem;
	text-align: center;
	font-size: 0.85rem;
	color: $gh-text-muted;
}

/* Footer bar — tinted strip carrying "Load more" and the N of M count. */
.cs-table-footer {
	display: flex;
	justify-content: center;
	align-items: center;
	gap: 0.75rem;
	padding: 0.65rem 1rem;
	border-top: 1px solid $gh-border-soft;
	background: $gh-bg-soft;
}

/* Status pill — color modifier based on subscriber state */
.cs-status-pill {
	display: inline-block;
	padding: 0.18rem 0.55rem;
	font-size: 0.72rem;
	font-weight: 600;
	line-height: 1;
	border-radius: 999px;
	text-transform: capitalize;
	letter-spacing: 0.01em;
	white-space: nowrap;
}

.cs-status-pill--active {
	color: #15803d;
	background: #dcfce7;
}

.cs-status-pill--pending {
	color: #a16207;
	background: #fef3c7;
}

.cs-status-pill--inactive {
	color: $gh-text-muted;
	background: $gh-bg-hover;
}

.cs-status-pill--error {
	color: #b91c1c;
	background: #fee2e2;
}

.cs-status-pill--neutral {
	color: $gh-text-soft;
	background: $gh-border-soft;
}

/* Plain text action — no border/background/padding, gray like the delete
   icon in the Hooks "Recent activity" table. Mirrors .cd-link-btn on the
   Deploy page. */
.cs-load-more-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	background: none;
	border: 0;
	padding: 0;
	color: #adb5bd;
	font-size: 0.85rem;
	font-weight: 600;
	cursor: pointer;
	transition: color 0.12s ease;
}

.cs-load-more-btn:hover:not(:disabled) {
	color: $gh-text;
}

.cs-load-more-btn:disabled {
	opacity: 0.55;
	cursor: default;
}

/* ─── Subscribers Basic — pill-tab templates (full-width below Prefix/Mail From) ─────
   The Templates block sits below the prefix/mail-from fields. A pill-tab
   switcher chooses between Subscription (sign-up confirmation) and Alert
   (outgoing notification) templates. Active tab lifts white off a soft-gray
   track; only the active tab's panel is shown. */

.cs-sub-templates {
	margin-top: 0.5rem;
	padding-top: 1.1rem;
	border-top: 1px solid $gh-border-soft;
}

.cs-sub-tabs__hint {
	font-size: 0.78rem;
	color: $gh-text-muted;
	margin: 0 0 0.85rem;
	line-height: 1.45;
}

/* Pill-style segmented control (mimics iOS-style tab switcher).
   Inactive tabs sit transparent on the soft-gray track; the active tab
   gets a white "lifted" background and accent text. */
.cs-sub-tabs {
	display: inline-flex;
	gap: 0.2rem;
	margin-bottom: 1rem;
	padding: 0.25rem;
	background: $gh-bg-soft;
	border-radius: 10px;
	border: 1px solid $gh-border-soft;
}

.cs-sub-tabs__tab {
	display: inline-flex;
	align-items: center;
	gap: 0.3rem;
	border: none;
	background: transparent;
	color: $gh-text-soft;
	font-size: 0.85rem;
	font-weight: 600;
	padding: 0.4rem 0.9rem;
	border-radius: 7px;
	cursor: pointer;
	transition: background 0.12s ease, color 0.12s ease, box-shadow 0.12s ease;
}

.cs-sub-tabs__tab:hover {
	color: $gh-text;
}

.cs-sub-tabs__tab--active {
	background: white;
	color: $gh-accent;
	box-shadow: 0 1px 3px rgba(15, 23, 42, 0.08);
}

.cs-sub-tabs__tab--active:hover {
	color: $gh-accent;
}

/* ─── Template help — info icon + modal ───────────────────────────────
   Each mail/SMS template field carries a borderless info icon in its
   label. Hover shows a Bootstrap tooltip; click opens the help modal
   with the API-side default and the supported variables. */
.cs-tpl-info {
	border: none;
	background: none;
	padding: 0;
	margin-left: 0.35rem;
	color: #adb5bd;
	font-size: 0.85rem;
	line-height: 1;
	cursor: pointer;
	transition: color 0.12s ease;
}

.cs-tpl-info:hover {
	color: $gh-accent;
}

/* Wider than the confirm dialog — the default templates are multi-line
   and the variable table needs room for its description column. */
.cs-modal--wide {
	max-width: 660px;
	text-align: left;
	max-height: 88vh;
	overflow-y: auto;
}

.cs-tplmodal__head {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: 1rem;
}

.cs-tplmodal__title {
	font-weight: 700;
	margin: 0;
	font-size: 1.02rem;
}

.cs-tplmodal__close {
	border: none;
	background: none;
	padding: 0;
	color: #adb5bd;
	font-size: 1rem;
	line-height: 1;
	cursor: pointer;
}

.cs-tplmodal__close:hover {
	color: $gh-text;
}

.cs-tplmodal__desc {
	color: $gh-text-soft;
	font-size: 0.83rem;
	line-height: 1.5;
	margin: 0.5rem 0 0;
}

.cs-tplmodal__section {
	margin-top: 1.15rem;
	padding-top: 1.15rem;
	border-top: 1px solid $gh-border-soft;
}

.cs-tplmodal__label {
	font-size: 0.7rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.04em;
	color: $gh-text-muted;
	margin-bottom: 0.5rem;
}

.cs-tplmodal__hint {
	text-transform: none;
	letter-spacing: 0;
	font-weight: 500;
	margin-left: 0.35rem;
}

.cs-tplmodal__code {
	background: $gh-bg-soft;
	border: 1px solid $gh-border-soft;
	border-radius: 8px;
	padding: 0.7rem 0.85rem;
	margin: 0 0 0.7rem;
	font-size: 0.76rem;
	line-height: 1.55;
	white-space: pre-wrap;
	word-break: break-word;
	color: $gh-text;
}

.cs-tplmodal__vars {
	width: 100%;
	border-collapse: collapse;
	font-size: 0.78rem;
}

.cs-tplmodal__vars td {
	padding: 0.4rem 0.5rem 0.4rem 0;
	border-top: 1px solid #f1f3f5;
	vertical-align: top;
	color: $gh-text-soft;
	line-height: 1.45;
}

.cs-tplmodal__vars tr:first-child td {
	border-top: none;
}

.cs-tplmodal__vars td:first-child {
	white-space: nowrap;
	width: 1%;
	padding-right: 0.9rem;
}

.cs-tplmodal__vars code {
	background: $gh-bg-soft;
	border-radius: 5px;
	padding: 0.1rem 0.35rem;
	font-size: 0.76rem;
	color: $gh-accent;
}

.cs-tplmodal__note {
	display: flex;
	gap: 0.45rem;
	margin: 0.85rem 0 0;
	padding: 0.55rem 0.7rem;
	border-radius: 8px;
	background: #fffbeb;
	border: 1px solid #fde68a;
	color: #92400e;
	font-size: 0.76rem;
	line-height: 1.5;
}

/* ─── Toggle grid (used for Meetings table-column switches) ────────────
   Responsive grid of switch-toggle "cells" that auto-arrange in 2–3
   columns based on viewport width. Each cell is a tappable row with
   the switch + label side-by-side. */
.cs-toggle-grid {
	display: grid;
	grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
	gap: 0.4rem 1rem;
}

.cs-toggle-cell {
	display: flex;
	align-items: center;
	gap: 0.55rem;
	padding: 0.45rem 0.65rem;
	margin: 0;
	border-radius: 8px;
	cursor: pointer;
	transition: background 0.12s ease;
	user-select: none;
}

.cs-toggle-cell:hover {
	background: $gh-bg-hover;
}

.cs-toggle-cell .form-check-input {
	margin: 0;
	flex-shrink: 0;
}

.cs-toggle-cell .form-check-label {
	font-size: 0.88rem;
	color: $gh-text;
	margin: 0;
	cursor: pointer;
}

/* ─── Category list with drag-and-drop ───────────────────────────────
   Modern row-card pattern. Drag handle on the left (lighter when idle,
   prominent on hover/grab). Toggle switch in the middle reads as the
   subscribable toggle. Edit / Delete actions on the right, hidden by
   default and revealed on row hover for a quieter resting state. */

.cs-cat-list {
	display: flex;
	flex-direction: column;
	gap: 0.4rem;
}

.cs-cat-row {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.55rem 0.85rem;
	background: $gh-bg-soft;
	border: 1px solid $gh-border-soft;
	border-radius: 10px;
	transition: border-color 0.15s ease, background 0.15s ease, box-shadow 0.15s ease;
}

.cs-cat-row:hover {
	border-color: $gh-border;
	background: white;
	box-shadow: 0 1px 3px rgba(15, 23, 42, 0.06);
}

.cs-cat-row.sortable-ghost {
	opacity: 0.4;
	background: $gh-accent-soft;
	border-color: lighten($gh-accent, 25%);
}

.cs-cat-row__handle {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 18px;
	height: 18px;
	color: #cbd5e1;
	cursor: grab;
	transition: color 0.15s ease;
	flex-shrink: 0;
}

.cs-cat-row:hover .cs-cat-row__handle {
	color: $gh-text-soft;
}

.cs-cat-row__handle:active {
	cursor: grabbing;
}

.cs-cat-row__toggle {
	flex: 1;
	display: flex;
	align-items: center;
	gap: 0.55rem;
	margin: 0;
	padding-left: 0;
	cursor: pointer;
	min-width: 0;
}

.cs-cat-row__toggle .form-check-input {
	margin: 0;
	flex-shrink: 0;
}

.cs-cat-row__name {
	font-size: 0.92rem;
	color: $gh-text;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.cs-cat-row__actions {
	display: flex;
	gap: 0.2rem;
	opacity: 0;
	transition: opacity 0.15s ease;
}

.cs-cat-row:hover .cs-cat-row__actions {
	opacity: 1;
}

.cs-cat-row__action {
	width: 28px;
	height: 28px;
	border-radius: 7px;
	border: none;
	background: transparent;
	color: $gh-text-soft;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	font-size: 0.78rem;
	transition: background 0.12s ease, color 0.12s ease;
}

.cs-cat-row__action:hover {
	background: rgba(15, 23, 42, 0.07);
	color: $gh-text;
}

.cs-cat-row__action--danger:hover {
	background: rgba(220, 38, 38, 0.1);
	color: #dc2626;
}

/* ─── Add-category button (header-aligned, neutral) ──────────────────── */
.cs-add-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.4rem;
	background: transparent;
	border: 1px dashed $gh-border;
	color: $gh-text-soft;
	font-size: 0.85rem;
	font-weight: 600;
	padding: 0.35rem 0.85rem;
	border-radius: 999px;
	cursor: pointer;
	transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.cs-add-btn:hover {
	background: $gh-accent-soft;
	border-color: lighten($gh-accent, 25%);
	border-style: solid;
	color: $gh-accent;
}

/* Inline add-form (shows when "Add category" pressed) */
.cs-add-form {
	margin-top: 1rem;
	padding: 1rem;
	background: white;
	border: 1px solid $gh-border;
	border-radius: 10px;
}

/* Empty state inside categories card */
.cs-empty-state {
	padding: 1.5rem 1rem;
	text-align: center;
	font-size: 0.85rem;
	color: $gh-text-muted;
	border: 1px dashed $gh-border;
	border-radius: 10px;
	background: $gh-bg-soft;
}

/* Unified-dashboard banner replacing the categories editor */
.cs-unified-banner {
	display: flex;
	align-items: center;
	gap: 0.75rem;
	padding: 0.85rem 1rem;
	background: $gh-accent-soft;
	border: 1px solid lighten($gh-accent, 25%);
	border-radius: 10px;
	color: $gh-text;
}

.cs-unified-banner strong {
	display: block;
	font-size: 0.92rem;
}

.cs-unified-banner small {
	color: $gh-text-muted;
	font-size: 0.78rem;
}

/* ─── Sync button — modern gradient pill ──────────────────────────────
   Works as <button> or <a> (for outbound "Visit on …" actions). Anchor
   variant strips its default underline + inherits white color. */
.cs-sync-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.55rem;
	background: linear-gradient(135deg, $gh-accent 0%, lighten($gh-accent, 12%) 100%);
	border: none;
	color: white;
	font-size: 0.9rem;
	font-weight: 600;
	padding: 0.55rem 1.2rem;
	border-radius: 999px;
	cursor: pointer;
	text-decoration: none;
	transition: transform 0.12s ease, box-shadow 0.18s ease, opacity 0.12s ease;
	box-shadow: 0 2px 5px rgba(43, 108, 255, 0.25);
}

.cs-sync-btn:hover:not(:disabled) {
	transform: translateY(-1px);
	box-shadow: 0 4px 12px rgba(43, 108, 255, 0.35);
	color: white;
	text-decoration: none;
}

.cs-sync-btn:active:not(:disabled) {
	transform: translateY(0);
}

.cs-sync-btn:disabled {
	opacity: 0.5;
	cursor: not-allowed;
	box-shadow: none;
}

/* Group-name chip that sits AFTER the main intro title, inline.
   Reads as a small chip beside the heading identifying the parent group,
   e.g.  "Templates ⟨News & Notices⟩". Stays vertically aligned with
   the large title via vertical-align + a tiny upward shift. */
.gh-intro__title .gh-intro__badge {
	display: inline-block;
	vertical-align: middle;
	margin-left: 0.45rem;
	padding: 0.22rem 0.6rem;
	font-size: 0.7rem;
	font-weight: 600;
	line-height: 1;
	letter-spacing: 0.02em;
	color: $gh-text-soft;
	background: $gh-bg-hover;
	border-radius: 999px;
	white-space: nowrap;
	position: relative;
	top: -0.15em;
}

.gh-intro__desc {
	font-size: 0.85rem;
	color: $gh-text-muted;
	line-height: 1.5;
	margin: 0;

	code {
		background: $gh-bg-hover;
		padding: 0.05rem 0.3rem;
		border-radius: 4px;
		font-size: 0.75rem;
	}
}

.gh-accounts {
	display: flex;
	flex-direction: column;
}

.gh-create-card {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 0.75rem;
	padding: 1.75rem 1.5rem;
	border: 1px solid $gh-border;
	border-radius: 14px;
	background: transparent;
	text-align: center;
}

.gh-create-card__icon {
	font-size: 2.25rem;
	color: #cbd0d8;
}

.gh-create-card__desc {
	font-size: 0.85rem;
	color: $gh-text-muted;
	max-width: 26rem;
	margin: 0;
	line-height: 1.45;
}

.gh-add-link-row {
	display: flex;
	justify-content: center;
	padding: 0.85rem 0 0.25rem;
}

.gh-add-link {
	display: inline-flex;
	align-items: center;
	gap: 0.55rem;
	padding: 0.35rem 0.6rem;
	font-size: 0.95rem;
	font-weight: 600;
	color: $gh-text-muted;
	text-decoration: none;
	border-radius: 8px;
	transition: color 0.15s ease, background 0.15s ease;

	&:hover {
		color: $gh-text;
		background: $gh-bg-hover;
		text-decoration: none;

		.gh-add-link__circle {
			border-color: $gh-text-soft;
			color: $gh-text;
		}
	}

	&:focus-visible {
		outline: 2px solid $gh-accent;
		outline-offset: 2px;
	}
}

.gh-add-link__circle {
	width: 18px;
	height: 18px;
	border-radius: 50%;
	border: 1.5px solid $gh-border;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	color: $gh-text-muted;
	font-size: 0.6rem;
	flex-shrink: 0;
	transition: border-color 0.15s ease, color 0.15s ease;
}

.gh-divider {
	border: none;
	border-top: 1px solid #c8ccd4;
	margin: 1.5rem 15px;
}

.gh-save-row {
	display: flex;
	justify-content: flex-end;
}

.ada-card {
	border: 1px solid $gh-border;
	border-radius: 12px;
	padding: 1.25rem 1.15rem;
	background: transparent;
}

/* Flush variant — drops the padding so a table meets the card border and the
   footer bar spans its full width, as .cd-card does on the Deploy page.
   Must stay after .ada-card: same specificity, so source order decides. */
.ada-card--flush {
	padding: 0;
	overflow: hidden;
}

.gh-save-btn {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	padding: 0.35rem 0.9rem;
	background: transparent;
	border: 1px solid $gh-border;
	border-radius: 10px;
	color: $gh-text;
	font-size: 0.9rem;
	font-weight: 600;
	cursor: pointer;
	transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;

	&:hover:not(:disabled) {
		background: $gh-bg-hover;
		border-color: darken($gh-border, 6%);
	}

	&:focus-visible {
		outline: 2px solid $gh-accent;
		outline-offset: 2px;
	}

	&:disabled {
		opacity: 0.55;
		cursor: not-allowed;
	}
}

/* Compact variant for buttons inside a card or modal. The shared
   _settings-shell.scss copy is namespaced under .settings-shell, which this
   view is not nested in, so the modifier is declared locally too. */
.gh-save-btn--sm {
	padding: 0.25rem 0.6rem;
	font-size: 0.78rem;
	border-radius: 7px;
	gap: 0.3rem;
}

.gh-accordion {
	list-style: none;
	margin: 0;
	padding: 0;
	display: flex;
	flex-direction: column;
	gap: 0.6rem;
}

.gh-accordion__item {
	border: 1px solid $gh-border;
	border-radius: 12px;
	overflow: hidden;
	background: transparent;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;

	&--open {
		border-color: lighten($gh-accent, 25%);
		box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.08);
	}
}

.gh-accordion__head {
	width: 100%;
	display: flex;
	align-items: center;
	gap: 0.85rem;
	padding: 0.85rem 1rem;
	background: transparent;
	border: none;
	cursor: pointer;
	text-align: left;
	transition: background 0.12s ease;

	&:hover { background: $gh-bg-hover; }
	&:focus-visible { outline: 2px solid $gh-accent; outline-offset: -2px; }
}

.gh-accordion__avatar {
	width: 36px;
	height: 36px;
	border-radius: 50%;
	background: $gh-text;
	color: #fff;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex-shrink: 0;
	font-size: 1rem;
}

.gh-accordion__head-body {
	min-width: 0;
	flex: 1;
}

.gh-accordion__name {
	font-weight: 600;
	font-size: 0.95rem;
	color: $gh-text;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.gh-accordion__meta {
	font-size: 0.78rem;
	color: $gh-text-muted;
	display: flex;
	gap: 0.4rem;
	align-items: center;
	margin-top: 0.1rem;
}

.gh-accordion__sep {
	color: #cbd0d8;
}

.gh-dot {
	width: 8px;
	height: 8px;
	border-radius: 50%;
	flex-shrink: 0;

	&--on { background: $gh-success; }
	&--off { background: #cbd0d8; }
	&--bad { background: $gh-danger; }
	&--pending {
		background: #cbd0d8;
		animation: gh-dot-pulse 1s ease-in-out infinite;
	}
}

@keyframes gh-dot-pulse {
	0%, 100% { opacity: 0.4; }
	50% { opacity: 1; }
}

.gh-accordion__chevron {
	color: $gh-text-muted;
	font-size: 0.8rem;
	transition: transform 0.2s ease;
	flex-shrink: 0;

	.gh-accordion__item--open & {
		transform: rotate(180deg);
	}
}

.gh-accordion__body {
	padding: 0.25rem 1.15rem 1.25rem;
	border-top: 1px solid $gh-border-soft;
	animation: gh-accordion-in 0.2s ease;
}

@keyframes gh-accordion-in {
	from { opacity: 0; transform: translateY(-4px); }
	to { opacity: 1; transform: translateY(0); }
}

.gh-accordion__actions {
	display: flex;
	justify-content: flex-end;
	gap: 0.5rem;
	padding-top: 0.5rem;
	margin-top: 1rem;
	border-top: 1px solid $gh-border-soft;
}

.gh-section {
	margin-bottom: 1.85rem;

	&:last-child { margin-bottom: 0; }
}

.gh-section__title {
	font-size: 0.72rem;
	font-weight: 700;
	text-transform: uppercase;
	letter-spacing: 0.05em;
	color: $gh-text-muted;
	margin-bottom: 0.75rem;
}

.gh-section__hint {
	font-weight: 500;
	text-transform: none;
	letter-spacing: 0;
	color: #b6bcc8;
}

.gh-grid {
	display: grid;
	gap: 0.85rem;

	&--2 {
		grid-template-columns: repeat(2, minmax(0, 1fr));
	}
}

.gh-field {
	margin-bottom: 0.85rem;

	&:last-child { margin-bottom: 0; }

	label {
		font-size: 0.78rem;
		font-weight: 600;
		color: $gh-text-soft;
		margin-bottom: 0.3rem;
		display: block;
	}

	::v-deep .form-control,
	::v-deep .form-select,
	.form-control,
	.form-select {
		border-radius: 9px;
		border-color: $gh-border;
		font-size: 0.9rem;
		padding: 0.5rem 0.75rem;
		transition: border-color 0.12s, box-shadow 0.12s;

		&:focus {
			border-color: $gh-accent;
			box-shadow: 0 0 0 3px rgba(43, 108, 255, 0.15);
		}
	}
}

.gh-help {
	font-size: 0.75rem;
	color: $gh-text-muted;
	margin-top: 0.4rem;
}

.ngh-group-heading {
	font-size: 1.05rem;
	font-weight: 700;
	color: $gh-text;
	margin: 1rem 0 1.25rem;
	padding-top: 1rem;
	border-top: 2px solid $gh-border;
	letter-spacing: -0.005em;
}

@media (max-width: 991px) {
	.gh-layout {
		grid-template-columns: 1fr;
	}

	.gh-intro {
		padding: 0 0 0.5rem 0;
	}
}

/* ─── Card-style picker (reusable) ─────────────────────────────────────────
   Same visual as the deploy-target picker in ClientDeploy.vue (Plesk FTP /
   Plesk Git / Cloudflare). Use for any 2–4 mutually-exclusive choice where
   you want clearer affordance than a stacked radio list. */

.cs-pick-card {
	padding: 0.85rem 0.95rem;
}

.cs-pick-group {
	display: grid;
	grid-template-columns: repeat(3, minmax(0, 1fr));
	gap: 0.65rem;
}

.cs-pick-group--2 {
	grid-template-columns: repeat(2, minmax(0, 1fr));
}

@media (max-width: 767px) {
	.cs-pick-group,
	.cs-pick-group--2 {
		grid-template-columns: 1fr;
	}
}

.cs-pick {
	display: flex;
	align-items: center;
	gap: 0.7rem;
	padding: 0.85rem 0.9rem;
	border: 1px solid $gh-border;
	border-radius: 10px;
	background: transparent;
	cursor: pointer;
	user-select: none;
	transition: all 0.12s ease;
	margin: 0;
}

.cs-pick input {
	display: none;
}

.cs-pick i {
	font-size: 1.1rem;
	color: $gh-text-muted;
	width: 22px;
	text-align: center;
	flex-shrink: 0;
}

.cs-pick:hover {
	background: $gh-bg-hover;
}

.cs-pick:hover i {
	color: $gh-text;
}

.cs-pick--active {
	border-color: lighten($gh-accent, 25%);
	background: $gh-accent-soft;
}

.cs-pick--active i {
	color: $gh-accent;
}

.cs-pick--active .cs-pick__name {
	color: $gh-accent;
}

.cs-pick__name {
	font-size: 0.9rem;
	font-weight: 600;
	color: $gh-text;
}

.cs-pick__sub {
	font-size: 0.72rem;
	color: $gh-text-muted;
	margin-top: 1px;
}

/* ─── Site settings grid (HeyGov-style) ────────────────────────────────────
   Two-column internal grid inside the Branding card:
   - Left:  Title + Description (the editable identity)
   - Right: Language + Preview (the live representation)
   Stacks to one column under 991px. */

.cs-site-settings {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: 1.5rem;
	align-items: stretch;
}

@media (max-width: 991px) {
	.cs-site-settings {
		grid-template-columns: 1fr;
	}
}

.cs-site-settings__col {
	display: flex;
	flex-direction: column;
	gap: 0.85rem;
	min-width: 0;
}

.cs-site-settings__col .gh-field {
	margin-bottom: 0;
}

/* Stretch the LAST field in each column (Description on left, Preview on
   right) so both grow to match the same height. Inside that field, the
   actual content (textarea / preview card) fills the available space. */
.cs-site-settings__col > .gh-field:last-child {
	flex: 1;
	display: flex;
	flex-direction: column;
	min-height: 0;
}

.cs-site-settings__col > .gh-field:last-child > textarea,
.cs-site-settings__col > .gh-field:last-child > .cs-search-preview {
	flex: 1;
	min-height: 0;
}

.cs-site-settings__col > .gh-field:last-child > textarea {
	resize: vertical;
}

.cs-site-settings__divider {
	border: none;
	border-top: 1px solid $gh-border-soft;
	margin: 1.5rem 0 0.25rem;
}

/* ─── Search-result preview card ──────────────────────────────────────────
   A miniature reproduction of how the site will appear in search results
   and social shares. Reads as: favicon avatar + domain on top, blue link-
   styled title, gray description below (placeholder italic when empty). */

.cs-search-preview {
	border: 1px solid $gh-border;
	border-radius: 12px;
	padding: 0.9rem 1rem;
	background: $gh-bg-soft;
}

.cs-search-preview__head {
	display: flex;
	align-items: center;
	gap: 0.6rem;
	margin-bottom: 0.5rem;
}

.cs-search-preview__favicon {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	overflow: hidden;
	flex-shrink: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: #f1f5f9;
}

.cs-search-preview__favicon img {
	width: 100%;
	height: 100%;
	object-fit: cover;
}

.cs-search-preview__favicon-fallback {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background: linear-gradient(135deg, #c084fc 0%, #f472b6 100%);
	color: white;
	font-weight: 700;
	font-size: 12px;
	letter-spacing: -0.02em;
}

.cs-search-preview__url {
	font-size: 0.82rem;
	color: $gh-text-soft;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.cs-search-preview__title {
	font-size: 1.05rem;
	font-weight: 500;
	color: #1a73e8;
	line-height: 1.3;
	margin-bottom: 0.35rem;
	overflow: hidden;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.cs-search-preview__desc {
	font-size: 0.82rem;
	color: $gh-text-soft;
	line-height: 1.45;
	display: -webkit-box;
	-webkit-line-clamp: 3;
	-webkit-box-orient: vertical;
	overflow: hidden;
}

.cs-search-preview__desc--empty {
	color: $gh-text-muted;
	font-style: italic;
}

/* ─── Image picker row (HeyGov-style) ──────────────────────────────────────
   A 2-column row inside .ada-card: descriptive info on the left (label +
   dimension hint + helper text) and a large image "slot" on the right.
   Sizes are ~30% smaller than HeyGov's reference (favicon 140×140 vs ~200,
   social 420×238 vs ~600×340).

   The slot itself:
   - Empty state — soft blue-tinted background with image icon, Upload
     button centered (always visible, no hover required).
   - Set state — image fills the slot; on hover a dark overlay reveals the
     Upload (change) button, and a small red ✕ Remove appears top-right. */

.cs-image-row {
	display: flex;
	align-items: center;
	justify-content: space-between;
	gap: 1.5rem;
	padding: 1rem 0;
}

.cs-image-row + .cs-image-row {
	border-top: 1px solid $gh-border-soft;
}

.cs-image-row__info {
	flex: 1;
	min-width: 0;
}

.cs-image-row__info strong {
	display: block;
	font-size: 0.95rem;
	color: $gh-text;
}

.cs-image-row__info small {
	display: block;
	font-size: 0.78rem;
	color: $gh-text-muted;
	margin-top: 2px;
}

.cs-image-row__desc {
	font-size: 0.8rem;
	color: $gh-text-muted;
	margin: 0.4rem 0 0;
	line-height: 1.4;
	max-width: 32rem;
}

.cs-image-slot {
	position: relative;
	flex-shrink: 0;
	border: 1px solid $gh-border;
	border-radius: 12px;
	background: #eef2ff;
	overflow: hidden;
	cursor: pointer;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.cs-image-slot:hover {
	border-color: lighten($gh-accent, 25%);
}

.cs-image-slot--square {
	width: 140px;
	height: 140px;
}

.cs-image-slot--wide {
	width: 420px;
	height: 238px;
}

@media (max-width: 991px) {
	.cs-image-row {
		flex-direction: column;
		align-items: flex-start;
	}

	.cs-image-slot--wide {
		width: 100%;
		max-width: 420px;
		height: auto;
		aspect-ratio: 1200 / 630;
	}
}

.cs-image-slot__img {
	width: 100%;
	height: 100%;
	object-fit: contain;
	background: #f8fafc;
}

.cs-image-slot__empty-icon {
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	color: #b6cfff;
	font-size: 2.25rem;
}

.cs-image-slot__overlay {
	position: absolute;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	background: rgba(15, 23, 42, 0.45);
	opacity: 0;
	transition: opacity 0.18s ease;
}

.cs-image-slot:hover .cs-image-slot__overlay {
	opacity: 1;
}

.cs-image-slot--empty .cs-image-slot__overlay {
	opacity: 1;
	background: transparent;
}

.cs-image-slot__upload {
	display: inline-flex;
	align-items: center;
	gap: 0.35rem;
	background: white;
	border: 1px solid #cbd5e1;
	color: $gh-text;
	font-size: 0.82rem;
	font-weight: 600;
	padding: 0.4rem 0.95rem;
	border-radius: 999px;
	cursor: pointer;
	box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
	transition: background 0.12s ease, border-color 0.12s ease, color 0.12s ease;
}

.cs-image-slot__upload:hover {
	background: $gh-bg-hover;
	border-color: #94a3b8;
}

.cs-image-slot__remove {
	position: absolute;
	top: 8px;
	right: 8px;
	width: 26px;
	height: 26px;
	border-radius: 50%;
	background: rgba(220, 38, 38, 0.95);
	border: none;
	color: white;
	font-size: 0.75rem;
	display: flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	opacity: 0;
	transition: opacity 0.15s ease, transform 0.15s ease;
}

.cs-image-slot:hover .cs-image-slot__remove {
	opacity: 1;
}

.cs-image-slot__remove:hover {
	background: rgb(220, 38, 38);
	transform: scale(1.08);
}

/* ── Skeleton Loader ──────────────────────────────────────────────────────── */
@keyframes jcs-skeleton-shimmer {
	0%   { background-position: -400px 0; }
	100% { background-position: 400px 0; }
}
.jcs-skeleton-shimmer {
	background-color: #eef0f3;
	background-image: linear-gradient(
		90deg,
		rgba(238, 240, 243, 0) 0%,
		rgba(255, 255, 255, 0.75) 50%,
		rgba(238, 240, 243, 0) 100%
	);
	background-size: 400px 100%;
	background-repeat: no-repeat;
	animation: jcs-skeleton-shimmer 1.4s linear infinite;
	border-radius: 6px;
}

/* Tabs strip — mirrors .jcs-top-tabs container so dimensions match */
.jcs-skeleton__tabs {
	display: flex;
	flex-wrap: wrap;
	gap: 6px;
	padding: 6px;
	border-radius: 18px;
	background: #eceef2;
}
.jcs-skeleton__tab {
	height: 32px;
	width: 92px;
	border-radius: 14px;
	background-color: #dfe3ea;
}
.jcs-skeleton__tab--active {
	background-color: #fff;
	box-shadow: 0 1px 2px rgba(17, 24, 39, 0.1);
}

/* Card / field placeholders inside each gh-layout section */
.jcs-skeleton__card {
	display: flex;
	flex-direction: column;
	gap: 18px;
	padding: 22px;
}
.jcs-skeleton__field {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

/* Lines */
.jcs-skeleton-line {
	display: block;
	height: 12px;
	width: 100%;
}
.jcs-skeleton-line--section-title {
	height: 22px;
	width: 55%;
	margin-bottom: 10px;
}
.jcs-skeleton-line--section-sub {
	height: 11px;
	width: 90%;
	margin-bottom: 6px;
}
.jcs-skeleton-line--section-sub2 {
	height: 11px;
	width: 65%;
}
.jcs-skeleton-line--label {
	height: 12px;
	width: 28%;
}
.jcs-skeleton-line--input {
	height: 38px;
	border-radius: 8px;
}
.jcs-skeleton-line--textarea {
	height: 92px;
	border-radius: 8px;
}
</style>
